// This file is auto-generated by @hey-api/openapi-ts

export type ClientOptions = {
  baseURL: `${string}://${string}` | (string & {})
}

export type AccountAccess = {
  readonly id: number
  role: AccountAccessRoleEnum
  account: Nested
}

/**
 * * `admin` - Admin
 * * `accountant` - Accountant
 */
export const AccountAccessRoleEnum = {
  ADMIN: 'admin',
  ACCOUNTANT: 'accountant'
} as const

/**
 * * `admin` - Admin
 * * `accountant` - Accountant
 */
export type AccountAccessRoleEnum =
  (typeof AccountAccessRoleEnum)[keyof typeof AccountAccessRoleEnum]

/**
 * Custom token obtain serializer with optional service_token.
 */
export type AccountUiTokenObtain = {
  service_token?: string
}

/**
 * * `archive` - Archive
 * * `unarchive` - Unarchive
 */
export const ActionEnum = {
  ARCHIVE: 'archive',
  UNARCHIVE: 'unarchive'
} as const

/**
 * * `archive` - Archive
 * * `unarchive` - Unarchive
 */
export type ActionEnum = (typeof ActionEnum)[keyof typeof ActionEnum]

/**
 * * `user` - user
 * * `agent` - agent
 * * `api_key` - api_key
 * * `system` - system
 */
export const ActorTypeEnum = {
  USER: 'user',
  AGENT: 'agent',
  API_KEY: 'api_key',
  SYSTEM: 'system'
} as const

/**
 * * `user` - user
 * * `agent` - agent
 * * `api_key` - api_key
 * * `system` - system
 */
export type ActorTypeEnum = (typeof ActorTypeEnum)[keyof typeof ActorTypeEnum]

export type AddUserInOrganization = {
  organization_id: number
  email: string
}

export type AdminArchiveUserRequest = {
  organization_id: number
  action?: ActionEnum
}

export type AdminArchiveUserResponse = {
  user_id: number
  is_blocked: boolean
  dependencies?: {
    [key: string]: unknown
  }
}

export type AdminResetPassword = {
  /**
   * Password-reset page URL. The server appends '?token=<reset-token>' and emails the member a link to choose a new password.
   */
  redirect_url: string
}

export type AdminResetPasswordResponse = {
  sent: boolean
}

/**
 * * `` - No OIDC support
 * * `RS256` - RSA with SHA-2 256
 * * `HS256` - HMAC with SHA-2 256
 */
export const AlgorithmEnum = { RS256: 'RS256', HS256: 'HS256' } as const

/**
 * * `` - No OIDC support
 * * `RS256` - RSA with SHA-2 256
 * * `HS256` - HMAC with SHA-2 256
 */
export type AlgorithmEnum = (typeof AlgorithmEnum)[keyof typeof AlgorithmEnum]

export type AppCredentialActor = {
  id: number
  email: string
}

export type AppCredentialCreate = {
  name: string
  expires_at?: string | null
  permission_codes?: Array<string>
}

export type AppCredentialCreateResponse = {
  id: string
  name: string
  status?: PublicAccessCredentialStatusEnum
  expires_at?: string | null
  created_by: AppCredentialActor
  readonly created_at: string
  last_used_at?: string | null
  revoked_at?: string | null
  readonly permission_codes: Array<string>
  readonly token: string
}

export type AppCredentialRead = {
  id: string
  name: string
  status?: PublicAccessCredentialStatusEnum
  expires_at?: string | null
  created_by: AppCredentialActor
  readonly created_at: string
  last_used_at?: string | null
  revoked_at?: string | null
  readonly permission_codes: Array<string>
}

export type AppCredentialRevoke = {
  reason?: string | null
}

export type AppCredentialRevokeResponse = {
  id: string
  status: string
  revoked_at: string
  reason: string | null
}

export type AppCredentialRotateResponse = {
  old_credential_id: string
  new_credential: AppCredentialCreateResponse
}

/**
 * * `skalx_calls` - Skalx Calls
 */
export const AppEnum = { SKALX_CALLS: 'skalx_calls' } as const

/**
 * * `skalx_calls` - Skalx Calls
 */
export type AppEnum = (typeof AppEnum)[keyof typeof AppEnum]

export type AppRole = {
  readonly id: number
  /**
   * Human-readable role name (e.g., 'Sales Manager')
   */
  name: string
  /**
   * Machine-readable role code name (e.g., 'sales_manager')
   */
  codename?: string | null
  description?: string
  /**
   * Admin flag: when True, role automatically has ALL permissions without explicit assignment for specified application (or if application is null, for organization-level permissions).
   */
  is_admin?: boolean
  readonly permissions: Array<unknown>
  readonly members_count: number
}

export type AppRoleMembers = {
  user_ids: Array<number>
}

export type AppRoleWrite = {
  name?: string
  codename?: string
  description?: string
  permissions_ids?: Array<number>
}

/**
 * What an application reports about its own step (TS-Core-012 BR-21).
 *
 * `scope`/`organization` come from the token, not the payload, so an app can only write its own workspace.
 */
export type AppStepReport = {
  user?: number
  step?: string
  state: StateEnum
  occurred_at?: string
  event_id?: string
  error_code?: OnboardingStepErrorCodeEnum
  user_recoverable?: boolean
  blocked_by?: string
}

export type AppUserGroup = {
  readonly core_id: number
  name: string
  readonly members: Array<string>
  readonly date_updated: string
}

export type AppUserInvite = {
  emails: Array<string>
  /**
   * Where the emailed link points; Core appends its one-time setup key to it. Required unless `notify` is false, when no mail is sent and there is no link to build.
   */
  invite_url?: string
  /**
   * Role to grant on the calling application. Must be one of that application's own roles; omit to invite into the organization without application access.
   */
  role?: string
  /**
   * Send the welcome mail. Pass false to create the identity, the membership and the application access without telling the person yet — for a migration that imports colleagues long before the workspace is theirs to enter. Invite them again with notify true at that point: the setup key is rotated and the mail goes out then.
   */
  notify?: boolean
}

export type AppUserRole = {
  id: number
  codename: string | null
  name: string
  is_admin: boolean
}

export type Application = {
  readonly id: number
  name: string
  token: string
  readonly date_created: string
  is_public?: boolean
  instance_type: ApplicationRepositoryShort
  readonly domain: string
  /**
   * User-facing URL where the app is launched (third-party apps).
   */
  app_url?: string | null
  icon: ShortAttachment | null
  description?: string | null
  /**
   * @deprecated
   */
  roles?: Array<string>
  readonly related_roles: Array<OrganizationRoleShort>
  readonly auto_provision_role: number | null
}

export type ApplicationAccessCreate = {
  id: number
  role?: string | null
}

export type ApplicationAccessList = {
  readonly id: number
  user: UserVeryShort
  application: ApplicationShort
  /**
   * @deprecated
   */
  role?: string | null
  readonly roles: Array<OrganizationRoleShort>
}

export type ApplicationAccessListCreate = {
  users: Array<number>
  applications: Array<ApplicationAccessCreate>
}

export type ApplicationAccessRead = {
  user: number
  role?: string | null
  application: number
}

export type ApplicationCreate = {
  organization_id: number
  instance_type_id: number
  name?: string
  url?: string
  settings?: {
    [key: string]: unknown
  } | null
  /**
   * Codename of the role every member of the organization should get this app with (see the repository's `default_roles`). The role itself is created during the install, so it is referenced by codename rather than id. Omit to install without auto-provisioning.
   */
  auto_provision_role_codename?: string | null
}

export type ApplicationDetail = {
  readonly id: number
  name: string
  token: string
  readonly date_created: string
  is_public?: boolean
  instance_type: ApplicationRepositoryShort
  readonly domain: string
  /**
   * User-facing URL where the app is launched (third-party apps).
   */
  app_url?: string | null
  icon: ShortAttachment | null
  description?: string | null
  /**
   * @deprecated
   */
  roles?: Array<string>
  readonly related_roles: Array<OrganizationRoleShort>
  readonly auto_provision_role: number | null
}

export type ApplicationInvite = {
  application: number
  role: string
}

/**
 * Create/update permissions scoped to a specific Application (third-party custom apps).
 */
export type ApplicationPermission = {
  readonly id: number
  /**
   * Permission code: 'action' or 'module.action' or 'app.module.action'
   */
  code: string
  label?: unknown
  description?: unknown
  application: number
  readonly date_created: string
  readonly date_updated: string
}

export type ApplicationPublish = {
  /**
   * New if null
   */
  app_token: string | null
  author_app_token: string
  application_type?: string
  name?: string
  description?: string | null
}

export type ApplicationRepository = {
  readonly id: number
  name: string
  type: string
  is_public?: boolean
  is_multiple?: boolean
  endpoint_url?: string | null
  description?: string | null
  readonly is_installed: boolean | null
  /**
   * Roles that will be created when this app is installed, in codename form.
   */
  readonly default_roles: Array<ApplicationRepositoryDefaultRole>
}

/**
 * A role that installing this app will create (not a persisted row yet).
 */
export type ApplicationRepositoryDefaultRole = {
  codename: string
  name: string
  is_admin: boolean
}

export type ApplicationRepositoryDetail = {
  readonly id: number
  name: string
  type: string
  is_public?: boolean
  is_multiple?: boolean
  endpoint_url?: string | null
  description?: string | null
  readonly is_installed: boolean | null
  /**
   * Roles that will be created when this app is installed, in codename form.
   */
  readonly default_roles: Array<ApplicationRepositoryDefaultRole>
  settings_mapping: Array<SettingsMapping>
}

export type ApplicationRepositoryShort = {
  readonly id: number
  name: string
  description?: string | null
  type: string
}

export type ApplicationScimConfigRead = {
  readonly application: number
  inbound_enabled?: boolean
  readonly has_inbound_token: boolean
  inbound_token_hint?: string | null
  inbound_token_rotated_at?: string | null
  outbound_enabled?: boolean
  outbound_base_url?: string | null
  readonly has_outbound_token: boolean
  last_sync_at?: string | null
  last_sync_status?: ScimSyncStatusEnum
  last_sync_error?: string | null
  last_backfill_at?: string | null
  last_backfill_status?: ScimSyncStatusEnum
  last_backfill_error?: string | null
}

export type ApplicationScimRotateToken = {
  readonly application: number
  inbound_enabled?: boolean
  readonly has_inbound_token: boolean
  inbound_token_hint?: string | null
  inbound_token_rotated_at?: string | null
  outbound_enabled?: boolean
  outbound_base_url?: string | null
  readonly has_outbound_token: boolean
  last_sync_at?: string | null
  last_sync_status?: ScimSyncStatusEnum
  last_sync_error?: string | null
  last_backfill_at?: string | null
  last_backfill_status?: ScimSyncStatusEnum
  last_backfill_error?: string | null
  readonly inbound_token: string
}

export type ApplicationShort = {
  readonly id: number
  name: string
}

export type ApplicationUpdate = {
  readonly id: number
  name?: string
  description?: string
  domain?: string | null
  app_url?: string | null
  /**
   * Attachment id of the uploaded launcher icon. Send null to remove and fall back to the default.
   */
  icon?: number | null
  /**
   * Role every member of the organization gets this app with. Must be a role of this app. Send null to stop auto-provisioning (already granted access is kept).
   */
  auto_provision_role?: number | null
}

export type ApplicationUserDetails = {
  /**
   * @deprecated
   */
  role: string | null
  user: UserShortWithoutLogin
  readonly teams: Array<OrganizationTeamShort> | null
  /**
   * @deprecated
   */
  readonly application_role: string | null
  organization: OrganizationShort | null
  application: ApplicationShort | null
  readonly permissions: Array<string> | null
  readonly roles: Array<AppUserRole> | null
}

export type ApplicationUserMembership = {
  member: boolean
  organization_id: number | null
  role: OrganizationRole | NullEnum | null
}

export type ApplicationValidate = {
  readonly id: number
  type: string
  readonly instance_type_id: number
  domain_name: string
  organization: OrganizationTiny
}

export type ApplicationWebhookConfigRead = {
  readonly application: number
  readonly webhook_endpoint: string | null
  readonly public_access_webhook_endpoint: string | null
  readonly has_webhook_secret_token: boolean
  readonly webhook_secret_token_hint: string | null
  readonly template_webhook_endpoint: string | null
  readonly template_public_access_webhook_endpoint: string | null
  readonly template_has_webhook_secret_token: boolean
  readonly domain: string | null
  readonly effective_webhook_url: string | null
  readonly effective_public_access_webhook_url: string | null
  readonly delivery_max_retries: number
  readonly delivery_timeout_seconds: number
  readonly delivery_retry_max_delay_seconds: number
}

export type ApplicationWebhookRotateSecret = {
  readonly application: number
  readonly webhook_endpoint: string | null
  readonly public_access_webhook_endpoint: string | null
  readonly has_webhook_secret_token: boolean
  readonly webhook_secret_token_hint: string | null
  readonly template_webhook_endpoint: string | null
  readonly template_public_access_webhook_endpoint: string | null
  readonly template_has_webhook_secret_token: boolean
  readonly domain: string | null
  readonly effective_webhook_url: string | null
  readonly effective_public_access_webhook_url: string | null
  readonly delivery_max_retries: number
  readonly delivery_timeout_seconds: number
  readonly delivery_retry_max_delay_seconds: number
  readonly webhook_secret_token: string
}

export type ArchiveConflictResponse = {
  code: string
  detail?: string
  dependencies?: ArchiveDependencyResponse
  job_id?: number
}

export type ArchiveDependencyResponse = {
  user_id: number
  organization_id: number
  services: {
    [key: string]: ArchiveServiceDependency
  }
  blocking_active_count: number
  has_blockers: boolean
}

export type ArchiveDownloadUrl = {
  url: string
  expires_in: number
}

export type ArchiveEntityDependency = {
  code: string
  label_ro?: string
  label_ru?: string
  label_en?: string
  label_it?: string
  active_count: number
}

export type ArchiveNotReady = {
  detail: string
  status: AuditLogArchiveStatusEnum
}

export type ArchiveReplacementRequest = {
  organization_id: number
  target_user_id: number
}

export type ArchiveServiceDependency = {
  service: ServiceEnum
  entities: Array<ArchiveEntityDependency>
  blocking_active_count: number
}

export type ArchiveServiceUnavailableResponse = {
  code: string
  services: Array<{
    [key: string]: unknown
  }>
}

/**
 * Replace editable personal roles without touching system or group-derived grants.
 */
export type AssignUserRoles = {
  /**
   * List of OrganizationRole IDs to assign to the user as personal (non-team) roles
   */
  roles: Array<number>
}

/**
 * Base Attachment serializer to return the file URL based on its is_public status.
 */
export type Attachment = {
  readonly id: number
  readonly date_created: string
  readonly date_updated: string
  readonly file: string
  title: string
  readonly size: number | null
  status: AttachmentStatusEnum
  type: AttachmentTypeEnum
  is_public?: boolean
}

/**
 * * `pending` - Pending
 * * `uploaded` - Uploaded
 * * `failed` - Failed
 * * `deleted` - Deleted
 */
export const AttachmentStatusEnum = {
  PENDING: 'pending',
  UPLOADED: 'uploaded',
  FAILED: 'failed',
  DELETED: 'deleted'
} as const

/**
 * * `pending` - Pending
 * * `uploaded` - Uploaded
 * * `failed` - Failed
 * * `deleted` - Deleted
 */
export type AttachmentStatusEnum =
  (typeof AttachmentStatusEnum)[keyof typeof AttachmentStatusEnum]

/**
 * * `document` - Document
 * * `photo` - Photo
 */
export const AttachmentTypeEnum = {
  DOCUMENT: 'document',
  PHOTO: 'photo'
} as const

/**
 * * `document` - Document
 * * `photo` - Photo
 */
export type AttachmentTypeEnum =
  (typeof AttachmentTypeEnum)[keyof typeof AttachmentTypeEnum]

export type AttachmentWebhook = {
  id: number
  upload_url: string
  content_type: string
  webhook_data: WebhookData
}

/**
 * * `google` - Google
 * * `youtube` - YouTube
 * * `linkedin` - LinkedIn
 * * `reviews` - Review sites
 * * `referral` - Referral
 * * `ai_assistant` - AI assistant
 * * `event` - Event
 * * `press` - Press
 * * `other` - Other
 */
export const AttributionSourceEnum = {
  GOOGLE: 'google',
  YOUTUBE: 'youtube',
  LINKEDIN: 'linkedin',
  REVIEWS: 'reviews',
  REFERRAL: 'referral',
  AI_ASSISTANT: 'ai_assistant',
  EVENT: 'event',
  PRESS: 'press',
  OTHER: 'other'
} as const

/**
 * * `google` - Google
 * * `youtube` - YouTube
 * * `linkedin` - LinkedIn
 * * `reviews` - Review sites
 * * `referral` - Referral
 * * `ai_assistant` - AI assistant
 * * `event` - Event
 * * `press` - Press
 * * `other` - Other
 */
export type AttributionSourceEnum =
  (typeof AttributionSourceEnum)[keyof typeof AttributionSourceEnum]

/**
 * OpenAPI shape for the actor dict while preserving runtime dict output.
 */
export type AuditLogActor = {
  /**
   * Cross-service actor user ID.
   */
  id?: string | null
  /**
   * Actor user email address.
   */
  email?: string | null
  /**
   * Actor user full name.
   */
  full_name?: string | null
}

/**
 * List/detail representation. The actual file URL is exposed via the dedicated download endpoint.
 */
export type AuditLogArchive = {
  readonly id: number
  type: AuditLogArchiveTypeEnum
  status: AuditLogArchiveStatusEnum
  readonly organization: number
  readonly application: number | null
  requested_by: RequestedByUser
  readonly period_start: string
  readonly period_end: string
  readonly filters: {
    [key: string]: unknown
  }
  format: AuditLogArchiveFormatEnum
  readonly file_size: number | null
  readonly checksum_sha256: string
  readonly error_message: string
  readonly metadata: {
    [key: string]: unknown
  }
  readonly date_created: string
  readonly date_updated: string
}

/**
 * Body for POST — validates the export request. Persistence + enqueue lives in the view.
 */
export type AuditLogArchiveCreate = {
  period_start: string
  period_end: string
  format?: AuditLogArchiveFormatEnum
  application?: number | null
  filters?: {
    [key: string]: string
  }
}

/**
 * * `csv` - CSV
 * * `xlsx` - Excel (XLSX)
 * * `jsonl` - JSON Lines
 */
export const AuditLogArchiveFormatEnum = {
  CSV: 'csv',
  XLSX: 'xlsx',
  JSONL: 'jsonl'
} as const

/**
 * * `csv` - CSV
 * * `xlsx` - Excel (XLSX)
 * * `jsonl` - JSON Lines
 */
export type AuditLogArchiveFormatEnum =
  (typeof AuditLogArchiveFormatEnum)[keyof typeof AuditLogArchiveFormatEnum]

/**
 * * `pending` - Pending
 * * `processing` - Processing
 * * `ready` - Ready
 * * `failed` - Failed
 */
export const AuditLogArchiveStatusEnum = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  READY: 'ready',
  FAILED: 'failed'
} as const

/**
 * * `pending` - Pending
 * * `processing` - Processing
 * * `ready` - Ready
 * * `failed` - Failed
 */
export type AuditLogArchiveStatusEnum =
  (typeof AuditLogArchiveStatusEnum)[keyof typeof AuditLogArchiveStatusEnum]

/**
 * * `periodic` - Periodic archive
 * * `export` - User export
 */
export const AuditLogArchiveTypeEnum = {
  PERIODIC: 'periodic',
  EXPORT: 'export'
} as const

/**
 * * `periodic` - Periodic archive
 * * `export` - User export
 */
export type AuditLogArchiveTypeEnum =
  (typeof AuditLogArchiveTypeEnum)[keyof typeof AuditLogArchiveTypeEnum]

/**
 * OpenAPI shape for localized descriptions while preserving runtime dict output.
 */
export type AuditLogDescriptions = {
  /**
   * Romanian localized description.
   */
  ro?: string | null
  /**
   * Russian localized description.
   */
  ru?: string | null
  /**
   * English localized description.
   */
  en?: string | null
  /**
   * Italian localized description.
   */
  it?: string | null
}

/**
 * Full document serializer for retrieve responses.
 */
export type AuditLogDetail = {
  readonly id: string
  timestamp: string
  organization_id: number | null
  application_id: number | null
  application_name: string | null
  service: string
  category: string
  action: string
  severity?: SeverityEnum | NullEnum | null
  event_type: string | null
  channel: string | null
  client_ip: string | null
  entity_type: string
  entity_id: string
  /**
   * Actor user that caused the audit event.
   */
  actor: AuditLogActor | null
  /**
   * What kind of principal caused the event: a person, an agent, an API key, or the platform.
   *
   * * `user` - user
   * * `agent` - agent
   * * `api_key` - api_key
   * * `system` - system
   */
  actor_type?: ActorTypeEnum | NullEnum | null
  /**
   * Description resolved from descriptions by Accept-Language; falls back to the stored description.
   */
  readonly description: string | null
  /**
   * Localized descriptions keyed by language code.
   */
  descriptions?: AuditLogDescriptions | null
  url: string | null
  method: string | null
}

/**
 * Distinct values of one filterable field, returned by the ``filters`` action.
 */
export type AuditLogFilterOptions = {
  field: string
  values: Array<string>
}

/**
 * Lightweight serializer used for list responses -- no ``changes`` payload.
 */
export type AuditLogList = {
  readonly id: string
  timestamp: string
  organization_id: number | null
  application_id: number | null
  application_name: string | null
  service: string
  category: string
  action: string
  severity?: SeverityEnum | NullEnum | null
  event_type: string | null
  channel: string | null
  client_ip: string | null
  entity_type: string
  entity_id: string
  /**
   * Actor user that caused the audit event.
   */
  actor: AuditLogActor | null
  /**
   * What kind of principal caused the event: a person, an agent, an API key, or the platform.
   *
   * * `user` - user
   * * `agent` - agent
   * * `api_key` - api_key
   * * `system` - system
   */
  actor_type?: ActorTypeEnum | NullEnum | null
  /**
   * Description resolved from descriptions by Accept-Language; falls back to the stored description.
   */
  readonly description: string | null
}

export type AuditLogSignInActivity = {
  period: AuditLogSignInActivityCurrentPeriod
  summary: AuditLogSignInActivitySummary
  comparison: AuditLogSignInActivityComparison | null
  series: Array<AuditLogSignInActivitySeriesItem>
}

export type AuditLogSignInActivityComparison = {
  period: AuditLogSignInActivityPeriod
  sign_ins: number
  sign_ins_change_percent: number | null
}

export type AuditLogSignInActivityCurrentPeriod = {
  start: string
  end: string
  days: number
  timezone: string
}

export type AuditLogSignInActivityPeriod = {
  start: string
  end: string
}

export type AuditLogSignInActivitySeriesItem = {
  date: string
  sign_ins: number
  failures: number
}

export type AuditLogSignInActivitySummary = {
  sign_ins: number
  sign_ins_change_percent: number | null
  avg_per_day: number
  failures: number
  failure_rate_percent: number
  unique_users: number
}

export type AuthorizationV2oAuthClientCreate = {
  organization: number
  name: string
  application?: number
  application_name?: string
  application_description?: string | null
  client_type?: ClientTypeEnum
  grant_type?: GrantTypeEnum
  allow_client_credentials?: boolean
  algorithm?: AlgorithmEnum | BlankEnum
  pkce_required?: boolean
  redirect_uris?: Array<string>
  post_logout_redirect_uris?: Array<string>
  allowed_origins?: Array<string>
}

export type AuthorizationV2oAuthClientRead = {
  readonly id: number
  readonly organization: number
  readonly application: number
  client_id?: string
  name?: string
  client_type: ClientTypeEnum
  readonly grant_type: string
  allow_client_credentials?: boolean
  is_revoked?: boolean
  algorithm?: AlgorithmEnum | BlankEnum
  pkce_required?: boolean
  readonly redirect_uris: Array<string>
  readonly post_logout_redirect_uris: Array<string>
  readonly allowed_origins: Array<string>
  readonly created: string
  readonly updated: string
}

export const BlankEnum = { '': '' } as const

export type BlankEnum = (typeof BlankEnum)[keyof typeof BlankEnum]

/**
 * * `organization` - Organization
 * * `users` - Users
 * * `roles` - Roles
 * * `groups` - Groups
 * * `applications` - Applications
 * * `attachments` - Attachments
 * * `audit` - Audit
 */
export const CategoryEnum = {
  ORGANIZATION: 'organization',
  USERS: 'users',
  ROLES: 'roles',
  GROUPS: 'groups',
  APPLICATIONS: 'applications',
  ATTACHMENTS: 'attachments',
  AUDIT: 'audit'
} as const

/**
 * * `organization` - Organization
 * * `users` - Users
 * * `roles` - Roles
 * * `groups` - Groups
 * * `applications` - Applications
 * * `attachments` - Attachments
 * * `audit` - Audit
 */
export type CategoryEnum = (typeof CategoryEnum)[keyof typeof CategoryEnum]

/**
 * Confirm password change (requires ChangePasswordJWT auth).
 */
export type ChangePasswordConfirm = {
  password: string
  confirm_password: string
}

export type ClientAuthMethod = {
  method: ClientAuthMethodMethodEnum
  enabled: boolean
}

/**
 * * `password` - password
 * * `google` - google
 * * `apple` - apple
 * * `email_otp` - email_otp
 * * `passkey` - passkey
 * * `qr_login` - qr_login
 */
export const ClientAuthMethodMethodEnum = {
  PASSWORD: 'password',
  GOOGLE: 'google',
  APPLE: 'apple',
  EMAIL_OTP: 'email_otp',
  PASSKEY: 'passkey',
  QR_LOGIN: 'qr_login'
} as const

/**
 * * `password` - password
 * * `google` - google
 * * `apple` - apple
 * * `email_otp` - email_otp
 * * `passkey` - passkey
 * * `qr_login` - qr_login
 */
export type ClientAuthMethodMethodEnum =
  (typeof ClientAuthMethodMethodEnum)[keyof typeof ClientAuthMethodMethodEnum]

export type ClientAuthMethods = {
  auth_methods: Array<ClientAuthMethod>
}

/**
 * * `confidential` - Confidential
 * * `public` - Public
 */
export const ClientTypeEnum = {
  CONFIDENTIAL: 'confidential',
  PUBLIC: 'public'
} as const

/**
 * * `confidential` - Confidential
 * * `public` - Public
 */
export type ClientTypeEnum =
  (typeof ClientTypeEnum)[keyof typeof ClientTypeEnum]

/**
 * * `organization.view` - View workspace
 * * `organization.manage` - Manage workspace
 * * `users.view` - View members
 * * `users.invite` - Invite members
 * * `users.manage` - Manage members
 * * `roles.view` - View workspace roles
 * * `roles.manage` - Manage workspace roles
 * * `groups.view` - View groups
 * * `groups.manage` - Manage groups
 * * `applications.view` - View applications
 * * `applications.manage` - Manage applications
 * * `applications.access` - Manage application access
 * * `attachments.view` - View attachments
 * * `attachments.upload` - Upload attachments
 * * `attachments.delete` - Delete attachments
 * * `audit.view` - View audit logs
 * * `audit.export` - Export audit logs
 */
export const CodeEnum = {
  ORGANIZATION_VIEW: 'organization.view',
  ORGANIZATION_MANAGE: 'organization.manage',
  USERS_VIEW: 'users.view',
  USERS_INVITE: 'users.invite',
  USERS_MANAGE: 'users.manage',
  ROLES_VIEW: 'roles.view',
  ROLES_MANAGE: 'roles.manage',
  GROUPS_VIEW: 'groups.view',
  GROUPS_MANAGE: 'groups.manage',
  APPLICATIONS_VIEW: 'applications.view',
  APPLICATIONS_MANAGE: 'applications.manage',
  APPLICATIONS_ACCESS: 'applications.access',
  ATTACHMENTS_VIEW: 'attachments.view',
  ATTACHMENTS_UPLOAD: 'attachments.upload',
  ATTACHMENTS_DELETE: 'attachments.delete',
  AUDIT_VIEW: 'audit.view',
  AUDIT_EXPORT: 'audit.export'
} as const

/**
 * * `organization.view` - View workspace
 * * `organization.manage` - Manage workspace
 * * `users.view` - View members
 * * `users.invite` - Invite members
 * * `users.manage` - Manage members
 * * `roles.view` - View workspace roles
 * * `roles.manage` - Manage workspace roles
 * * `groups.view` - View groups
 * * `groups.manage` - Manage groups
 * * `applications.view` - View applications
 * * `applications.manage` - Manage applications
 * * `applications.access` - Manage application access
 * * `attachments.view` - View attachments
 * * `attachments.upload` - Upload attachments
 * * `attachments.delete` - Delete attachments
 * * `audit.view` - View audit logs
 * * `audit.export` - Export audit logs
 */
export type CodeEnum = (typeof CodeEnum)[keyof typeof CodeEnum]

/**
 * * `1-10` - 1-10
 * * `11-50` - 11-50
 * * `51-200` - 51-200
 * * `201-1000` - 201-1000
 */
export const CompanySizeEnum = {
  '1_10': '1-10',
  '11_50': '11-50',
  '51_200': '51-200',
  '201_1000': '201-1000'
} as const

/**
 * * `1-10` - 1-10
 * * `11-50` - 11-50
 * * `51-200` - 51-200
 * * `201-1000` - 201-1000
 */
export type CompanySizeEnum =
  (typeof CompanySizeEnum)[keyof typeof CompanySizeEnum]

/**
 * Confirm user via token.
 */
export type ConfirmUser = {
  token: string
}

export type Country = {
  /**
   * 2-letter country code
   */
  id: string
  name: string
}

export type CountryDetail = {
  /**
   * 2-letter country code
   */
  id: string
  /**
   * 3-letter country code
   */
  alpha_3: string
  /**
   * Phone code
   */
  phone_code: number
  name: string
}

/**
 * Create a service account for current user.
 */
export type CreateAccount = {
  service: number
}

export type CreateAvatarUploadUrl = {
  title?: string
  is_public?: boolean
}

export type CreateUploadUrl = {
  title?: string
  type?: AttachmentTypeEnum
  is_public?: boolean
}

export type CrossLink = {
  scope: string
  title: string
  done: number
  total: number
}

export type CurrentAccountUser = {
  user: UserShortWithSettings
  account_access: Array<AccountAccess>
  organizations: Array<OrganizationLong>
}

export type CurrentApplication = {
  application_role?: string
  type?: string
  name?: string
  token?: string
  icon: ShortAttachment | null
}

export type CurrentOrganizationUser = {
  id: number
  user: UserShortWithSettings
  role: string
  organization: OrganizationShort
  available_organizations: Array<OrganizationShort>
  pinned_applications: Array<Application> | null
  public_applications: Array<Application> | null
  current_application: CurrentApplication | null
}

export type CustomAuthExchange = {
  provider_id: number
  code: string
  state: string
  redirect_uri: string
}

export type CustomAuthSamlExchange = {
  handoff: string
}

export type CustomAuthStart = {
  domain_name: string
  redirect_uri: string
}

export type CustomAuthStartResponse = {
  provider_id: number
  protocol: string
  auth_url: string
  state: string
  expires_in: number
}

/**
 * Custom refresh serializer with user validation.
 */
export type CustomTokenRefresh = {
  refresh: string
  readonly access: string
  service_token?: string
}

/**
 * A predefined role an application declares so Core can auto-provision it with permissions.
 */
export type DefaultRole = {
  codename: string
  name: string
  description?: string
  is_admin?: boolean
  permissions?: Array<string>
}

export type DeprecatedAuditApplication = {
  id?: number | null
  type?: string | null
  token?: string | null
}

export type DeprecatedAuditLogList = {
  created_at: string
  organization_id?: number | null
  application?: DeprecatedAuditApplication | null
  user?: UserLog | null
  team?: OrganizationTeamShort | null
  action_type: string
}

/**
 * * `already_set_up` - Already set up
 * * `not_relevant` - Not relevant to my role
 * * `later` - Later
 * * `not_interested` - Not interested
 */
export const DismissReasonEnum = {
  ALREADY_SET_UP: 'already_set_up',
  NOT_RELEVANT: 'not_relevant',
  LATER: 'later',
  NOT_INTERESTED: 'not_interested'
} as const

/**
 * * `already_set_up` - Already set up
 * * `not_relevant` - Not relevant to my role
 * * `later` - Later
 * * `not_interested` - Not interested
 */
export type DismissReasonEnum =
  (typeof DismissReasonEnum)[keyof typeof DismissReasonEnum]

/**
 * Whether a would-be workspace subdomain can be claimed, for the live address check.
 */
export type DomainAvailability = {
  domain_name: string
  available: boolean
}

/**
 * The answer the workspace screen branches on: every matching workspace.
 */
export type DomainMatch = {
  matched: boolean
  policy: string
  organizations: Array<DomainMatchEntry>
}

/**
 * One matching workspace plus the caller's own request against it (null if none).
 */
export type DomainMatchEntry = {
  id: number
  name: string
  request: OrganizationJoinRequest | null
}

/**
 * Custom token obtain serializer with optional service_token.
 */
export type DomainTokenObtain = {
  service_token?: string
  domain_name?: string
}

/**
 * Edit an existing service account.
 */
export type EditAccount = {
  service?: number
}

/**
 * Serializer for editing user password / email.
 */
export type EditUser = {
  password?: string
  new_password?: string
  confirm_password?: string
  email?: string
}

/**
 * Verify a magic-link token from the email-OTP login email.
 */
export type EmailOtpLinkVerify = {
  token: string
}

/**
 * Initiate passwordless email-OTP sign-in. No user enumeration.
 */
export type EmailOtpRequest = {
  username: string
  service_token?: string
  domain_name?: string
  redirect_url?: string
}

/**
 * Verify an email-OTP code issued by EmailOTPRequest.
 */
export type EmailOtpVerify = {
  otp_code: string
}

export type EmailVerifyV2Error = {
  error: string
}

export type EmailVerifyV2Response = {
  redirect_url?: string
}

export type EnableDemo = {
  expires_at?: string | null
}

export type Enum = {
  /**
   * key
   */
  value: string
  /**
   * Labels by language
   */
  labels: {
    [key: string]: unknown
  }
}

export type EnvironmentBuild = {
  preset: number | null
  /**
   * If true, automatically install Applications for service types declared by the pipeline (e.g. CRM/HRM) that are not yet present in the organization. Intended for initial workspace creation; recreate flows should leave it false.
   */
  install_missing_applications?: boolean
}

/**
 * Same body and redirect policy as the Outlook connect; `connection_id` makes it a reconnect.
 */
export type GoogleMailConnectStart = {
  domain_name: string
  redirect_uri: string
  return_origin?: string
  connection_id?: number | null
}

export type GoogleMailConnectStartResponse = {
  readonly auth_url: string
  readonly state: string
  readonly expires_at: string
  readonly scopes: Array<string>
}

/**
 * One connected Gmail mailbox; never the refresh token.
 */
export type GoogleMailConnection = {
  readonly id: number
  /**
   * Email address of the connected Gmail mailbox.
   */
  readonly email: string
  /**
   * Display name returned by Google for this connected account.
   */
  readonly display_name: string
  status: MicrosoftGraphConnectionStatusEnum
  readonly granted_scopes: Array<string>
  readonly last_used_at: string | null
  readonly last_error: string
  readonly connected_at: string
  readonly created_at: string
}

export type GoogleMailConnectionList = {
  readonly available: boolean
  readonly results: Array<GoogleMailConnection>
}

export type GoogleMailDisconnect = {
  domain_name: string
}

/**
 * A refusal the UI branches on, e.g. `provider_not_configured` when Gmail is switched off.
 */
export type GoogleMailError = {
  readonly detail: string
  readonly error_code: string
}

/**
 * Same `error_code` vocabulary as the Outlook broker.
 */
export type GoogleMailTokenError = {
  readonly detail: string
  error_code: MicrosoftGraphBrokerErrorCodeEnum
}

/**
 * `user_id` is the member's sso_id; without `connection_id` the latest active mailbox answers.
 */
export type GoogleMailTokenRequest = {
  organization_id: number
  user_id: string
  connection_id?: number | null
}

export type GoogleMailTokenResponse = {
  readonly access_token: string
  readonly token_type: string
  readonly expires_at: string
  readonly connection_id: number
  readonly email: string
}

/**
 * * `authorization-code` - Authorization code
 * * `implicit` - Implicit
 * * `password` - Resource owner password-based
 * * `client-credentials` - Client credentials
 * * `openid-hybrid` - OpenID connect hybrid
 */
export const GrantTypeEnum = {
  AUTHORIZATION_CODE: 'authorization-code',
  IMPLICIT: 'implicit',
  PASSWORD: 'password',
  CLIENT_CREDENTIALS: 'client-credentials',
  OPENID_HYBRID: 'openid-hybrid'
} as const

/**
 * * `authorization-code` - Authorization code
 * * `implicit` - Implicit
 * * `password` - Resource owner password-based
 * * `client-credentials` - Client credentials
 * * `openid-hybrid` - OpenID connect hybrid
 */
export type GrantTypeEnum = (typeof GrantTypeEnum)[keyof typeof GrantTypeEnum]

export type HeadUser = {
  id: number
  first_name: string
  last_name: string
  email: string
  readonly organization_access_id: number | null
  photo: ShortAttachment | null
}

export type ImpersonateResponse = {
  status: string
  access: string
  refresh: string
  organization_id: number
}

/**
 * A single in-app notification, as surfaced from the Notification service.
 */
export type InAppNotificationItem = {
  readonly id: number
  readonly is_read: boolean
  readonly is_seen: boolean
  readonly sender: string | null
  readonly status: string
  readonly delivery_method: string | null
  readonly distribution_date: string | null
  readonly created_at: string
  readonly message_subject: string | null
  readonly message_category: string
  readonly message_body: string | null
  readonly message_lang: string | null
  readonly redirect_url: string | null
}

export type InAppNotificationsList = {
  readonly count: number
  readonly page: number
  readonly page_size: number
  readonly results: Array<InAppNotificationItem>
}

export type InAppNotificationsMarkReadRequest = {
  ids?: Array<number> | null
}

export type InAppNotificationsMarkReadResponse = {
  readonly updated: number
}

export type InAppNotificationsUnreadCount = {
  readonly count: number
}

export type Industry = {
  readonly id: number
  name: string
}

/**
 * * `generic` - Generic
 * * `information_technology` - Information Technology
 * * `healthcare` - Healthcare
 * * `education` - Education
 * * `retail` - Retail
 * * `finance` - Finance
 * * `no_data` - Clean workspace
 */
export const IndustryEnum = {
  GENERIC: 'generic',
  INFORMATION_TECHNOLOGY: 'information_technology',
  HEALTHCARE: 'healthcare',
  EDUCATION: 'education',
  RETAIL: 'retail',
  FINANCE: 'finance',
  NO_DATA: 'no_data'
} as const

/**
 * * `generic` - Generic
 * * `information_technology` - Information Technology
 * * `healthcare` - Healthcare
 * * `education` - Education
 * * `retail` - Retail
 * * `finance` - Finance
 * * `no_data` - Clean workspace
 */
export type IndustryEnum = (typeof IndustryEnum)[keyof typeof IndustryEnum]

export type IntegrationApplication = {
  readonly id: number
  name: string
  is_public?: boolean
  type: string
  token: string
}

export type IntegrationApplicationDetail = {
  readonly id: number
  name: string
  is_public?: boolean
  instance_type: number
  token: string
  roles?: Array<string>
  /**
   * This tenant's own API rate limit, used by the application's service in place of its default. In CRM: requests per user and IP, per hour for reads and per day for creates. Empty keeps the default. A change is pushed to the service right away (tenant.sync).
   */
  api_rate_limit?: number | null
  organization: IntegrationApplicationOrganization
}

export type IntegrationApplicationOrganization = {
  readonly id: number
  readonly name: string
  readonly domain_name: string
}

export type IntegrationApplicationRoles = {
  /**
   * List of roles to set for the application
   */
  roles: Array<string>
}

export type IntegrationApplicationType = {
  readonly id: number
  name: string
  type: string
}

export type IntegrationNotifications = {
  notifications: Array<Enum>
}

export type IntegrationRolesPermissions = {
  permissions: Array<Enum>
}

export type InternalMfaConfig = {
  readonly id: number
  readonly user_username: string
  enabled?: boolean
  email_enabled?: boolean
  preferred_method?: OtpCodeChannelEnum
  readonly created_at: string
  readonly updated_at: string
}

export type InternalMfaLockout = {
  readonly id: number
  readonly user_username: string
  lockout_type: LockoutTypeEnum
  locked_until: string
  reason: string
  readonly created_at: string
}

export type InviteInfoV2Response = {
  email: string
  organization_name: string
  has_password: boolean
}

export const InviteStateEnum = {
  PENDING: 'pending',
  EXPIRED: 'expired',
  ACCEPTED: 'accepted',
  REVOKED: 'revoked'
} as const

export type InviteStateEnum =
  (typeof InviteStateEnum)[keyof typeof InviteStateEnum]

/**
 * Who is asking. An administrator deciding on a request has to see a person.
 */
export type JoinRequestUser = {
  id: number
  email: string
  full_name: string
}

/**
 * * `wizard` - wizard
 * * `checklist` - checklist
 */
export const JourneyActionMethodEnum = {
  WIZARD: 'wizard',
  CHECKLIST: 'checklist'
} as const

/**
 * * `wizard` - wizard
 * * `checklist` - checklist
 */
export type JourneyActionMethodEnum =
  (typeof JourneyActionMethodEnum)[keyof typeof JourneyActionMethodEnum]

/**
 * * `todo` - todo
 * * `done` - done
 * * `failed` - failed
 */
export const JourneyActionStatusEnum = {
  TODO: 'todo',
  DONE: 'done',
  FAILED: 'failed'
} as const

/**
 * * `todo` - todo
 * * `done` - done
 * * `failed` - failed
 */
export type JourneyActionStatusEnum =
  (typeof JourneyActionStatusEnum)[keyof typeof JourneyActionStatusEnum]

export const KindEnum = { PASSWORD: 'password', OIDC: 'oidc' } as const

export type KindEnum = (typeof KindEnum)[keyof typeof KindEnum]

/**
 * * `en` - En
 * * `ru` - Ru
 * * `ro` - Ro
 */
export const LanguageEnum = {
  EN: 'en',
  RU: 'ru',
  RO: 'ro'
} as const

/**
 * * `en` - En
 * * `ru` - Ru
 * * `ro` - Ro
 */
export type LanguageEnum = (typeof LanguageEnum)[keyof typeof LanguageEnum]

/**
 * Response schema for GET /account/linked-auth-methods/.
 */
export type LinkedAuthMethods = {
  readonly has_password: boolean
  readonly social_providers: Array<SocialProviderInfo>
}

/**
 * * `otp_max_failed_verify_attempts` - OTP Max Failed Verify Attempts
 * * `otp_send_rate_limit` - OTP Send Rate Limit
 * * `max_daily_lockouts` - Max Daily Lockouts
 */
export const LockoutTypeEnum = {
  OTP_MAX_FAILED_VERIFY_ATTEMPTS: 'otp_max_failed_verify_attempts',
  OTP_SEND_RATE_LIMIT: 'otp_send_rate_limit',
  MAX_DAILY_LOCKOUTS: 'max_daily_lockouts'
} as const

/**
 * * `otp_max_failed_verify_attempts` - OTP Max Failed Verify Attempts
 * * `otp_send_rate_limit` - OTP Send Rate Limit
 * * `max_daily_lockouts` - Max Daily Lockouts
 */
export type LockoutTypeEnum =
  (typeof LockoutTypeEnum)[keyof typeof LockoutTypeEnum]

export type MfaConfig = {
  readonly id: number
  enabled?: boolean
  email_enabled?: boolean
  preferred_method?: OtpCodeChannelEnum
  readonly backup_codes_count: number
  readonly created_at: string
  readonly updated_at: string
}

export type MfaConfigCreate = {
  enabled?: boolean
  email_enabled?: boolean
  preferred_method?: OtpCodeChannelEnum
}

export type MfaConfigGenerateBackupCodes = {
  readonly backup_codes: Array<string>
}

export type MfaSendOtp = {
  channel?: OtpCodeChannelEnum
  purpose?: PurposeEnum
}

export type MfaVerifyBackupCode = {
  backup_code: string
}

export type MfaVerifyOtp = {
  otp_code: string
  trusted_device?: boolean
  device_name?: string | null
}

export type McpApplicationRef = {
  id: number
  name: string
}

export type McpConnection = {
  readonly id: string
  readonly organization_id: number
  service: McpServiceEnum
  kind: McpConnectionKindEnum
  readonly name: string
  readonly client_id: string | null
  readonly scopes: string
  status: McpConnectionStatusEnum
  readonly mcp_url: string
  application: McpApplicationRef | null
  user: McpConnectionUser
  readonly created_at: string
  readonly last_used_at: string | null
  readonly expires_at: string | null
  readonly revoked_at: string | null
  readonly revoke_reason: string
  readonly revoke_note: string
}

/**
 * * `oauth` - OAuth
 * * `pat` - Personal access token
 */
export const McpConnectionKindEnum = { OAUTH: 'oauth', PAT: 'pat' } as const

/**
 * * `oauth` - OAuth
 * * `pat` - Personal access token
 */
export type McpConnectionKindEnum =
  (typeof McpConnectionKindEnum)[keyof typeof McpConnectionKindEnum]

export type McpConnectionRevoke = {
  reason?: string | null
}

export type McpConnectionScopesUpdate = {
  /**
   * Space-delimited scope codes of the connection's service.
   */
  scopes: string
}

export type McpConnectionStatus = {
  cid: string
  status: McpConnectionStatusEnum
  organization_id: number
  service: McpServiceEnum
  application_token: string | null
  user_sso_id: string
  /**
   * The connection's scopes; empty while the policy is off.
   */
  scopes: string
  /**
   * As GET /applications/user/<token>/details computes it.
   */
  user_access: McpUserAccess
}

/**
 * * `active` - Connected
 * * `revoked` - Revoked
 * * `expired` - Expired
 */
export const McpConnectionStatusEnum = {
  ACTIVE: 'active',
  REVOKED: 'revoked',
  EXPIRED: 'expired'
} as const

/**
 * * `active` - Connected
 * * `revoked` - Revoked
 * * `expired` - Expired
 */
export type McpConnectionStatusEnum =
  (typeof McpConnectionStatusEnum)[keyof typeof McpConnectionStatusEnum]

export type McpConnectionUser = {
  id: number
  email: string
  readonly full_name: string
  first_name: string
  last_name: string
}

export type McpConsentApprove = {
  organization_id: number
  /**
   * The offered scopes this connection gets; omitted means all of them.
   */
  scopes?: Array<string>
}

export type McpConsentClient = {
  client_id: string
  name: string
  /**
   * Always false in v1: self-registered clients are unverified.
   */
  verified: boolean
  redirect_uri: string
  redirect_host: string
}

export type McpConsentDecision = {
  /**
   * Navigate the browser here (window.location).
   */
  redirect_to: string
}

export type McpConsentOrganization = {
  id: number
  name: string
  /**
   * The scopes the client requested of this service.
   */
  scopes: Array<McpConsentScope>
}

export type McpConsentRequest = {
  request_id: string
  client: McpConsentClient
  service: McpServiceEnum
  mcp_url: string
  organizations: Array<McpConsentOrganization>
}

export type McpConsentScope = {
  code: string
  description: string
}

export type McpMemberService = {
  service: McpServiceEnum
  is_enabled: boolean
  /**
   * Always true: only accessible services are listed.
   */
  has_access: boolean
  mcp_url: string
  application: McpApplicationRef | null
  /**
   * The caller passes the admin check (applications.manage).
   */
  can_manage: boolean
}

export type McpPersonalTokenCreate = {
  organization_id: number
  service: McpServiceEnum
  name: string
  /**
   * Space-delimited scope codes of the service.
   */
  scopes: string
  /**
   * Required; at most MCP_PERSONAL_TOKEN_MAX_LIFETIME_DAYS ahead.
   */
  expires_at: string
}

export type McpPersonalTokenCreated = {
  /**
   * The PAT (an mcp_access JWT). Returned only once.
   */
  token: string
  connection: McpConnection
}

/**
 * * `core` - Core
 * * `crm` - CRM
 * * `notification` - Notification
 * * `squad` - Squad
 */
export const McpServiceEnum = {
  CORE: 'core',
  CRM: 'crm',
  NOTIFICATION: 'notification',
  SQUAD: 'squad'
} as const

/**
 * * `core` - Core
 * * `crm` - CRM
 * * `notification` - Notification
 * * `squad` - Squad
 */
export type McpServiceEnum =
  (typeof McpServiceEnum)[keyof typeof McpServiceEnum]

export type McpServicePolicy = {
  service: McpServiceEnum
  is_enabled: boolean
  /**
   * Canonical MCP URL (trailing slash).
   */
  mcp_url: string
  /**
   * Configured and, for CRM/Squad, installed.
   */
  available: boolean
  application: McpApplicationRef | null
  updated_at: string | null
}

export type McpUserAccess = {
  is_admin: boolean
  role: string | null
  permissions: Array<string>
  /**
   * Effective organization role (owner, admin, employee); null without an active membership.
   */
  organization_role: string | null
}

/**
 * A member's successful sign-in count over a trailing window (audit-log backed).
 */
export type MemberSignInActivity = {
  sign_ins: number
  days: number
}

/**
 * * `work` - Work or school account
 * * `personal` - Personal Microsoft account
 */
export const MicrosoftAccountTypeEnum = {
  WORK: 'work',
  PERSONAL: 'personal'
} as const

/**
 * * `work` - Work or school account
 * * `personal` - Personal Microsoft account
 */
export type MicrosoftAccountTypeEnum =
  (typeof MicrosoftAccountTypeEnum)[keyof typeof MicrosoftAccountTypeEnum]

/**
 * * `organization` - Workspace Entra ID app
 * * `platform` - Skalx Microsoft app
 */
export const MicrosoftGraphAppEnum = {
  ORGANIZATION: 'organization',
  PLATFORM: 'platform'
} as const

/**
 * * `organization` - Workspace Entra ID app
 * * `platform` - Skalx Microsoft app
 */
export type MicrosoftGraphAppEnum =
  (typeof MicrosoftGraphAppEnum)[keyof typeof MicrosoftGraphAppEnum]

/**
 * Which apps can connect an Outlook mailbox in this workspace right now.
 */
export type MicrosoftGraphAvailability = {
  readonly organization: boolean
  readonly platform: boolean
}

/**
 * * `connection_not_found` - Connection not found
 * * `reconnect_required` - Reconnect required
 * * `token_exchange_failed` - Token exchange failed
 */
export const MicrosoftGraphBrokerErrorCodeEnum = {
  CONNECTION_NOT_FOUND: 'connection_not_found',
  RECONNECT_REQUIRED: 'reconnect_required',
  TOKEN_EXCHANGE_FAILED: 'token_exchange_failed'
} as const

/**
 * * `connection_not_found` - Connection not found
 * * `reconnect_required` - Reconnect required
 * * `token_exchange_failed` - Token exchange failed
 */
export type MicrosoftGraphBrokerErrorCodeEnum =
  (typeof MicrosoftGraphBrokerErrorCodeEnum)[keyof typeof MicrosoftGraphBrokerErrorCodeEnum]

export type MicrosoftGraphConnectStart = {
  domain_name: string
  redirect_uri: string
  return_origin?: string
  connection_id?: number | null
  app?: MicrosoftGraphAppEnum | NullEnum | null
}

export type MicrosoftGraphConnectStartResponse = {
  readonly auth_url: string
  readonly state: string
  readonly expires_at: string
  readonly scopes: Array<string>
  readonly permissions: Array<MicrosoftGraphPermission>
}

/**
 * One row of the connected-mailboxes list.
 *
 * Deliberately not a `__all__` serializer: the model also holds the refresh token and the
 * tenant, and neither belongs in a browser.
 */
export type MicrosoftGraphConnection = {
  readonly id: number
  /**
   * Email address of the connected Microsoft mailbox.
   */
  readonly email: string
  /**
   * Display name returned by Microsoft for this connected account.
   */
  readonly display_name: string
  status: MicrosoftGraphConnectionStatusEnum
  readonly granted_scopes: {
    [key: string]: unknown
  }
  readonly last_used_at: string | null
  readonly last_error: string
  readonly connected_at: string
  app: MicrosoftGraphAppEnum
  account_type: MicrosoftAccountTypeEnum
}

/**
 * * `active` - Active
 * * `needs_reconnect` - Needs reconnect
 * * `revoked` - Revoked
 */
export const MicrosoftGraphConnectionStatusEnum = {
  ACTIVE: 'active',
  NEEDS_RECONNECT: 'needs_reconnect',
  REVOKED: 'revoked'
} as const

/**
 * * `active` - Active
 * * `needs_reconnect` - Needs reconnect
 * * `revoked` - Revoked
 */
export type MicrosoftGraphConnectionStatusEnum =
  (typeof MicrosoftGraphConnectionStatusEnum)[keyof typeof MicrosoftGraphConnectionStatusEnum]

export type MicrosoftGraphDisconnect = {
  domain_name: string
}

export type MicrosoftGraphPermission = {
  readonly scope: string
  readonly description_ro: string
  readonly description_ru: string
  readonly description_en: string
}

export type MicrosoftGraphStatus = {
  readonly email: string | null
  readonly display_name: string
  status: MicrosoftGraphConnectionStatusEnum | NullEnum | null
  readonly granted_scopes: Array<string>
}

/**
 * The 400 body of the token broker — `error_code` is what a CRM service branches on.
 */
export type MicrosoftGraphTokenError = {
  readonly detail: string
  error_code: MicrosoftGraphBrokerErrorCodeEnum
}

export type MicrosoftGraphTokenRequest = {
  organization_id: number
  user_id: string
  connection_id?: number | null
}

export type MicrosoftGraphTokenResponse = {
  readonly access_token: string
  readonly token_type: string
  readonly expires_at: string
  readonly connection_id: number
  readonly email: string
}

export type MobileAppBuild = {
  app: AppEnum
  /**
   * The version people see, e.g. "1.2.0".
   */
  version_name: string
  /**
   * Android versionCode. The highest active one is the latest, as a phone would judge it.
   */
  version_code: number
  readonly size: number
  release_notes?: string
  readonly date_created: string
  /**
   * Short-lived presigned link to the APK. Fetch a fresh one rather than storing it.
   */
  readonly download_url: string
}

export type Nested = {
  readonly id: number
  name: string
  billing_first_name?: string | null
  billing_registration_number?: string | null
  billing_last_name?: string | null
  billing_phone?: string | null
  billing_email?: string | null
  billing_address1?: string | null
  billing_address2?: string | null
  billing_city?: string | null
  billing_zip?: string | null
  billing_industry?: number | null
  billing_country?: number | null
}

/**
 * Per-app/category/channel matrix for the current user.
 *
 * Format mirrors the Notification service: ``{app_code: {category: [channels]}}``.
 * A channel listed for a category means the user has *opted out* of receiving that
 * category on that channel. ``email``/``push``/``internal`` are the supported channels.
 */
export type NotificationPreferences = {
  /**
   * {app_code: {category: [channels]}} matrix of opted-out (category, channel) pairs.
   */
  disabled_categories: {
    [key: string]: {
      [key: string]: Array<string>
    }
  }
}

export type NotificationServiceSync = {
  uuid: string
}

export type NotificationServiceUser = {
  uuid: string
  /**
   * Email address
   */
  readonly email: string
  readonly name: string
  organizations_access: Array<UserOrganizationAccess>
}

export const NullEnum = {} as const

export type NullEnum = (typeof NullEnum)[keyof typeof NullEnum]

/**
 * * `email` - Email
 */
export const OtpCodeChannelEnum = { EMAIL: 'email' } as const

/**
 * * `email` - Email
 */
export type OtpCodeChannelEnum =
  (typeof OtpCodeChannelEnum)[keyof typeof OtpCodeChannelEnum]

/**
 * Read shape of a journey. Requires `manifest` in the serializer context.
 */
export type OnboardingJourney = {
  readonly scope: string
  role_variant: RoleVariantEnum
  status: OnboardingJourneyStatusEnum
  readonly current_step: string
  readonly activated_at: string | null
  readonly is_collapsed: boolean
  readonly hidden_until: string | null
  readonly dismissed_at: string | null
  dismiss_reason: DismissReasonEnum
  readonly steps: Array<OnboardingStep>
  readonly cross_links: Array<CrossLink>
}

/**
 * * `in_progress` - In progress
 * * `completed` - Completed
 */
export const OnboardingJourneyStatusEnum = {
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed'
} as const

/**
 * * `in_progress` - In progress
 * * `completed` - Completed
 */
export type OnboardingJourneyStatusEnum =
  (typeof OnboardingJourneyStatusEnum)[keyof typeof OnboardingJourneyStatusEnum]

/**
 * Industry is write-once — the workspace was seeded from it (BR-12).
 */
export type OnboardingProfile = {
  readonly id: number
  industry: IndustryEnum
  company_size: CompanySizeEnum
  primary_job?: PrimaryJobEnum | BlankEnum
  estimated_users?: number | null
  source?: AttributionSourceEnum | BlankEnum
  locale?: string
}

/**
 * Industry is write-once — the workspace was seeded from it (BR-12).
 */
export type OnboardingProfileResponse = {
  readonly id: number
  industry: IndustryEnum
  company_size: CompanySizeEnum
  primary_job?: PrimaryJobEnum | BlankEnum
  estimated_users?: number | null
  source?: AttributionSourceEnum | BlankEnum
  locale?: string
  provisioning: ProvisioningState
}

export type OnboardingStep = {
  key: string
  status: OnboardingStepStatusEnum
  completed_at: string | null
  skip_reason: SkipReasonEnum | NullEnum | null
  error_code: OnboardingStepErrorCodeEnum | NullEnum | null
}

/**
 * * `code_expired` - Verification code expired
 * * `code_invalid` - Verification code invalid
 * * `m365_admin_consent_required` - Microsoft 365 admin consent required
 * * `domain_not_verified` - Domain not verified
 * * `import_parse_failed` - Import could not be parsed
 * * `provisioning_failed` - Provisioning failed
 */
export const OnboardingStepErrorCodeEnum = {
  CODE_EXPIRED: 'code_expired',
  CODE_INVALID: 'code_invalid',
  M365_ADMIN_CONSENT_REQUIRED: 'm365_admin_consent_required',
  DOMAIN_NOT_VERIFIED: 'domain_not_verified',
  IMPORT_PARSE_FAILED: 'import_parse_failed',
  PROVISIONING_FAILED: 'provisioning_failed'
} as const

/**
 * * `code_expired` - Verification code expired
 * * `code_invalid` - Verification code invalid
 * * `m365_admin_consent_required` - Microsoft 365 admin consent required
 * * `domain_not_verified` - Domain not verified
 * * `import_parse_failed` - Import could not be parsed
 * * `provisioning_failed` - Provisioning failed
 */
export type OnboardingStepErrorCodeEnum =
  (typeof OnboardingStepErrorCodeEnum)[keyof typeof OnboardingStepErrorCodeEnum]

/**
 * * `todo` - To do
 * * `done` - Done
 * * `skipped` - Skipped
 * * `failed` - Failed
 */
export const OnboardingStepStatusEnum = {
  TODO: 'todo',
  DONE: 'done',
  SKIPPED: 'skipped',
  FAILED: 'failed'
} as const

/**
 * * `todo` - To do
 * * `done` - Done
 * * `skipped` - Skipped
 * * `failed` - Failed
 */
export type OnboardingStepStatusEnum =
  (typeof OnboardingStepStatusEnum)[keyof typeof OnboardingStepStatusEnum]

export type OperationStatus = {
  success: boolean
}

/**
 * Response schema for GET /account/org-linked-methods/{org_id}/.
 */
export type OrgLinkedMethods = {
  readonly has_password: boolean
  readonly google_linked: boolean
  readonly entraid_linked: boolean
  readonly apple_linked: boolean
}

export type Organization = {
  readonly id: number
  photo: Attachment
  readonly date_created: string
  readonly date_updated: string
  name: string
  domain_name: string
  allow_ad_sync?: boolean
  google_client_id?: string | null
  enforce_sso?: boolean
  /**
   * Workspace MFA policy: when set, every member of this organization must complete multi-factor authentication at login (composed with each member's own enrollment and any per-member override).
   */
  require_mfa?: boolean
  allow_self_service_join?: boolean
  auto_provision_users?: boolean
  /**
   * Workspace default UI language; new or no-preference members start in this language.
   *
   * * `en` - En
   * * `ru` - Ru
   * * `ro` - Ro
   */
  default_language?: LanguageEnum
  /**
   * Subset of supported UI languages this workspace offers in its language switcher.
   */
  enabled_languages?: Array<LanguageEnum>
  readonly is_demo: boolean
  readonly demo_cleanup_in_progress: boolean
  readonly demo_expires_at: string | null
  account?: number | null
}

export type OrganizationAccess = {
  readonly id: number
  readonly date_created: string
  readonly date_updated: string
  readonly user: number
  readonly organization: number
  readonly role: string
  job_title?: string | null
  readonly blocked: boolean
  /**
   * Per-member MFA override (org-scoped): when set, this member must complete MFA in THIS workspace. Independent of the member's account-global MFA enrollment, so forcing MFA in one workspace never changes their posture in another (fixes the cross-workspace leak).
   */
  readonly require_mfa: boolean
  /**
   * Set when an admin cancels a still-pending invite. Cleared when the invite is resent.
   */
  readonly invite_revoked_at: string | null
  /**
   * Set when the member accepts THIS workspace's invite (completes setup via the invite token). Authoritative per-workspace 'accepted' signal; when null the status falls back to the last_login>=invite_issued heuristic.
   */
  readonly invite_accepted_at: string | null
  /**
   * Head user who manages this user in the organization
   */
  head_user?: number | null
}

export type OrganizationAccessBlock = {
  blocked_value: boolean
}

export type OrganizationAccessUser = {
  id: number
  email: string
  first_name: string
  last_name: string
  readonly phone: string | null
  photo: ShortAttachment | null
  readonly last_login: string | null
  readonly position: string | null
  readonly sso_id: string | null
  readonly teams: Array<OrganizationTeamShort>
  language: string
  readonly application_role: string | null
  readonly archived: boolean
}

export type OrganizationAuthMethod = {
  method: OrganizationAuthMethodEnum
  enabled?: boolean
  client_id?: string | null
  readonly client_secret_masked: string | null
  tenant_id?: string | null
  key_id?: string | null
}

/**
 * * `password` - Password
 * * `google` - Google
 * * `entraid` - Entra ID
 * * `apple` - Apple
 * * `email_otp` - Email OTP
 * * `passkey` - Passkey
 * * `qr_login` - QR Code
 */
export const OrganizationAuthMethodEnum = {
  PASSWORD: 'password',
  GOOGLE: 'google',
  ENTRAID: 'entraid',
  APPLE: 'apple',
  EMAIL_OTP: 'email_otp',
  PASSKEY: 'passkey',
  QR_LOGIN: 'qr_login'
} as const

/**
 * * `password` - Password
 * * `google` - Google
 * * `entraid` - Entra ID
 * * `apple` - Apple
 * * `email_otp` - Email OTP
 * * `passkey` - Passkey
 * * `qr_login` - QR Code
 */
export type OrganizationAuthMethodEnum =
  (typeof OrganizationAuthMethodEnum)[keyof typeof OrganizationAuthMethodEnum]

export type OrganizationAuthMethodPublic = {
  method: OrganizationAuthMethodEnum
  enabled?: boolean
}

export type OrganizationAuthMethodUpdate = {
  method: OrganizationAuthMethodEnum
  enabled: boolean
  client_id?: string | null
  tenant_id?: string | null
  key_id?: string | null
}

export type OrganizationAuthMethodsResponse = {
  auth_methods: Array<OrganizationAuthMethod>
  enforce_sso: boolean
  require_mfa: boolean
  allow_self_service_join: boolean
  auto_provision_users: boolean
}

export type OrganizationCreate = {
  readonly id: number
  photo?: number | null
  readonly date_created: string
  readonly date_updated: string
  name: string
  domain_name: string
  allow_ad_sync?: boolean
  google_client_id?: string | null
  enforce_sso?: boolean
  /**
   * Workspace MFA policy: when set, every member of this organization must complete multi-factor authentication at login (composed with each member's own enrollment and any per-member override).
   */
  require_mfa?: boolean
  allow_self_service_join?: boolean
  auto_provision_users?: boolean
  /**
   * Workspace default UI language; new or no-preference members start in this language.
   *
   * * `en` - En
   * * `ru` - Ru
   * * `ro` - Ro
   */
  default_language?: LanguageEnum
  /**
   * Subset of supported UI languages this workspace offers in its language switcher.
   */
  enabled_languages?: Array<LanguageEnum>
  is_demo?: boolean
  readonly demo_cleanup_in_progress: boolean
  readonly demo_expires_at: string | null
  account?: number | null
}

/**
 * Read serializer — secrets always masked.
 */
export type OrganizationCustomAuthProvider = {
  readonly id: number
  readonly organization: number
  name: string
  slug: string
  protocol: ProtocolEnum
  enabled?: boolean
  logo?: string
  client_id?: string | null
  readonly client_secret: string | null
  issuer_url?: string | null
  authorize_url?: string | null
  token_url?: string | null
  userinfo_url?: string | null
  jwks_url?: string | null
  scope?: string
  saml_entity_id?: string | null
  saml_sso_url?: string | null
  saml_slo_url?: string | null
  saml_x509_cert?: string | null
  email_claim?: string
  first_name_claim?: string
  last_name_claim?: string
  readonly date_created: string
  readonly date_updated: string
}

/**
 * Public/member-visible payload — no secrets, no internal URLs.
 */
export type OrganizationCustomAuthProviderPublic = {
  readonly id: number
  name: string
  slug: string
  protocol: ProtocolEnum
  enabled?: boolean
  logo?: string
}

/**
 * Write serializer — handles MASKED_CLIENT_SECRET passthrough and per-protocol validation.
 */
export type OrganizationCustomAuthProviderWrite = {
  name: string
  slug: string
  protocol: ProtocolEnum
  enabled?: boolean
  logo?: string
  client_id?: string | null
  issuer_url?: string | null
  authorize_url?: string | null
  token_url?: string | null
  userinfo_url?: string | null
  jwks_url?: string | null
  scope?: string
  saml_entity_id?: string | null
  saml_sso_url?: string | null
  saml_slo_url?: string | null
  saml_x509_cert?: string | null
  email_claim?: string
  first_name_claim?: string
  last_name_claim?: string
}

export type OrganizationInviteUser = {
  organization: number
  emails: Array<string>
  /**
   * Invite URL prefix (e.g. https://{domain}.example.com/authentication/invitation/?token=). The invite token is appended to this value by the server.
   */
  invite_url: string
  /**
   * Head user who will manage the invited users
   */
  head_user?: number | null
}

export type OrganizationJoinRequest = {
  readonly id: number
  status: OrganizationJoinRequestStatusEnum
  readonly email: string
  user: JoinRequestUser
  readonly date_created: string
  readonly reviewed_at: string | null
}

/**
 * * `pending` - Pending
 * * `approved` - Approved
 * * `rejected` - Rejected
 */
export const OrganizationJoinRequestStatusEnum = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected'
} as const

/**
 * * `pending` - Pending
 * * `approved` - Approved
 * * `rejected` - Rejected
 */
export type OrganizationJoinRequestStatusEnum =
  (typeof OrganizationJoinRequestStatusEnum)[keyof typeof OrganizationJoinRequestStatusEnum]

export type OrganizationLong = {
  readonly id: number
  name: string
  domain_name: string
  readonly date_created: string
  photo: Attachment
  readonly users: number | null
  /**
   * Organization Role
   */
  readonly access_role: string
  is_demo?: boolean
  demo_expires_at?: string | null
  demo_cleanup_in_progress?: boolean
  /**
   * Workspace default UI language; new or no-preference members start in this language.
   *
   * * `en` - En
   * * `ru` - Ru
   * * `ro` - Ro
   */
  default_language?: LanguageEnum
  /**
   * Subset of supported UI languages this workspace offers in its language switcher.
   */
  enabled_languages?: Array<LanguageEnum>
}

/**
 * A single role grant on a member, tagged with its scope.
 *
 * ``application`` is the discriminator the members list needs: ``null`` marks a
 * workspace/organization role, a number marks an application role. Fed the plain
 * dicts built by ``_roles`` (JSONObject), so fields stay primitive — a model FK
 * field would choke on the raw ``application`` id.
 */
export type OrganizationMemberRole = {
  readonly id: number
  readonly name: string
  readonly description: string | null
  readonly codename: string | null
  readonly application: number | null
  readonly is_admin: boolean
}

export type OrganizationMicrosoftGraphAppCredentials = {
  readonly id: number
  readonly tenant_id: string | null
  readonly client_id: string | null
  readonly enabled: boolean
  readonly date_created: string
  readonly date_updated: string
}

export type OrganizationOrganigrama = {
  readonly id: number
  role: OrganizationRole
  user: OrganizationOrganigramaUser
  head_user: OrganizationOrganigramaUser
  job_title?: string | null
  readonly children_count: number
}

export type OrganizationOrganigramaUser = {
  readonly id: number
  /**
   * Email address
   */
  email: string
  first_name: string
  last_name: string
  phone?: string | null
  photo: Attachment
  last_login?: string | null
  position?: string | null
  sso_id?: string | null
  language?: LanguageEnum
  archived?: boolean
}

export type OrganizationOwnershipTransfer = {
  /**
   * User ID of the active workspace member who will become the owner.
   */
  new_owner_id: number
}

export type OrganizationOwnershipTransferResponse = {
  organization_id: number
  previous_owner_id: number
  new_owner_id: number
}

/**
 * * `owner` - Owner
 * * `admin` - Admin
 * * `employee` - Employee
 */
export const OrganizationRole = {
  OWNER: 'owner',
  ADMIN: 'admin',
  EMPLOYEE: 'employee'
} as const

/**
 * * `owner` - Owner
 * * `admin` - Admin
 * * `employee` - Employee
 */
export type OrganizationRole =
  (typeof OrganizationRole)[keyof typeof OrganizationRole]

/**
 * Supplies localized, role-named descriptions for OrganizationRole audit events.
 */
export type OrganizationRoleEdit = {
  readonly id: number
  readonly date_created: string
  readonly date_updated: string
  /**
   * Human-readable role name (e.g., 'Sales Manager')
   */
  name: string
  /**
   * Machine-readable role code name (e.g., 'sales_manager')
   */
  codename?: string | null
  description?: string
  /**
   * Admin flag: when True, role automatically has ALL permissions without explicit assignment for specified application (or if application is null, for organization-level permissions).
   */
  readonly is_admin: boolean
  readonly organization: number
  readonly permissions: Array<Permissions>
  readonly application: number | null
}

/**
 * Supplies localized, role-named descriptions for OrganizationRole audit events.
 */
export type OrganizationRoleFull = {
  readonly id: number
  readonly date_created: string
  readonly date_updated: string
  /**
   * Human-readable role name (e.g., 'Sales Manager')
   */
  name: string
  /**
   * Machine-readable role code name (e.g., 'sales_manager')
   */
  codename?: string | null
  description?: string
  /**
   * Admin flag: when True, role automatically has ALL permissions without explicit assignment for specified application (or if application is null, for organization-level permissions).
   */
  readonly is_admin: boolean
  organization: number
  readonly permissions: Array<PermissionsLocalized>
  application?: number
}

/**
 * Supplies localized, role-named descriptions for OrganizationRole audit events.
 */
export type OrganizationRoleRetrieve = {
  readonly id: number
  readonly date_created: string
  readonly date_updated: string
  /**
   * Human-readable role name (e.g., 'Sales Manager')
   */
  name: string
  /**
   * Machine-readable role code name (e.g., 'sales_manager')
   */
  codename?: string | null
  description?: string
  /**
   * Admin flag: when True, role automatically has ALL permissions without explicit assignment for specified application (or if application is null, for organization-level permissions).
   */
  readonly is_admin: boolean
  organization: number
  permissions: Array<PermissionsLocalized>
  application?: number
  readonly users_count: number
  readonly user_assignments: Array<OrganizationRoleUserAssignment>
}

export type OrganizationRoleShort = {
  readonly id: number
  /**
   * Human-readable role name (e.g., 'Sales Manager')
   */
  name: string
  description?: string
}

/**
 * Read-only: who holds this role, and whether the grant is personal or group-derived.
 */
export type OrganizationRoleUserAssignment = {
  readonly id: number
  readonly organization_access_id: number
  user: UserVeryShort
  readonly source: string
  source_group: UserGroupShort | null
}

export type OrganizationShort = {
  readonly id: number
  name: string
  domain_name: string
  readonly date_created: string
  photo: ShortAttachment
  google_client_id?: string | null
  readonly auth_methods: Array<OrganizationAuthMethodPublic>
  readonly personal_auth_methods: Array<OrganizationAuthMethodPublic>
  readonly custom_providers: Array<OrganizationCustomAuthProviderPublic>
  /**
   * Workspace default UI language; new or no-preference members start in this language.
   *
   * * `en` - En
   * * `ru` - Ru
   * * `ro` - Ro
   */
  default_language?: LanguageEnum
  /**
   * Subset of supported UI languages this workspace offers in its language switcher.
   */
  enabled_languages?: Array<LanguageEnum>
}

export type OrganizationTeamShort = {
  readonly id: number
  name: string
  readonly parent: number | null
}

export type OrganizationTeamsOrganigram = {
  readonly id: number
  name: string
  type?: TeamTypeEnum | NullEnum | null
  abbreviation?: string | null
  readonly children_count: number
  readonly users_count: number
  parent: TeamShort
}

export type OrganizationTiny = {
  readonly id: number
  name: string
}

/**
 * Public workspace profile fields accepted by the generic PATCH endpoint.
 */
export type OrganizationUpdate = {
  readonly id: number
  readonly date_created: string
  readonly date_updated: string
  name: string
  photo?: number | null
  readonly account: number | null
  domain_name: string
  readonly allow_ad_sync: boolean
  readonly google_client_id: string | null
  readonly enforce_sso: boolean
  /**
   * Workspace MFA policy: when set, every member of this organization must complete multi-factor authentication at login (composed with each member's own enrollment and any per-member override).
   */
  readonly require_mfa: boolean
  readonly allow_self_service_join: boolean
  readonly auto_provision_users: boolean
  /**
   * Workspace default UI language; new or no-preference members start in this language.
   *
   * * `en` - En
   * * `ru` - Ru
   * * `ro` - Ro
   */
  default_language?: LanguageEnum
  /**
   * Subset of supported UI languages this workspace offers in its language switcher.
   */
  enabled_languages?: Array<LanguageEnum>
  readonly is_demo: boolean
  readonly demo_cleanup_in_progress: boolean
  readonly demo_expires_at: string | null
}

export type OrganizationUsers = {
  readonly id: number
  readonly role: string
  user: OrganizationAccessUser
  head_user: HeadUser | null
  job_title?: string | null
  readonly status: string
  invite_state: InviteStateEnum
  readonly invite_expires_at: string | null
  /**
   * Whether MFA is switched on for this member — ``null`` for non-admin viewers.
   */
  readonly mfa_enabled: boolean | null
  /**
   * Whether MFA is *compelled* for this member in this workspace — the
   * per-member override OR the workspace-wide policy. Distinct from
   * ``mfa_enabled`` (the member's own enrollment); ``null`` for non-admins.
   */
  readonly mfa_required: boolean | null
  readonly teams: Array<OrganizationTeamShort>
  readonly roles: Array<OrganizationMemberRole>
  readonly joined_date: string
}

export type OrganizationVeryShort = {
  id: number
  name: string
}

/**
 * What the settings UI lists and retrieves.
 *
 * ``secret`` is deliberately absent. It is served only by the audited reveal endpoint --
 * shipping it with the list would make that audit trail meaningless, since the value would
 * already be in the browser and any "reveal" would be a client-side unmask of something the
 * user had been handed anyway.
 */
export type OutboundWebhookRead = {
  readonly id: number
  readonly name: string
  readonly url: string
  readonly events: Array<string>
  readonly is_active: boolean
  readonly managed_by: string
  readonly managed_ref: string
  readonly application: number
  readonly date_created: string
  readonly date_updated: string
}

export type OutboundWebhookSecret = {
  readonly secret: string
}

/**
 * What the Test button renders. Documentation and schema only -- the view builds the dict.
 *
 * Failures are described here rather than raised as 4xx: a refused URL, a timeout and an HTTP
 * 404 are all valid answers to "what happens if I send to this endpoint", and the UI shows
 * them in the same place.
 */
export type OutboundWebhookTestResult = {
  readonly ok: boolean
  readonly event: string
  readonly delivery_id: string
  readonly status_code: number | null
  readonly response_body: string | null
  readonly error: string | null
}

/**
 * Create/update. ``custom_secret`` is write-only and never echoed back.
 *
 * ``managed_by`` / ``managed_ref`` tag a webhook another service owns (Squad: ``"squad"`` /
 * ``"agent:<uuid>"``). They are set on create; a later request may repeat them, not change them.
 */
export type OutboundWebhookWrite = {
  url: string
  events?: Array<string>
  is_active?: boolean
  name?: string
  managed_by?: string
  managed_ref?: string
}

export type PaginatedAppRoleList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<AppRole>
}

export type PaginatedAppUserGroupList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<AppUserGroup>
}

export type PaginatedApplicationAccessListList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<ApplicationAccessList>
}

export type PaginatedApplicationAccessReadList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<ApplicationAccessRead>
}

export type PaginatedApplicationList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<Application>
}

export type PaginatedApplicationRepositoryList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<ApplicationRepository>
}

export type PaginatedAttachmentList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<Attachment>
}

export type PaginatedAuditLogArchiveList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<AuditLogArchive>
}

export type PaginatedAuditLogListList = {
  count: number
  next?: string | null
  previous?: string | null
  results: Array<AuditLogList>
}

export type PaginatedCountryList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<Country>
}

export type PaginatedIndustryList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<Industry>
}

export type PaginatedIntegrationApplicationList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<IntegrationApplication>
}

export type PaginatedInternalMfaConfigList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<InternalMfaConfig>
}

export type PaginatedInternalMfaLockoutList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<InternalMfaLockout>
}

export type PaginatedOrganizationAuthMethodPublicList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<OrganizationAuthMethodPublic>
}

export type PaginatedOrganizationCustomAuthProviderList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<OrganizationCustomAuthProvider>
}

export type PaginatedOrganizationJoinRequestList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<OrganizationJoinRequest>
}

export type PaginatedOrganizationList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<Organization>
}

export type PaginatedOrganizationRoleFullList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<OrganizationRoleFull>
}

export type PaginatedOrganizationTeamShortList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<OrganizationTeamShort>
}

export type PaginatedOrganizationUsersList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<OrganizationUsers>
}

export type PaginatedPasskeyList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<Passkey>
}

export type PaginatedPermissionsLocalizedList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<PermissionsLocalized>
}

export type PaginatedPublicNetworkingOrganizationList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<PublicNetworkingOrganization>
}

export type PaginatedRpcPresetList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<RpcPreset>
}

export type PaginatedServiceList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<Service>
}

export type PaginatedSessionList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<Session>
}

export type PaginatedSetupJobList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<SetupJob>
}

export type PaginatedSupportTicketList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<SupportTicket>
}

export type PaginatedTeamListList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<TeamList>
}

export type PaginatedTeamUsersDetailList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<TeamUsersDetail>
}

export type PaginatedTrustedDeviceList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<TrustedDevice>
}

export type PaginatedUserGroupList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<UserGroup>
}

export type PaginatedUserListList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<UserList>
}

export type PaginatedUserShortList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<UserShort>
}

export type PaginatedWorkspaceRoleList = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<WorkspaceRole>
}

export type Passkey = {
  readonly id: number
  name: string
  readonly credential_id_last_four: string
  platform: PlatformEnum
  readonly created_at: string
  readonly last_used_at: string | null
}

export type PasskeyAuthTokens = {
  status: string
  access: string
  refresh: string
}

export type PasskeyAuthenticateStart = {
  domain_name?: string
  service_token?: string
  platform?: PlatformEnum
}

export type PasskeyAuthenticateVerify = {
  credential: {
    [key: string]: unknown
  }
  challenge_id: string
  domain_name?: string
  service_token?: string
  platform?: PlatformEnum
}

export type PasskeyAuthenticationOptions = {
  challenge_id: string
  options: {
    [key: string]: unknown
  }
}

export type PasskeyRegisterStart = {
  name: string
  platform?: PlatformEnum
}

export type PasskeyRegisterVerify = {
  credential: {
    [key: string]: unknown
  }
  name?: string
  platform?: PlatformEnum
}

export type PasskeyRegistrationOptions = {
  options: {
    [key: string]: unknown
  }
}

export type PatchedAppCredentialUpdate = {
  name?: string
  expires_at?: string | null
  permission_codes?: Array<string>
}

export type PatchedAppRoleWrite = {
  name?: string
  codename?: string
  description?: string
  permissions_ids?: Array<number>
}

/**
 * Create/update permissions scoped to a specific Application (third-party custom apps).
 */
export type PatchedApplicationPermission = {
  readonly id?: number
  /**
   * Permission code: 'action' or 'module.action' or 'app.module.action'
   */
  code?: string
  label?: unknown
  description?: unknown
  application?: number
  readonly date_created?: string
  readonly date_updated?: string
}

export type PatchedApplicationScimConfigUpdate = {
  inbound_enabled?: boolean
  outbound_enabled?: boolean
  outbound_base_url?: string | null
}

export type PatchedApplicationUpdate = {
  readonly id?: number
  name?: string
  description?: string
  domain?: string | null
  app_url?: string | null
  /**
   * Attachment id of the uploaded launcher icon. Send null to remove and fall back to the default.
   */
  icon?: number | null
  /**
   * Role every member of the organization gets this app with. Must be a role of this app. Send null to stop auto-provisioning (already granted access is kept).
   */
  auto_provision_role?: number | null
}

export type PatchedApplicationWebhookConfigUpdate = {
  webhook_endpoint?: string | null
  public_access_webhook_endpoint?: string | null
}

export type PatchedAuthorizationV2oAuthClientUpdate = {
  name?: string
  client_type?: ClientTypeEnum
  grant_type?: GrantTypeEnum
  allow_client_credentials?: boolean
  is_revoked?: boolean
  algorithm?: AlgorithmEnum | BlankEnum
  pkce_required?: boolean
  redirect_uris?: Array<string>
  post_logout_redirect_uris?: Array<string>
  allowed_origins?: Array<string>
}

/**
 * Edit an existing service account.
 */
export type PatchedEditAccount = {
  service?: number
}

/**
 * Serializer for editing user password / email.
 */
export type PatchedEditUser = {
  password?: string
  new_password?: string
  confirm_password?: string
  email?: string
}

export type PatchedIntegrationPatchUser = {
  first_name?: string
  last_name?: string
  phone?: string
  position?: string
  photo?: Photo
}

export type PatchedInternalMfaConfig = {
  readonly id?: number
  readonly user_username?: string
  enabled?: boolean
  email_enabled?: boolean
  preferred_method?: OtpCodeChannelEnum
  readonly created_at?: string
  readonly updated_at?: string
}

/**
 * One PATCH: `set_step` reports a step's status; the rest are checklist intents (skip/dismiss/hide/…).
 */
export type PatchedJourneyAction = {
  action?: string
  step_key?: string
  reason?: string
  days?: number
  status?: JourneyActionStatusEnum
  method?: JourneyActionMethodEnum
  error_code?: OnboardingStepErrorCodeEnum
  user_recoverable?: boolean
  blocked_by?: string
}

export type PatchedMfaConfigCreate = {
  enabled?: boolean
  email_enabled?: boolean
  preferred_method?: OtpCodeChannelEnum
}

export type PatchedMcpServicePolicy = {
  is_enabled?: boolean
}

/**
 * Industry is write-once — the workspace was seeded from it (BR-12).
 */
export type PatchedOnboardingProfile = {
  readonly id?: number
  industry?: IndustryEnum
  company_size?: CompanySizeEnum
  primary_job?: PrimaryJobEnum | BlankEnum
  estimated_users?: number | null
  source?: AttributionSourceEnum | BlankEnum
  locale?: string
}

export type PatchedOrganizationAccess = {
  readonly id?: number
  readonly date_created?: string
  readonly date_updated?: string
  readonly user?: number
  readonly organization?: number
  readonly role?: string
  job_title?: string | null
  readonly blocked?: boolean
  /**
   * Per-member MFA override (org-scoped): when set, this member must complete MFA in THIS workspace. Independent of the member's account-global MFA enrollment, so forcing MFA in one workspace never changes their posture in another (fixes the cross-workspace leak).
   */
  readonly require_mfa?: boolean
  /**
   * Set when an admin cancels a still-pending invite. Cleared when the invite is resent.
   */
  readonly invite_revoked_at?: string | null
  /**
   * Set when the member accepts THIS workspace's invite (completes setup via the invite token). Authoritative per-workspace 'accepted' signal; when null the status falls back to the last_login>=invite_issued heuristic.
   */
  readonly invite_accepted_at?: string | null
  /**
   * Head user who manages this user in the organization
   */
  head_user?: number | null
}

export type PatchedOrganizationAuthMethodsPatch = {
  auth_methods?: Array<OrganizationAuthMethodUpdate>
  enforce_sso?: boolean
  require_mfa?: boolean
  allow_self_service_join?: boolean
  auto_provision_users?: boolean
}

/**
 * Write serializer — handles MASKED_CLIENT_SECRET passthrough and per-protocol validation.
 */
export type PatchedOrganizationCustomAuthProviderWrite = {
  name?: string
  slug?: string
  protocol?: ProtocolEnum
  enabled?: boolean
  logo?: string
  client_id?: string | null
  issuer_url?: string | null
  authorize_url?: string | null
  token_url?: string | null
  userinfo_url?: string | null
  jwks_url?: string | null
  scope?: string
  saml_entity_id?: string | null
  saml_sso_url?: string | null
  saml_slo_url?: string | null
  saml_x509_cert?: string | null
  email_claim?: string
  first_name_claim?: string
  last_name_claim?: string
}

export type PatchedOrganizationMicrosoftGraphAppCredentialsPatch = {
  tenant_id?: string | null
  client_id?: string | null
  enabled?: boolean
}

/**
 * Supplies localized, role-named descriptions for OrganizationRole audit events.
 */
export type PatchedOrganizationRoleEdit = {
  readonly id?: number
  readonly date_created?: string
  readonly date_updated?: string
  /**
   * Human-readable role name (e.g., 'Sales Manager')
   */
  name?: string
  /**
   * Machine-readable role code name (e.g., 'sales_manager')
   */
  codename?: string | null
  description?: string
  /**
   * Admin flag: when True, role automatically has ALL permissions without explicit assignment for specified application (or if application is null, for organization-level permissions).
   */
  readonly is_admin?: boolean
  readonly organization?: number
  readonly permissions?: Array<Permissions>
  readonly application?: number | null
}

/**
 * Public workspace profile fields accepted by the generic PATCH endpoint.
 */
export type PatchedOrganizationUpdate = {
  readonly id?: number
  readonly date_created?: string
  readonly date_updated?: string
  name?: string
  photo?: number | null
  readonly account?: number | null
  domain_name?: string
  readonly allow_ad_sync?: boolean
  readonly google_client_id?: string | null
  readonly enforce_sso?: boolean
  /**
   * Workspace MFA policy: when set, every member of this organization must complete multi-factor authentication at login (composed with each member's own enrollment and any per-member override).
   */
  readonly require_mfa?: boolean
  readonly allow_self_service_join?: boolean
  readonly auto_provision_users?: boolean
  /**
   * Workspace default UI language; new or no-preference members start in this language.
   *
   * * `en` - En
   * * `ru` - Ru
   * * `ro` - Ro
   */
  default_language?: LanguageEnum
  /**
   * Subset of supported UI languages this workspace offers in its language switcher.
   */
  enabled_languages?: Array<LanguageEnum>
  readonly is_demo?: boolean
  readonly demo_cleanup_in_progress?: boolean
  readonly demo_expires_at?: string | null
}

/**
 * Create/update. ``custom_secret`` is write-only and never echoed back.
 *
 * ``managed_by`` / ``managed_ref`` tag a webhook another service owns (Squad: ``"squad"`` /
 * ``"agent:<uuid>"``). They are set on create; a later request may repeat them, not change them.
 */
export type PatchedOutboundWebhookWrite = {
  url?: string
  events?: Array<string>
  is_active?: boolean
  name?: string
  managed_by?: string
  managed_ref?: string
}

export type PatchedPasskeyRename = {
  name?: string
}

export type PatchedPublicAccessCredentialUpdate = {
  name?: string
  expires_at?: string | null
  apps?: Array<PublicAccessAppBindingWrite>
}

/**
 * Per-user sign-in policy: allowed methods, refresh-token TTL, trusted device TTL.
 */
export type PatchedSignInPolicy = {
  readonly id?: number
  allow_password_sign_in?: boolean
  allow_social_sign_in?: boolean
  refresh_token_lifetime_days?: number
  trusted_device_validity_days?: number
  readonly created_at?: string
  readonly updated_at?: string
}

export type PatchedTeamModify = {
  readonly id?: number
  readonly date_created?: string
  readonly date_updated?: string
  name?: string
  type?: TeamTypeEnum | NullEnum | null
  abbreviation?: string | null
  description?: string
  parent?: number | null
  organization?: number
  head_user?: number | null
}

export type PatchedUpdateAttachmentVisibility = {
  is_public?: boolean
}

export type PatchedUser = {
  readonly id?: number
  last_login?: string | null
  /**
   * Active
   *
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active?: boolean
  date_joined?: string
  first_name?: string
  last_name?: string
  /**
   * Email address
   */
  email?: string
  phone?: string | null
  position?: string | null
  sso_id?: string | null
  language?: LanguageEnum
  archived?: boolean
  photo?: number | null
  about_me?: string
  linkedin_url?: string | null
  birth_date?: string | null
}

export type PatchedUserEdit = {
  first_name?: string
  last_name?: string
  phone?: string | null
  photo?: number | null
  position?: string | null
  language?: LanguageEnum
  about_me?: string
  linkedin_url?: string | null
  settings?: {
    [key: string]: unknown
  } | null
}

/**
 * Replaces the roles a group grants **within one application**.
 *
 * The whole-group ``roles`` array is the workspace admin's view, where every application is on
 * screen at once. Inside an application only its own roles are, so a full replace there would
 * silently drop everything the admin cannot see.
 */
export type PatchedUserGroupApplicationRoles = {
  roles?: Array<number>
}

export type PatchedUserGroupModify = {
  readonly id?: number
  name?: string
  organization?: number
  applications?: Array<number>
  users?: Array<number>
}

/**
 * Supplies localized, role-named descriptions for OrganizationRole audit events.
 */
export type PatchedWorkspaceRole = {
  readonly id?: number
  readonly date_created?: string
  readonly date_updated?: string
  /**
   * Human-readable role name (e.g., 'Sales Manager')
   */
  name?: string
  codename?: string
  description?: string
  organization?: number
  /**
   * Admin flag: when True, role automatically has ALL permissions without explicit assignment for specified application (or if application is null, for organization-level permissions).
   */
  readonly is_admin?: boolean
  readonly is_system?: boolean
  readonly users_count?: number
  readonly permissions?: Array<WorkspacePermissionCatalog>
}

export type PermissionMigrationApply = {
  name: string
  operations: Array<PermissionMigrationOperation>
}

/**
 * Polymorphic operation entry dispatched by the ``type`` discriminator.
 *
 * Validation delegates to a per-type sub-serializer; the validated dict keeps the
 * ``type`` key so downstream dispatch in helpers reads cleanly.
 */
export type PermissionMigrationOperation = {
  type: PermissionMigrationOperationTypeEnum
}

/**
 * * `rename` - rename
 * * `merge` - merge
 */
export const PermissionMigrationOperationTypeEnum = {
  RENAME: 'rename',
  MERGE: 'merge'
} as const

/**
 * * `rename` - rename
 * * `merge` - merge
 */
export type PermissionMigrationOperationTypeEnum =
  (typeof PermissionMigrationOperationTypeEnum)[keyof typeof PermissionMigrationOperationTypeEnum]

export type Permissions = {
  readonly id: number
  /**
   * Permission code: 'action' or 'module.action' or 'app.module.action'
   */
  code: string
  label?: unknown
  description?: unknown
  is_externally_grantable?: boolean
}

export type PermissionsLocalized = {
  readonly id: number
  /**
   * Permission code: 'action' or 'module.action' or 'app.module.action'
   */
  code: string
  is_externally_grantable?: boolean
  readonly date_created: string
  readonly date_updated: string
  /**
   * Application repository (built-in app type) the permission belongs to.
   */
  readonly application_repository_id: number | null
  /**
   * Per-instance application (third-party custom apps) the permission belongs to.
   */
  readonly application_id: number | null
}

export type Photo = {
  url: string
  content_type?: string
}

/**
 * * `web` - web
 * * `mobile` - mobile
 */
export const PlatformEnum = { WEB: 'web', MOBILE: 'mobile' } as const

/**
 * * `web` - web
 * * `mobile` - mobile
 */
export type PlatformEnum = (typeof PlatformEnum)[keyof typeof PlatformEnum]

/**
 * * `deal_pipeline` - Run a deal pipeline
 * * `stop_losing_deals` - Stop losing deals
 * * `automate_routine` - Automate routine work
 * * `forecast_revenue` - Forecast revenue
 * * `customer_support` - Support customers
 * * `off_spreadsheets` - Move off spreadsheets
 * * `other` - Other
 */
export const PrimaryJobEnum = {
  DEAL_PIPELINE: 'deal_pipeline',
  STOP_LOSING_DEALS: 'stop_losing_deals',
  AUTOMATE_ROUTINE: 'automate_routine',
  FORECAST_REVENUE: 'forecast_revenue',
  CUSTOMER_SUPPORT: 'customer_support',
  OFF_SPREADSHEETS: 'off_spreadsheets',
  OTHER: 'other'
} as const

/**
 * * `deal_pipeline` - Run a deal pipeline
 * * `stop_losing_deals` - Stop losing deals
 * * `automate_routine` - Automate routine work
 * * `forecast_revenue` - Forecast revenue
 * * `customer_support` - Support customers
 * * `off_spreadsheets` - Move off spreadsheets
 * * `other` - Other
 */
export type PrimaryJobEnum =
  (typeof PrimaryJobEnum)[keyof typeof PrimaryJobEnum]

/**
 * * `oidc` - OpenID Connect
 * * `oauth2` - OAuth 2.0
 * * `saml2` - SAML 2.0
 */
export const ProtocolEnum = {
  OIDC: 'oidc',
  OAUTH2: 'oauth2',
  SAML2: 'saml2'
} as const

/**
 * * `oidc` - OpenID Connect
 * * `oauth2` - OAuth 2.0
 * * `saml2` - SAML 2.0
 */
export type ProtocolEnum = (typeof ProtocolEnum)[keyof typeof ProtocolEnum]

/**
 * * `google` - google
 * * `entraid` - entraid
 * * `apple` - apple
 */
export const ProviderEnum = {
  GOOGLE: 'google',
  ENTRAID: 'entraid',
  APPLE: 'apple'
} as const

/**
 * * `google` - google
 * * `entraid` - entraid
 * * `apple` - apple
 */
export type ProviderEnum = (typeof ProviderEnum)[keyof typeof ProviderEnum]

export type ProvisioningState = {
  job_id: number | null
  state: string
}

export type PublicAccessActor = {
  id: number
  email: string
}

export type PublicAccessAppBindingWrite = {
  app_code: string
  saas_app_token: string
  permission_codes: Array<string>
}

export type PublicAccessCredentialAppBindingRead = {
  app_code: string
  saas_app_token: string
  status: string
  permission_codes: Array<string>
}

export type PublicAccessCredentialCreate = {
  name: string
  expires_at?: string | null
  apps: Array<PublicAccessAppBindingWrite>
}

export type PublicAccessCredentialCreateResponse = {
  id: string
  name: string
  status?: PublicAccessCredentialStatusEnum
  expires_at?: string | null
  created_by: PublicAccessActor
  readonly created_at: string
  last_used_at?: string | null
  revoked_at?: string | null
  readonly apps: Array<PublicAccessCredentialAppBindingRead>
  readonly token: string
}

export type PublicAccessCredentialRead = {
  id: string
  name: string
  status?: PublicAccessCredentialStatusEnum
  expires_at?: string | null
  created_by: PublicAccessActor
  readonly created_at: string
  last_used_at?: string | null
  revoked_at?: string | null
  readonly apps: Array<PublicAccessCredentialAppBindingRead>
}

export type PublicAccessCredentialRevoke = {
  reason?: string | null
}

export type PublicAccessCredentialRevokeResponse = {
  id: string
  status: string
  revoked_at: string
  reason: string | null
}

export type PublicAccessCredentialRotate = {
  name?: string
  expires_at?: string | null
  copy_permissions?: boolean
  revoke_old_credential?: boolean
}

export type PublicAccessCredentialRotateResponse = {
  old_credential_id: string
  new_credential: PublicAccessCredentialCreateResponse
}

/**
 * * `active` - Active
 * * `revoked` - Revoked
 * * `expired` - Expired
 */
export const PublicAccessCredentialStatusEnum = {
  ACTIVE: 'active',
  REVOKED: 'revoked',
  EXPIRED: 'expired'
} as const

/**
 * * `active` - Active
 * * `revoked` - Revoked
 * * `expired` - Expired
 */
export type PublicAccessCredentialStatusEnum =
  (typeof PublicAccessCredentialStatusEnum)[keyof typeof PublicAccessCredentialStatusEnum]

export type PublicAccessSnapshot = {
  credential_id: string
  saas_app_token: string
  status: string
  creator_sso_id: string | null
  expires_at: string | null
  permission_codes: Array<string>
  synced_at: string
}

export type PublicAccessUsage = {
  app_code: string
  used_at: string
}

export type PublicNetworkingOrganization = {
  id: number
  name: string
  domain_name: string
  logo: ShortAttachment | null
}

export type PublicNetworkingOrganizationsRequest = {
  email: string
}

/**
 * * `login` - Login
 * * `change_password` - Change Password
 */
export const PurposeEnum = {
  LOGIN: 'login',
  CHANGE_PASSWORD: 'change_password'
} as const

/**
 * * `login` - Login
 * * `change_password` - Change Password
 */
export type PurposeEnum = (typeof PurposeEnum)[keyof typeof PurposeEnum]

export type QrLoginApprove = {
  session_id: string
  organization_id?: number | null
}

export type QrLoginCancel = {
  session_id: string
  web_token: string
}

export type QrLoginDeny = {
  session_id: string
}

export type QrLoginErrorResponse = {
  detail: string
  code: string
}

export type QrLoginScan = {
  session_id: string
  nonce: string
}

export type QrLoginScanResponse = {
  status: QrLoginScanResponseStatusEnum
  web_device: QrLoginWebDevice
  organization_name?: string | null
  expires_at: string
}

/**
 * * `scanned` - scanned
 */
export const QrLoginScanResponseStatusEnum = { SCANNED: 'scanned' } as const

/**
 * * `scanned` - scanned
 */
export type QrLoginScanResponseStatusEnum =
  (typeof QrLoginScanResponseStatusEnum)[keyof typeof QrLoginScanResponseStatusEnum]

export type QrLoginStart = {
  domain_name?: string
}

export type QrLoginStartResponse = {
  session_id: string
  web_token: string
  qr_payload: string
  expires_in: number
  poll_interval: number
  long_poll_timeout: number
}

export type QrLoginStatusResponse = {
  status: QrLoginStatusResponseStatusEnum
}

/**
 * * `approved` - approved
 * * `denied` - denied
 * * `canceled` - canceled
 * * `expired` - expired
 */
export const QrLoginStatusResponseStatusEnum = {
  APPROVED: 'approved',
  DENIED: 'denied',
  CANCELED: 'canceled',
  EXPIRED: 'expired'
} as const

/**
 * * `approved` - approved
 * * `denied` - denied
 * * `canceled` - canceled
 * * `expired` - expired
 */
export type QrLoginStatusResponseStatusEnum =
  (typeof QrLoginStatusResponseStatusEnum)[keyof typeof QrLoginStatusResponseStatusEnum]

export type QrLoginWait = {
  session_id: string
  web_token: string
}

export type QrLoginWaitApprovedResponse = {
  status: QrLoginWaitApprovedResponseStatusEnum
  access: string
  refresh: string
  organization_id?: number | null
}

/**
 * * `approved` - approved
 */
export const QrLoginWaitApprovedResponseStatusEnum = {
  APPROVED: 'approved'
} as const

/**
 * * `approved` - approved
 */
export type QrLoginWaitApprovedResponseStatusEnum =
  (typeof QrLoginWaitApprovedResponseStatusEnum)[keyof typeof QrLoginWaitApprovedResponseStatusEnum]

export type QrLoginWaitPendingResponse = {
  status: QrLoginWaitPendingResponseStatusEnum
  expires_in_remaining: number
}

/**
 * * `pending` - pending
 * * `scanned` - scanned
 */
export const QrLoginWaitPendingResponseStatusEnum = {
  PENDING: 'pending',
  SCANNED: 'scanned'
} as const

/**
 * * `pending` - pending
 * * `scanned` - scanned
 */
export type QrLoginWaitPendingResponseStatusEnum =
  (typeof QrLoginWaitPendingResponseStatusEnum)[keyof typeof QrLoginWaitPendingResponseStatusEnum]

export type QrLoginWaitResponse =
  | ({
      status: 'QrLoginWaitPendingResponse'
    } & QrLoginWaitPendingResponse)
  | ({
      status: 'QrLoginWaitApprovedResponse'
    } & QrLoginWaitApprovedResponse)
  | ({
      status: 'null'
    } & QrLoginStatusResponse)

export type QrLoginWebDevice = {
  device_name: string
  ip_address: string | null
  city: string | null
  country_code: string | null
  user_agent_summary: string
}

export type RpcMethodCreate = {
  readonly id: number
  readonly date_created: string
  readonly date_updated: string
  function: string
  priority?: number
  readonly service: number
}

export type RpcPipelineStep = {
  readonly id: number
  order: number
  readonly template_name: string
}

export type RpcPreset = {
  readonly id: number
  name: string
  codename: string
}

export type ReplaceOrganizationRole = {
  replacement_role: number
}

export type ReportCreate = {
  user_mail: string
  user_phone?: string | null
  subject: string
  message: string
}

export type ReportFull = {
  readonly id: number
  readonly date_created: string
  readonly date_updated: string
  user_mail: string
  user_phone?: string | null
  subject: string
  message: string
  status?: ReportFullStatusEnum
  user: number
}

/**
 * * `open` - open
 * * `closed` - closed
 */
export const ReportFullStatusEnum = { OPEN: 'open', CLOSED: 'closed' } as const

/**
 * * `open` - open
 * * `closed` - closed
 */
export type ReportFullStatusEnum =
  (typeof ReportFullStatusEnum)[keyof typeof ReportFullStatusEnum]

export type RequestedByUser = {
  readonly id: number
  readonly first_name: string
  readonly last_name: string
  readonly full_name: string
  /**
   * Email address
   */
  readonly email: string
  photo: ShortAttachment | null
}

export type ResendInvite = {
  /**
   * Invite URL prefix (e.g. https://{domain}.example.com/authentication/invitation/?token=). The freshly-issued invite token is appended to this value by the server.
   */
  invite_url: string
}

export type ResponseAuditLog = {
  count: number
  results: Array<DeprecatedAuditLogList>
}

/**
 * Serializer for password restore request.
 */
export type RestorePass = {
  username: string
  redirect_url: string
  service_token?: string
  mobile?: boolean
}

/**
 * Confirm password restore (with token).
 */
export type RestorePasswordConfirm = {
  token: string
  password: string
  confirm_password: string
  service_token?: string
}

/**
 * * `admin` - Admin
 * * `member` - Member
 */
export const RoleVariantEnum = { ADMIN: 'admin', MEMBER: 'member' } as const

/**
 * * `admin` - Admin
 * * `member` - Member
 */
export type RoleVariantEnum =
  (typeof RoleVariantEnum)[keyof typeof RoleVariantEnum]

export type S3Bucket = {
  name: string
}

export type S3Event = {
  bucket: S3Bucket
  object: S3Object
}

export type S3Object = {
  key: string
  size: number
  content_type: string
}

export type S3Record = {
  s3: S3Event
}

/**
 * * `idle` - Idle
 * * `pending` - Pending
 * * `success` - Success
 * * `failed` - Failed
 */
export const ScimSyncStatusEnum = {
  IDLE: 'idle',
  PENDING: 'pending',
  SUCCESS: 'success',
  FAILED: 'failed'
} as const

/**
 * * `idle` - Idle
 * * `pending` - Pending
 * * `success` - Success
 * * `failed` - Failed
 */
export type ScimSyncStatusEnum =
  (typeof ScimSyncStatusEnum)[keyof typeof ScimSyncStatusEnum]

/**
 * Serializer for listing services.
 */
export type Service = {
  readonly id: number
  name: string
  token: string
  secret_token: string
  logo?: string | null
  confirm_url?: string | null
  webhook_create?: string | null
  is_active?: boolean
  readonly created_at: string
  readonly updated_at: string
}

/**
 * * `crm` - Crm
 * * `hrm` - Hrm
 */
export const ServiceEnum = { CRM: 'crm', HRM: 'hrm' } as const

/**
 * * `crm` - Crm
 * * `hrm` - Hrm
 */
export type ServiceEnum = (typeof ServiceEnum)[keyof typeof ServiceEnum]

/**
 * User-facing view of an active sign-in session.
 *
 * One row per active refresh token (password/social) or ``oidc_refresh``
 * (OIDC third-party). ``is_current`` is stamped by the viewset. ``kind`` and
 * ``application`` let the UI distinguish first-party logins from OIDC
 * third-party sessions.
 */
export type Session = {
  readonly id: string
  readonly device_name: string | null
  readonly user_agent: string | null
  readonly ip_address: string | null
  readonly city: string | null
  readonly country_code: string | null
  readonly created_at: string
  readonly last_used_at: string | null
  readonly expires_at: string
  readonly is_current: boolean
  readonly trusted_device_id: number
  organization: SessionOrganization
  kind: KindEnum
  readonly application: {
    id: number
    name: string
  } | null
  auth_method: OrganizationAuthMethodEnum | NullEnum | null
}

/**
 * Minimal workspace shape embedded into a session row.
 */
export type SessionOrganization = {
  readonly id: number
  readonly name: string
  readonly domain_name: string
}

export type SessionRevokeResponse = {
  revoked: number
}

export type SettingsMapping = {
  readonly id: number
  readonly date_created: string
  readonly date_updated: string
  name: string
  key: string
  default_value?: string | null
  application_repository: number
}

export type SetupJob = {
  readonly id: number
  pipeline_step: RpcPipelineStep
  readonly date_created: string
  readonly date_updated: string
  status?: SetupJobStatusEnum
  job_type?: SetupJobTypeEnum
  organization?: number | null
  created_by?: number | null
  parent?: number | null
}

/**
 * * `pending` - Pending
 * * `in_process` - In Process
 * * `failed` - Failed
 * * `canceled` - Canceled
 * * `finished` - Finished
 */
export const SetupJobStatusEnum = {
  PENDING: 'pending',
  IN_PROCESS: 'in_process',
  FAILED: 'failed',
  CANCELED: 'canceled',
  FINISHED: 'finished'
} as const

/**
 * * `pending` - Pending
 * * `in_process` - In Process
 * * `failed` - Failed
 * * `canceled` - Canceled
 * * `finished` - Finished
 */
export type SetupJobStatusEnum =
  (typeof SetupJobStatusEnum)[keyof typeof SetupJobStatusEnum]

/**
 * * `build` - Build
 * * `rebuild` - Rebuild
 */
export const SetupJobTypeEnum = { BUILD: 'build', REBUILD: 'rebuild' } as const

/**
 * * `build` - Build
 * * `rebuild` - Rebuild
 */
export type SetupJobTypeEnum =
  (typeof SetupJobTypeEnum)[keyof typeof SetupJobTypeEnum]

/**
 * * `low` - low
 * * `medium` - medium
 * * `high` - high
 */
export const SeverityEnum = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high'
} as const

/**
 * * `low` - low
 * * `medium` - medium
 * * `high` - high
 */
export type SeverityEnum = (typeof SeverityEnum)[keyof typeof SeverityEnum]

/**
 * Base Attachment serializer to return the file URL based on its is_public status.
 */
export type ShortAttachment = {
  readonly id: number
  readonly title: string
  readonly file: string
  readonly size: number | null
  status: AttachmentStatusEnum
  type: AttachmentTypeEnum
}

/**
 * Per-user sign-in policy: allowed methods, refresh-token TTL, trusted device TTL.
 */
export type SignInPolicy = {
  readonly id: number
  allow_password_sign_in?: boolean
  allow_social_sign_in?: boolean
  refresh_token_lifetime_days?: number
  trusted_device_validity_days?: number
  readonly created_at: string
  readonly updated_at: string
}

/**
 * * `no_permission` - I do not have permission
 * * `not_needed` - Not needed
 * * `later` - Later
 * * `blocked` - Blocked
 * * `user_skipped` - Skipped
 */
export const SkipReasonEnum = {
  NO_PERMISSION: 'no_permission',
  NOT_NEEDED: 'not_needed',
  LATER: 'later',
  BLOCKED: 'blocked',
  USER_SKIPPED: 'user_skipped'
} as const

/**
 * * `no_permission` - I do not have permission
 * * `not_needed` - Not needed
 * * `later` - Later
 * * `blocked` - Blocked
 * * `user_skipped` - Skipped
 */
export type SkipReasonEnum =
  (typeof SkipReasonEnum)[keyof typeof SkipReasonEnum]

/**
 * Serializer for Apple connect/url (link to existing account).
 */
export type SocialAppleConnectStart = {
  domain_name?: string
  redirect_uri: string
  return_origin?: string
}

/**
 * Serializer for Apple Sign In OAuth start (org-scoped login).
 */
export type SocialAppleStart = {
  domain_name: string
  redirect_uri: string
  return_origin?: string
}

/**
 * Serializer for Entra ID connect/url (link to existing account).
 */
export type SocialEntraIdConnectStart = {
  domain_name?: string
  redirect_uri: string
  return_origin?: string
}

/**
 * Serializer for Entra ID OAuth start (org-scoped login).
 */
export type SocialEntraIdStart = {
  domain_name: string
  redirect_uri: string
  return_origin?: string
}

export type SocialExchange = {
  provider: ProviderEnum
  code: string
  state: string
  redirect_uri: string
  user?: string | null
}

/**
 * Serializer for connect/url with optional return_origin (same rules as org start).
 */
export type SocialGoogleConnectStart = {
  redirect_uri: string
  return_origin?: string
}

export type SocialGoogleOrgStart = {
  redirect_uri: string
  return_origin?: string
  domain_name: string
}

export type SocialGoogleStart = {
  redirect_uri: string
  return_origin?: string
}

/**
 * Nested serializer for a linked social provider.
 */
export type SocialProviderInfo = {
  readonly provider: string
  readonly email: string
}

/**
 * * `todo` - todo
 * * `done` - done
 * * `skipped` - skipped
 * * `failed` - failed
 * * `reset` - reset
 */
export const StateEnum = {
  TODO: 'todo',
  DONE: 'done',
  SKIPPED: 'skipped',
  FAILED: 'failed',
  RESET: 'reset'
} as const

/**
 * * `todo` - todo
 * * `done` - done
 * * `skipped` - skipped
 * * `failed` - failed
 * * `reset` - reset
 */
export type StateEnum = (typeof StateEnum)[keyof typeof StateEnum]

export type SupportTicket = {
  id: number
  title: string
  description: string
  status: string
  status_order_index: number
  progress: number
  completed: boolean
  completed_at: string | null
  created_at: string
  updated_at: string
  start_date: string | null
  due_date: string | null
  priority: number | null
  has_unseen_update: boolean
  attachments: Array<SupportTicketAttachment>
}

export type SupportTicketAttachment = {
  id: string
  name: string
  url: string
  thumbnail_url: string | null
  mime_type: string | null
  extension: string | null
  size: number | null
  is_image: boolean
}

export type SupportTicketComment = {
  id: string
  text: string
  created_at: string | null
  source: SupportTicketCommentSourceEnum
  images: Array<SupportTicketCommentImage>
}

export type SupportTicketCommentCreate = {
  text: string
}

export type SupportTicketCommentImage = {
  url: string
  name: string | null
  width: number | null
  height: number | null
}

/**
 * * `platform` - platform
 * * `clickup` - clickup
 */
export const SupportTicketCommentSourceEnum = {
  PLATFORM: 'platform',
  CLICKUP: 'clickup'
} as const

/**
 * * `platform` - platform
 * * `clickup` - clickup
 */
export type SupportTicketCommentSourceEnum =
  (typeof SupportTicketCommentSourceEnum)[keyof typeof SupportTicketCommentSourceEnum]

export type SupportTicketCreate = {
  title: string
  description?: string
  priority?: number | null
  source_url?: string
  attachment_ids?: Array<number>
}

export type SupportTicketCreated = {
  id: number
  title: string
  description: string
  status: string
  status_order_index: number
  progress: number
  completed: boolean
  completed_at: string | null
  created_at: string
  updated_at: string
  start_date: string | null
  due_date: string | null
  priority: number | null
  has_unseen_update: boolean
  attachments: Array<SupportTicketAttachment>
  warnings: Array<string>
}

export type SupportTicketUnseenCount = {
  count: number
}

export type SupportTicketUpload = {
  id: number
  upload_url: string
  content_type: string
}

export type SupportTicketUploadCreate = {
  title: string
  /**
   * MIME type the client will send in the PUT. Defaults to a guess from `title`.
   */
  content_type?: string
}

export type SyncActiveDirectoryResponse = {
  users_synced: number
}

export type SyncPermissions = {
  permissions: Array<Permissions>
  default_roles?: Array<DefaultRole>
}

export type SyncPermissionsResponse = {
  created: number
  updated: number
  deleted: number
  roles_applied: number
  applications_processed: number
}

export type SyncRpcMethods = {
  rpc_methods: Array<RpcMethodCreate>
}

export type SyncRpcMethodsResponse = {
  created: number
  updated: number
  deleted: number
}

export type SyncRpcTemplatePresetBinding = {
  preset_codename: string
  variant: string
}

export type SyncRpcTemplates = {
  version?: number
  service_type?: string
  source?: {
    [key: string]: unknown
  }
  variants?: {
    [key: string]: unknown
  }
  preset_bindings: Array<SyncRpcTemplatePresetBinding>
}

export type SyncRpcTemplatesResponse = {
  skipped: boolean
  reason: string
  manifest_hash: string
  created_templates: number
  updated_templates: number
  created_steps: number
  updated_steps: number
  deleted_templates: number
  deleted_steps: number
  skipped_missing_methods: number
  skipped_missing_pipelines: number
}

export type TeamHeadUser = {
  readonly id: number
  /**
   * Email address
   */
  email: string
  first_name: string
  last_name: string
}

export type TeamList = {
  readonly id: number
  readonly has_children: boolean
  readonly top_users: Array<UserVeryShort> | null
  readonly users_count: number
  head_user_detail: TeamHeadUser | null
  readonly date_created: string
  readonly date_updated: string
  name: string
  type?: TeamTypeEnum | NullEnum | null
  abbreviation?: string | null
  description?: string
  parent?: number | null
  organization: number
  head_user?: number | null
}

export type TeamMember = {
  id: number
}

export type TeamModify = {
  readonly id: number
  readonly date_created: string
  readonly date_updated: string
  name: string
  type?: TeamTypeEnum | NullEnum | null
  abbreviation?: string | null
  description?: string
  parent?: number | null
  organization: number
  head_user?: number | null
}

export type TeamShort = {
  readonly id: number
  name: string
  parent?: number | null
}

/**
 * * `branch` - Branch
 * * `department` - Department
 * * `section` - Section
 */
export const TeamTypeEnum = {
  BRANCH: 'branch',
  DEPARTMENT: 'department',
  SECTION: 'section'
} as const

/**
 * * `branch` - Branch
 * * `department` - Department
 * * `section` - Section
 */
export type TeamTypeEnum = (typeof TeamTypeEnum)[keyof typeof TeamTypeEnum]

export type TeamUsers = {
  readonly id: number
  readonly date_created: string
  readonly date_updated: string
  user: number
  team: number
}

export type TeamUsersDetail = {
  readonly id: number
  team: TeamShort
  /**
   * Get the organization access ID for this team user
   */
  readonly organization_access_id: number | null
  readonly date_created: string
  readonly date_updated: string
}

export type ThirdPartyApplicationCreate = {
  organization: number
  application_name: string
  application_description?: string | null
  application_domain?: string | null
  application_url?: string | null
}

/**
 * Response serializer for token pair.
 */
export type TokenPairWithStatus = {
  access: string
  refresh: string
  status: TokenPairWithStatusStatusEnum
  organization_id?: number | null
}

/**
 * * `authenticated` - authenticated
 */
export const TokenPairWithStatusStatusEnum = {
  AUTHENTICATED: 'authenticated'
} as const

/**
 * * `authenticated` - authenticated
 */
export type TokenPairWithStatusStatusEnum =
  (typeof TokenPairWithStatusStatusEnum)[keyof typeof TokenPairWithStatusStatusEnum]

/**
 * Verify a JWT token with service context.
 */
export type TokenServiceVerify = {
  token: string
  service_token: string
}

/**
 * Verify a JWT token and return user info.
 */
export type TokenVerify = {
  token: string
}

export type TrustedDevice = {
  readonly id: number
  readonly created_at: string
  readonly last_used: string
  user_agent?: string | null
  ip_address?: string | null
  device_name?: string | null
  expires_at: string
}

export type User = {
  readonly id: number
  last_login?: string | null
  /**
   * Active
   *
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active?: boolean
  date_joined?: string
  first_name: string
  last_name: string
  /**
   * Email address
   */
  email: string
  phone?: string | null
  position?: string | null
  sso_id?: string | null
  language?: LanguageEnum
  archived?: boolean
  photo?: number | null
  about_me?: string
  linkedin_url?: string | null
  birth_date?: string | null
}

export type UserArchiveJob = {
  readonly id: number
  organization_id: number
  source_user_id: number
  target_user_id: number
  requested_by_id: number | null
  status: UserArchiveJobStatusEnum
  readonly errors: Array<{
    code: string
    message: string
    failed_services?: Array<string>
  }>
  readonly date_created: string
  readonly date_updated: string
}

export type UserArchiveJobDetailResponse = {
  readonly id: number
  organization_id: number
  source_user_id: number
  target_user_id: number
  requested_by_id: number | null
  status: UserArchiveJobStatusEnum
  readonly errors: Array<{
    code: string
    message: string
    failed_services?: Array<string>
  }>
  readonly date_created: string
  readonly date_updated: string
  latest_dependency_summary?: ArchiveDependencyResponse
}

/**
 * * `pending` - Pending
 * * `processing` - Processing
 * * `succeeded` - Succeeded
 * * `failed` - Failed
 */
export const UserArchiveJobStatusEnum = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  SUCCEEDED: 'succeeded',
  FAILED: 'failed'
} as const

/**
 * * `pending` - Pending
 * * `processing` - Processing
 * * `succeeded` - Succeeded
 * * `failed` - Failed
 */
export type UserArchiveJobStatusEnum =
  (typeof UserArchiveJobStatusEnum)[keyof typeof UserArchiveJobStatusEnum]

export type UserCreate = {
  /**
   * Email address
   */
  email: string
  first_name: string
  last_name: string
  invite_url?: string
}

export type UserDetail = {
  readonly id: number
  /**
   * Email address
   */
  email: string
  first_name: string
  last_name: string
  phone?: string | null
  photo: Attachment
  last_login?: string | null
  sso_id?: string | null
  /**
   * Active
   *
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active?: boolean
  date_joined?: string
  language?: LanguageEnum
  archived?: boolean
}

export type UserDomainLogin = {
  domain_name: string
}

export type UserEdit = {
  first_name: string
  last_name: string
  phone?: string | null
  photo?: number | null
  position?: string | null
  language?: LanguageEnum
  about_me?: string
  linkedin_url?: string | null
  settings?: {
    [key: string]: unknown
  } | null
}

export type UserGroup = {
  readonly id: number
  readonly name: string
  readonly organization: number
  readonly applications: Array<number>
  readonly users: Array<UserVeryShort>
  readonly roles: Array<UserGroupRoleShort>
  readonly date_created: string
  readonly date_updated: string
}

export type UserGroupModify = {
  readonly id: number
  name: string
  organization: number
  applications?: Array<number>
  users?: Array<number>
}

export type UserGroupRoleShort = {
  readonly id: number
  /**
   * Human-readable role name (e.g., 'Sales Manager')
   */
  name: string
  /**
   * Machine-readable role code name (e.g., 'sales_manager')
   */
  codename?: string | null
  application?: number | null
  /**
   * Admin flag: when True, role automatically has ALL permissions without explicit assignment for specified application (or if application is null, for organization-level permissions).
   */
  is_admin?: boolean
}

export type UserGroupShort = {
  readonly id: number
  name: string
}

export type UserInvite = {
  invite_url: string
  organization_id: number
  emails: Array<string>
  team?: number
  /**
   * Head user who will manage the invited users
   */
  head_user?: number | null
  /**
   * List of applications with roles to assign
   */
  applications?: Array<ApplicationInvite>
}

export type UserInviteAccept = {
  invite_token: string
  first_name?: string
  last_name?: string
  password?: string
}

export type UserList = {
  readonly id: number
  last_login?: string | null
  /**
   * Active
   *
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active?: boolean
  date_joined?: string
  first_name: string
  last_name: string
  /**
   * Email address
   */
  email: string
  phone?: string | null
  position?: string | null
  sso_id?: string | null
  language?: LanguageEnum
  archived?: boolean
  photo: ShortAttachment | null
  about_me?: string
  linkedin_url?: string | null
  birth_date?: string | null
}

export type UserLog = {
  id?: number | null
  sso_id?: string | null
  first_name?: string | null
  last_name?: string | null
  email?: string | null
  teams?: Array<OrganizationTeamShort>
}

export type UserOrganizationAccess = {
  organization: OrganizationVeryShort
  readonly role: string
  blocked?: boolean
}

export type UserRegisterConfirm = {
  token: string
  password: string
  confirm_password: string
}

export type UserRegisterStart = {
  /**
   * Email address
   */
  email: string
  phone?: string
  first_name: string
  last_name: string
  photo?: number | null
  language?: LanguageEnum
}

export type UserRetrieve = {
  readonly id: number
  last_login?: string | null
  /**
   * Active
   *
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active?: boolean
  date_joined?: string
  first_name: string
  last_name: string
  /**
   * Email address
   */
  email: string
  phone?: string | null
  position?: string | null
  sso_id?: string | null
  language?: LanguageEnum
  archived?: boolean
  photo: ShortAttachment | null
  about_me?: string
  linkedin_url?: string | null
  birth_date?: string | null
}

export type UserShort = {
  readonly id: number
  /**
   * Email address
   */
  email: string
  first_name: string
  last_name: string
  phone?: string | null
  photo: ShortAttachment
  last_login?: string | null
  position?: string | null
  sso_id?: string | null
  readonly teams: Array<{
    id?: number
    name?: string
    parent?: number
  }>
  language?: LanguageEnum
  application_role: string | null
  archived?: boolean
}

export type UserShortWithApplications = {
  readonly id: number
  /**
   * Email address
   */
  email: string
  first_name: string
  last_name: string
  phone?: string | null
  photo: ShortAttachment
  last_login?: string | null
  position?: string | null
  sso_id?: string | null
  readonly teams: Array<{
    id?: number
    name?: string
    parent?: number
  }>
  language?: LanguageEnum
  application_role: string | null
  archived?: boolean
  readonly applications: Array<Application>
}

export type UserShortWithSettings = {
  readonly id: number
  /**
   * Email address
   */
  email: string
  first_name: string
  last_name: string
  phone?: string | null
  photo: ShortAttachment
  last_login?: string | null
  position?: string | null
  sso_id?: string | null
  readonly teams: Array<{
    id?: number
    name?: string
    parent?: number
  }>
  language?: LanguageEnum
  application_role: string | null
  archived?: boolean
}

export type UserShortWithoutLogin = {
  readonly id: number
  /**
   * Email address
   */
  email: string
  first_name: string
  last_name: string
  phone?: string | null
  photo: ShortAttachment
  position?: string | null
  sso_id?: string | null
  readonly teams: Array<{
    id?: number
    name?: string
    parent?: number
  }>
  language?: LanguageEnum
  application_role: string | null
  archived?: boolean
}

export type UserVeryShort = {
  readonly id: number
  sso_id?: string | null
  /**
   * Email address
   */
  email: string
  first_name: string
  last_name: string
  photo: ShortAttachment
}

export type WebhookData = {
  EventName: string
  Key: string
  Records: Array<S3Record>
}

export type WorkspacePermissionCatalog = {
  readonly id: number
  code: CodeEnum
  category: CategoryEnum
  readonly label: {
    [key: string]: unknown
  } | null
  readonly description: {
    [key: string]: unknown
  } | null
}

/**
 * Supplies localized, role-named descriptions for OrganizationRole audit events.
 */
export type WorkspaceRole = {
  readonly id: number
  readonly date_created: string
  readonly date_updated: string
  /**
   * Human-readable role name (e.g., 'Sales Manager')
   */
  name: string
  codename: string
  description?: string
  organization: number
  /**
   * Admin flag: when True, role automatically has ALL permissions without explicit assignment for specified application (or if application is null, for organization-level permissions).
   */
  readonly is_admin: boolean
  readonly is_system: boolean
  readonly users_count: number
  readonly permissions: Array<WorkspacePermissionCatalog>
}

export type AccountAccessWritable = {
  role: AccountAccessRoleEnum
}

/**
 * Custom token obtain serializer with optional service_token.
 */
export type AccountUiTokenObtainWritable = {
  service_token?: string
  username: string
  password: string
}

export type AppCredentialCreateResponseWritable = {
  id: string
  name: string
  status?: PublicAccessCredentialStatusEnum
  expires_at?: string | null
  last_used_at?: string | null
  revoked_at?: string | null
}

export type AppCredentialReadWritable = {
  id: string
  name: string
  status?: PublicAccessCredentialStatusEnum
  expires_at?: string | null
  last_used_at?: string | null
  revoked_at?: string | null
}

export type AppCredentialRotateResponseWritable = {
  old_credential_id: string
  new_credential: AppCredentialCreateResponseWritable
}

export type AppRoleWritable = {
  /**
   * Human-readable role name (e.g., 'Sales Manager')
   */
  name: string
  /**
   * Machine-readable role code name (e.g., 'sales_manager')
   */
  codename?: string | null
  description?: string
  /**
   * Admin flag: when True, role automatically has ALL permissions without explicit assignment for specified application (or if application is null, for organization-level permissions).
   */
  is_admin?: boolean
}

export type AppUserGroupWritable = {
  name: string
}

export type ApplicationWritable = {
  name: string
  token: string
  is_public?: boolean
  instance_type: ApplicationRepositoryShortWritable
  /**
   * User-facing URL where the app is launched (third-party apps).
   */
  app_url?: string | null
  description?: string | null
  /**
   * @deprecated
   */
  roles?: Array<string>
}

export type ApplicationAccessListWritable = {
  user: UserVeryShortWritable
  application: ApplicationShortWritable
  /**
   * @deprecated
   */
  role?: string | null
}

export type ApplicationDetailWritable = {
  name: string
  token: string
  is_public?: boolean
  instance_type: ApplicationRepositoryShortWritable
  /**
   * User-facing URL where the app is launched (third-party apps).
   */
  app_url?: string | null
  description?: string | null
  /**
   * @deprecated
   */
  roles?: Array<string>
}

/**
 * Create/update permissions scoped to a specific Application (third-party custom apps).
 */
export type ApplicationPermissionWritable = {
  /**
   * Permission code: 'action' or 'module.action' or 'app.module.action'
   */
  code: string
  label?: unknown
  description?: unknown
  application: number
}

export type ApplicationRepositoryWritable = {
  name: string
  type: string
  is_public?: boolean
  is_multiple?: boolean
  endpoint_url?: string | null
  description?: string | null
}

export type ApplicationRepositoryDetailWritable = {
  name: string
  type: string
  is_public?: boolean
  is_multiple?: boolean
  endpoint_url?: string | null
  description?: string | null
  settings_mapping: Array<SettingsMappingWritable>
}

export type ApplicationRepositoryShortWritable = {
  name: string
  description?: string | null
  type: string
}

export type ApplicationScimConfigReadWritable = {
  inbound_enabled?: boolean
  inbound_token_hint?: string | null
  inbound_token_rotated_at?: string | null
  outbound_enabled?: boolean
  outbound_base_url?: string | null
  last_sync_at?: string | null
  last_sync_status?: ScimSyncStatusEnum
  last_sync_error?: string | null
  last_backfill_at?: string | null
  last_backfill_status?: ScimSyncStatusEnum
  last_backfill_error?: string | null
}

export type ApplicationScimRotateTokenWritable = {
  inbound_enabled?: boolean
  inbound_token_hint?: string | null
  inbound_token_rotated_at?: string | null
  outbound_enabled?: boolean
  outbound_base_url?: string | null
  last_sync_at?: string | null
  last_sync_status?: ScimSyncStatusEnum
  last_sync_error?: string | null
  last_backfill_at?: string | null
  last_backfill_status?: ScimSyncStatusEnum
  last_backfill_error?: string | null
}

export type ApplicationShortWritable = {
  name: string
}

export type ApplicationUpdateWritable = {
  name?: string
  description?: string
  domain?: string | null
  app_url?: string | null
  /**
   * Attachment id of the uploaded launcher icon. Send null to remove and fall back to the default.
   */
  icon?: number | null
  /**
   * Role every member of the organization gets this app with. Must be a role of this app. Send null to stop auto-provisioning (already granted access is kept).
   */
  auto_provision_role?: number | null
}

export type ApplicationUserDetailsWritable = {
  /**
   * @deprecated
   */
  role: string | null
  user: UserShortWithoutLoginWritable
  organization: OrganizationShortWritable | null
}

export type ApplicationValidateWritable = {
  type: string
  domain_name: string
  organization: OrganizationTinyWritable
}

/**
 * Base Attachment serializer to return the file URL based on its is_public status.
 */
export type AttachmentWritable = {
  title: string
  is_public?: boolean
}

/**
 * Full document serializer for retrieve responses.
 */
export type AuditLogDetailWritable = {
  timestamp: string
  organization_id: number | null
  application_id: number | null
  application_name: string | null
  service: string
  category: string
  action: string
  severity?: SeverityEnum | NullEnum | null
  event_type: string | null
  channel: string | null
  client_ip: string | null
  entity_type: string
  entity_id: string
  /**
   * Actor user that caused the audit event.
   */
  actor: AuditLogActor | null
  /**
   * What kind of principal caused the event: a person, an agent, an API key, or the platform.
   *
   * * `user` - user
   * * `agent` - agent
   * * `api_key` - api_key
   * * `system` - system
   */
  actor_type?: ActorTypeEnum | NullEnum | null
  /**
   * Localized descriptions keyed by language code.
   */
  descriptions?: AuditLogDescriptions | null
  url: string | null
  method: string | null
}

/**
 * Lightweight serializer used for list responses -- no ``changes`` payload.
 */
export type AuditLogListWritable = {
  timestamp: string
  organization_id: number | null
  application_id: number | null
  application_name: string | null
  service: string
  category: string
  action: string
  severity?: SeverityEnum | NullEnum | null
  event_type: string | null
  channel: string | null
  client_ip: string | null
  entity_type: string
  entity_id: string
  /**
   * Actor user that caused the audit event.
   */
  actor: AuditLogActor | null
  /**
   * What kind of principal caused the event: a person, an agent, an API key, or the platform.
   *
   * * `user` - user
   * * `agent` - agent
   * * `api_key` - api_key
   * * `system` - system
   */
  actor_type?: ActorTypeEnum | NullEnum | null
}

export type AuthorizationV2oAuthClientReadWritable = {
  client_id?: string
  name?: string
  client_type: ClientTypeEnum
  allow_client_credentials?: boolean
  is_revoked?: boolean
  algorithm?: AlgorithmEnum | BlankEnum
  pkce_required?: boolean
}

export type CreateAvatarUploadUrlWritable = {
  title?: string
  is_public?: boolean
  /**
   * MIME type the client will send in the PUT. Defaults to a guess from `title`.
   */
  content_type?: string
}

export type CreateUploadUrlWritable = {
  title?: string
  type?: AttachmentTypeEnum
  is_public?: boolean
  organization_id?: number | null
  /**
   * MIME type the client will send in the PUT. Defaults to a guess from `title`.
   */
  content_type?: string
}

export type CurrentAccountUserWritable = {
  user: UserShortWithSettingsWritable
  account_access: Array<AccountAccessWritable>
  organizations: Array<OrganizationLongWritable>
}

export type CurrentApplicationWritable = {
  application_role?: string
  type?: string
  name?: string
  token?: string
}

export type CurrentOrganizationUserWritable = {
  id: number
  user: UserShortWithSettingsWritable
  role: string
  organization: OrganizationShortWritable
  available_organizations: Array<OrganizationShortWritable>
  pinned_applications: Array<ApplicationWritable> | null
  public_applications: Array<ApplicationWritable> | null
  current_application: CurrentApplicationWritable | null
}

/**
 * Custom refresh serializer with user validation.
 */
export type CustomTokenRefreshWritable = {
  refresh: string
  service_token?: string
}

export type DeprecatedAuditLogListWritable = {
  created_at: string
  organization_id?: number | null
  application?: DeprecatedAuditApplication | null
  user?: UserLogWritable | null
  team?: OrganizationTeamShortWritable | null
  action_type: string
}

/**
 * The answer the workspace screen branches on: every matching workspace.
 */
export type DomainMatchWritable = {
  matched: boolean
  policy: string
  organizations: Array<DomainMatchEntryWritable>
}

/**
 * One matching workspace plus the caller's own request against it (null if none).
 */
export type DomainMatchEntryWritable = {
  id: number
  name: string
  request: OrganizationJoinRequestWritable | null
}

/**
 * Custom token obtain serializer with optional service_token.
 */
export type DomainTokenObtainWritable = {
  service_token?: string
  domain_name?: string
  username: string
  password: string
}

export type HeadUserWritable = {
  id: number
  first_name: string
  last_name: string
  email: string
}

export type IndustryWritable = {
  name: string
}

export type IntegrationApplicationWritable = {
  name: string
  is_public?: boolean
  type: string
  token: string
}

export type IntegrationApplicationDetailWritable = {
  name: string
  is_public?: boolean
  instance_type: number
  token: string
  roles?: Array<string>
  /**
   * This tenant's own API rate limit, used by the application's service in place of its default. In CRM: requests per user and IP, per hour for reads and per day for creates. Empty keeps the default. A change is pushed to the service right away (tenant.sync).
   */
  api_rate_limit?: number | null
}

export type IntegrationApplicationTypeWritable = {
  name: string
  type: string
}

export type InternalMfaConfigWritable = {
  enabled?: boolean
  email_enabled?: boolean
  preferred_method?: OtpCodeChannelEnum
}

export type InternalMfaLockoutWritable = {
  lockout_type: LockoutTypeEnum
  locked_until: string
  reason: string
}

export type MfaConfigWritable = {
  enabled?: boolean
  email_enabled?: boolean
  preferred_method?: OtpCodeChannelEnum
}

export type McpConnectionWritable = {
  [key: string]: unknown
}

export type McpConnectionUserWritable = {
  id: number
  email: string
  first_name: string
  last_name: string
}

export type McpPersonalTokenCreatedWritable = {
  /**
   * The PAT (an mcp_access JWT). Returned only once.
   */
  token: string
  connection: McpConnectionWritable
}

export type MobileAppBuildWritable = {
  app: AppEnum
  /**
   * The version people see, e.g. "1.2.0".
   */
  version_name: string
  /**
   * Android versionCode. The highest active one is the latest, as a phone would judge it.
   */
  version_code: number
  release_notes?: string
}

export type NestedWritable = {
  name: string
  billing_first_name?: string | null
  billing_registration_number?: string | null
  billing_last_name?: string | null
  billing_phone?: string | null
  billing_email?: string | null
  billing_address1?: string | null
  billing_address2?: string | null
  billing_city?: string | null
  billing_zip?: string | null
  billing_industry?: number | null
  billing_country?: number | null
}

export type NotificationServiceUserWritable = {
  uuid: string
  organizations_access: Array<UserOrganizationAccessWritable>
}

/**
 * Read shape of a journey. Requires `manifest` in the serializer context.
 */
export type OnboardingJourneyWritable = {
  [key: string]: unknown
}

/**
 * Industry is write-once — the workspace was seeded from it (BR-12).
 */
export type OnboardingProfileWritable = {
  industry: IndustryEnum
  company_size: CompanySizeEnum
  primary_job?: PrimaryJobEnum | BlankEnum
  estimated_users?: number | null
  source?: AttributionSourceEnum | BlankEnum
  locale?: string
}

/**
 * Industry is write-once — the workspace was seeded from it (BR-12).
 */
export type OnboardingProfileResponseWritable = {
  industry: IndustryEnum
  company_size: CompanySizeEnum
  primary_job?: PrimaryJobEnum | BlankEnum
  estimated_users?: number | null
  source?: AttributionSourceEnum | BlankEnum
  locale?: string
}

export type OrganizationWritable = {
  name: string
  domain_name: string
  allow_ad_sync?: boolean
  google_client_id?: string | null
  enforce_sso?: boolean
  /**
   * Workspace MFA policy: when set, every member of this organization must complete multi-factor authentication at login (composed with each member's own enrollment and any per-member override).
   */
  require_mfa?: boolean
  allow_self_service_join?: boolean
  auto_provision_users?: boolean
  /**
   * Workspace default UI language; new or no-preference members start in this language.
   *
   * * `en` - En
   * * `ru` - Ru
   * * `ro` - Ro
   */
  default_language?: LanguageEnum
  /**
   * Subset of supported UI languages this workspace offers in its language switcher.
   */
  enabled_languages?: Array<LanguageEnum>
  account?: number | null
}

export type OrganizationAccessWritable = {
  job_title?: string | null
  /**
   * Head user who manages this user in the organization
   */
  head_user?: number | null
}

export type OrganizationAccessUserWritable = {
  id: number
  email: string
  first_name: string
  last_name: string
  language: string
}

export type OrganizationAuthMethodWritable = {
  method: OrganizationAuthMethodEnum
  enabled?: boolean
  client_id?: string | null
  tenant_id?: string | null
  key_id?: string | null
}

export type OrganizationAuthMethodUpdateWritable = {
  method: OrganizationAuthMethodEnum
  enabled: boolean
  client_id?: string | null
  client_secret?: string | null
  tenant_id?: string | null
  key_id?: string | null
}

export type OrganizationAuthMethodsResponseWritable = {
  auth_methods: Array<OrganizationAuthMethodWritable>
  enforce_sso: boolean
  require_mfa: boolean
  allow_self_service_join: boolean
  auto_provision_users: boolean
}

export type OrganizationCreateWritable = {
  photo?: number | null
  name: string
  domain_name: string
  allow_ad_sync?: boolean
  google_client_id?: string | null
  enforce_sso?: boolean
  /**
   * Workspace MFA policy: when set, every member of this organization must complete multi-factor authentication at login (composed with each member's own enrollment and any per-member override).
   */
  require_mfa?: boolean
  allow_self_service_join?: boolean
  auto_provision_users?: boolean
  /**
   * Workspace default UI language; new or no-preference members start in this language.
   *
   * * `en` - En
   * * `ru` - Ru
   * * `ro` - Ro
   */
  default_language?: LanguageEnum
  /**
   * Subset of supported UI languages this workspace offers in its language switcher.
   */
  enabled_languages?: Array<LanguageEnum>
  is_demo?: boolean
  account?: number | null
}

/**
 * Read serializer — secrets always masked.
 */
export type OrganizationCustomAuthProviderWritable = {
  name: string
  slug: string
  protocol: ProtocolEnum
  enabled?: boolean
  logo?: string
  client_id?: string | null
  issuer_url?: string | null
  authorize_url?: string | null
  token_url?: string | null
  userinfo_url?: string | null
  jwks_url?: string | null
  scope?: string
  saml_entity_id?: string | null
  saml_sso_url?: string | null
  saml_slo_url?: string | null
  saml_x509_cert?: string | null
  email_claim?: string
  first_name_claim?: string
  last_name_claim?: string
}

/**
 * Public/member-visible payload — no secrets, no internal URLs.
 */
export type OrganizationCustomAuthProviderPublicWritable = {
  name: string
  slug: string
  protocol: ProtocolEnum
  enabled?: boolean
  logo?: string
}

/**
 * Write serializer — handles MASKED_CLIENT_SECRET passthrough and per-protocol validation.
 */
export type OrganizationCustomAuthProviderWriteWritable = {
  name: string
  slug: string
  protocol: ProtocolEnum
  enabled?: boolean
  logo?: string
  client_id?: string | null
  client_secret?: string | null
  issuer_url?: string | null
  authorize_url?: string | null
  token_url?: string | null
  userinfo_url?: string | null
  jwks_url?: string | null
  scope?: string
  saml_entity_id?: string | null
  saml_sso_url?: string | null
  saml_slo_url?: string | null
  saml_x509_cert?: string | null
  email_claim?: string
  first_name_claim?: string
  last_name_claim?: string
}

export type OrganizationJoinRequestWritable = {
  [key: string]: unknown
}

export type OrganizationLongWritable = {
  name: string
  domain_name: string
  is_demo?: boolean
  demo_expires_at?: string | null
  demo_cleanup_in_progress?: boolean
  /**
   * Workspace default UI language; new or no-preference members start in this language.
   *
   * * `en` - En
   * * `ru` - Ru
   * * `ro` - Ro
   */
  default_language?: LanguageEnum
  /**
   * Subset of supported UI languages this workspace offers in its language switcher.
   */
  enabled_languages?: Array<LanguageEnum>
}

export type OrganizationOrganigramaWritable = {
  role: OrganizationRole
  user: OrganizationOrganigramaUserWritable
  job_title?: string | null
}

export type OrganizationOrganigramaUserWritable = {
  /**
   * Email address
   */
  email: string
  first_name: string
  last_name: string
  phone?: string | null
  last_login?: string | null
  position?: string | null
  sso_id?: string | null
  language?: LanguageEnum
  archived?: boolean
}

/**
 * Supplies localized, role-named descriptions for OrganizationRole audit events.
 */
export type OrganizationRoleEditWritable = {
  /**
   * Human-readable role name (e.g., 'Sales Manager')
   */
  name: string
  /**
   * Machine-readable role code name (e.g., 'sales_manager')
   */
  codename?: string | null
  description?: string
  permissions_ids?: Array<number>
}

/**
 * Supplies localized, role-named descriptions for OrganizationRole audit events.
 */
export type OrganizationRoleFullWritable = {
  /**
   * Human-readable role name (e.g., 'Sales Manager')
   */
  name: string
  /**
   * Machine-readable role code name (e.g., 'sales_manager')
   */
  codename?: string | null
  description?: string
  organization: number
  permissions_ids?: Array<number>
  application?: number
}

/**
 * Supplies localized, role-named descriptions for OrganizationRole audit events.
 */
export type OrganizationRoleRetrieveWritable = {
  /**
   * Human-readable role name (e.g., 'Sales Manager')
   */
  name: string
  /**
   * Machine-readable role code name (e.g., 'sales_manager')
   */
  codename?: string | null
  description?: string
  organization: number
  permissions_ids?: Array<number>
  permissions: Array<PermissionsLocalizedWritable>
  application?: number
}

export type OrganizationRoleShortWritable = {
  /**
   * Human-readable role name (e.g., 'Sales Manager')
   */
  name: string
  description?: string
}

/**
 * Read-only: who holds this role, and whether the grant is personal or group-derived.
 */
export type OrganizationRoleUserAssignmentWritable = {
  [key: string]: unknown
}

export type OrganizationShortWritable = {
  name: string
  domain_name: string
  google_client_id?: string | null
  /**
   * Workspace default UI language; new or no-preference members start in this language.
   *
   * * `en` - En
   * * `ru` - Ru
   * * `ro` - Ro
   */
  default_language?: LanguageEnum
  /**
   * Subset of supported UI languages this workspace offers in its language switcher.
   */
  enabled_languages?: Array<LanguageEnum>
}

export type OrganizationTeamShortWritable = {
  name: string
}

export type OrganizationTeamsOrganigramWritable = {
  name: string
  type?: TeamTypeEnum | NullEnum | null
  abbreviation?: string | null
}

export type OrganizationTinyWritable = {
  name: string
}

/**
 * Public workspace profile fields accepted by the generic PATCH endpoint.
 */
export type OrganizationUpdateWritable = {
  name: string
  photo?: number | null
  domain_name: string
  /**
   * Workspace default UI language; new or no-preference members start in this language.
   *
   * * `en` - En
   * * `ru` - Ru
   * * `ro` - Ro
   */
  default_language?: LanguageEnum
  /**
   * Subset of supported UI languages this workspace offers in its language switcher.
   */
  enabled_languages?: Array<LanguageEnum>
}

export type OrganizationUsersWritable = {
  job_title?: string | null
}

/**
 * Create/update. ``custom_secret`` is write-only and never echoed back.
 *
 * ``managed_by`` / ``managed_ref`` tag a webhook another service owns (Squad: ``"squad"`` /
 * ``"agent:<uuid>"``). They are set on create; a later request may repeat them, not change them.
 */
export type OutboundWebhookWriteWritable = {
  url: string
  events?: Array<string>
  is_active?: boolean
  custom_secret?: string
  name?: string
  managed_by?: string
  managed_ref?: string
}

export type PaginatedAppRoleListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<AppRoleWritable>
}

export type PaginatedAppUserGroupListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<AppUserGroupWritable>
}

export type PaginatedApplicationAccessListListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<ApplicationAccessListWritable>
}

export type PaginatedApplicationListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<ApplicationWritable>
}

export type PaginatedApplicationRepositoryListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<ApplicationRepositoryWritable>
}

export type PaginatedAttachmentListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<AttachmentWritable>
}

export type PaginatedAuditLogArchiveListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<unknown>
}

export type PaginatedAuditLogListListWritable = {
  count: number
  next?: string | null
  previous?: string | null
  results: Array<AuditLogListWritable>
}

export type PaginatedIndustryListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<IndustryWritable>
}

export type PaginatedIntegrationApplicationListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<IntegrationApplicationWritable>
}

export type PaginatedInternalMfaConfigListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<InternalMfaConfigWritable>
}

export type PaginatedInternalMfaLockoutListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<InternalMfaLockoutWritable>
}

export type PaginatedOrganizationCustomAuthProviderListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<OrganizationCustomAuthProviderWritable>
}

export type PaginatedOrganizationJoinRequestListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<OrganizationJoinRequestWritable>
}

export type PaginatedOrganizationListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<OrganizationWritable>
}

export type PaginatedOrganizationRoleFullListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<OrganizationRoleFullWritable>
}

export type PaginatedOrganizationTeamShortListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<OrganizationTeamShortWritable>
}

export type PaginatedOrganizationUsersListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<OrganizationUsersWritable>
}

export type PaginatedPasskeyListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<PasskeyWritable>
}

export type PaginatedPermissionsLocalizedListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<PermissionsLocalizedWritable>
}

export type PaginatedPublicNetworkingOrganizationListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<PublicNetworkingOrganizationWritable>
}

export type PaginatedRpcPresetListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<RpcPresetWritable>
}

export type PaginatedServiceListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<ServiceWritable>
}

export type PaginatedSessionListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<SessionWritable>
}

export type PaginatedSetupJobListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<SetupJobWritable>
}

export type PaginatedTeamListListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<TeamListWritable>
}

export type PaginatedTeamUsersDetailListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<TeamUsersDetailWritable>
}

export type PaginatedTrustedDeviceListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<TrustedDeviceWritable>
}

export type PaginatedUserGroupListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<UserGroupWritable>
}

export type PaginatedUserListListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<UserListWritable>
}

export type PaginatedUserShortListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<UserShortWritable>
}

export type PaginatedWorkspaceRoleListWritable = {
  count: number
  next?: number | null
  previous?: number | null
  results: Array<WorkspaceRoleWritable>
}

export type PasskeyWritable = {
  name: string
}

/**
 * Create/update permissions scoped to a specific Application (third-party custom apps).
 */
export type PatchedApplicationPermissionWritable = {
  /**
   * Permission code: 'action' or 'module.action' or 'app.module.action'
   */
  code?: string
  label?: unknown
  description?: unknown
  application?: number
}

export type PatchedApplicationScimConfigUpdateWritable = {
  inbound_enabled?: boolean
  outbound_enabled?: boolean
  outbound_base_url?: string | null
  outbound_bearer_token?: string | null
}

export type PatchedApplicationUpdateWritable = {
  name?: string
  description?: string
  domain?: string | null
  app_url?: string | null
  /**
   * Attachment id of the uploaded launcher icon. Send null to remove and fall back to the default.
   */
  icon?: number | null
  /**
   * Role every member of the organization gets this app with. Must be a role of this app. Send null to stop auto-provisioning (already granted access is kept).
   */
  auto_provision_role?: number | null
}

export type PatchedInternalMfaConfigWritable = {
  enabled?: boolean
  email_enabled?: boolean
  preferred_method?: OtpCodeChannelEnum
}

/**
 * Industry is write-once — the workspace was seeded from it (BR-12).
 */
export type PatchedOnboardingProfileWritable = {
  industry?: IndustryEnum
  company_size?: CompanySizeEnum
  primary_job?: PrimaryJobEnum | BlankEnum
  estimated_users?: number | null
  source?: AttributionSourceEnum | BlankEnum
  locale?: string
}

export type PatchedOrganizationAccessWritable = {
  job_title?: string | null
  /**
   * Head user who manages this user in the organization
   */
  head_user?: number | null
}

export type PatchedOrganizationAuthMethodsPatchWritable = {
  auth_methods?: Array<OrganizationAuthMethodUpdateWritable>
  enforce_sso?: boolean
  require_mfa?: boolean
  allow_self_service_join?: boolean
  auto_provision_users?: boolean
}

/**
 * Write serializer — handles MASKED_CLIENT_SECRET passthrough and per-protocol validation.
 */
export type PatchedOrganizationCustomAuthProviderWriteWritable = {
  name?: string
  slug?: string
  protocol?: ProtocolEnum
  enabled?: boolean
  logo?: string
  client_id?: string | null
  client_secret?: string | null
  issuer_url?: string | null
  authorize_url?: string | null
  token_url?: string | null
  userinfo_url?: string | null
  jwks_url?: string | null
  scope?: string
  saml_entity_id?: string | null
  saml_sso_url?: string | null
  saml_slo_url?: string | null
  saml_x509_cert?: string | null
  email_claim?: string
  first_name_claim?: string
  last_name_claim?: string
}

export type PatchedOrganizationMicrosoftGraphAppCredentialsPatchWritable = {
  tenant_id?: string | null
  client_id?: string | null
  client_secret?: string | null
  enabled?: boolean
}

/**
 * Supplies localized, role-named descriptions for OrganizationRole audit events.
 */
export type PatchedOrganizationRoleEditWritable = {
  /**
   * Human-readable role name (e.g., 'Sales Manager')
   */
  name?: string
  /**
   * Machine-readable role code name (e.g., 'sales_manager')
   */
  codename?: string | null
  description?: string
  permissions_ids?: Array<number>
}

/**
 * Public workspace profile fields accepted by the generic PATCH endpoint.
 */
export type PatchedOrganizationUpdateWritable = {
  name?: string
  photo?: number | null
  domain_name?: string
  /**
   * Workspace default UI language; new or no-preference members start in this language.
   *
   * * `en` - En
   * * `ru` - Ru
   * * `ro` - Ro
   */
  default_language?: LanguageEnum
  /**
   * Subset of supported UI languages this workspace offers in its language switcher.
   */
  enabled_languages?: Array<LanguageEnum>
}

/**
 * Create/update. ``custom_secret`` is write-only and never echoed back.
 *
 * ``managed_by`` / ``managed_ref`` tag a webhook another service owns (Squad: ``"squad"`` /
 * ``"agent:<uuid>"``). They are set on create; a later request may repeat them, not change them.
 */
export type PatchedOutboundWebhookWriteWritable = {
  url?: string
  events?: Array<string>
  is_active?: boolean
  custom_secret?: string
  name?: string
  managed_by?: string
  managed_ref?: string
}

/**
 * Per-user sign-in policy: allowed methods, refresh-token TTL, trusted device TTL.
 */
export type PatchedSignInPolicyWritable = {
  allow_password_sign_in?: boolean
  allow_social_sign_in?: boolean
  refresh_token_lifetime_days?: number
  trusted_device_validity_days?: number
}

export type PatchedTeamModifyWritable = {
  /**
   * upsert all users
   */
  new_users?: Array<TeamMember> | null
  name?: string
  type?: TeamTypeEnum | NullEnum | null
  abbreviation?: string | null
  description?: string
  parent?: number | null
  organization?: number
  head_user?: number | null
}

export type PatchedUserWritable = {
  last_login?: string | null
  /**
   * Active
   *
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active?: boolean
  date_joined?: string
  first_name?: string
  last_name?: string
  /**
   * Email address
   */
  email?: string
  phone?: string | null
  position?: string | null
  sso_id?: string | null
  language?: LanguageEnum
  archived?: boolean
  photo?: number | null
  about_me?: string
  linkedin_url?: string | null
  birth_date?: string | null
}

export type PatchedUserGroupModifyWritable = {
  name?: string
  organization?: number
  applications?: Array<number>
  users?: Array<number>
  /**
   * Roles this group grants to its members. Replaces the current set.
   */
  roles?: Array<number>
}

/**
 * Supplies localized, role-named descriptions for OrganizationRole audit events.
 */
export type PatchedWorkspaceRoleWritable = {
  /**
   * Human-readable role name (e.g., 'Sales Manager')
   */
  name?: string
  codename?: string
  description?: string
  organization?: number
  permissions_ids?: Array<number>
}

export type PermissionsWritable = {
  /**
   * Permission code: 'action' or 'module.action' or 'app.module.action'
   */
  code: string
  label?: unknown
  description?: unknown
  is_externally_grantable?: boolean
}

export type PermissionsLocalizedWritable = {
  /**
   * Permission code: 'action' or 'module.action' or 'app.module.action'
   */
  code: string
  is_externally_grantable?: boolean
}

export type PublicAccessCredentialCreateResponseWritable = {
  id: string
  name: string
  status?: PublicAccessCredentialStatusEnum
  expires_at?: string | null
  last_used_at?: string | null
  revoked_at?: string | null
}

export type PublicAccessCredentialReadWritable = {
  id: string
  name: string
  status?: PublicAccessCredentialStatusEnum
  expires_at?: string | null
  last_used_at?: string | null
  revoked_at?: string | null
}

export type PublicAccessCredentialRotateResponseWritable = {
  old_credential_id: string
  new_credential: PublicAccessCredentialCreateResponseWritable
}

export type PublicNetworkingOrganizationWritable = {
  id: number
  name: string
  domain_name: string
}

export type RpcMethodCreateWritable = {
  function: string
  priority?: number
}

export type RpcPipelineStepWritable = {
  order: number
}

export type RpcPresetWritable = {
  name: string
  codename: string
}

export type ReportFullWritable = {
  user_mail: string
  user_phone?: string | null
  subject: string
  message: string
  status?: ReportFullStatusEnum
  user: number
}

export type ResponseAuditLogWritable = {
  count: number
  results: Array<DeprecatedAuditLogListWritable>
}

/**
 * Serializer for listing services.
 */
export type ServiceWritable = {
  name: string
  token: string
  secret_token: string
  logo?: string | null
  confirm_url?: string | null
  webhook_create?: string | null
  is_active?: boolean
}

/**
 * User-facing view of an active sign-in session.
 *
 * One row per active refresh token (password/social) or ``oidc_refresh``
 * (OIDC third-party). ``is_current`` is stamped by the viewset. ``kind`` and
 * ``application`` let the UI distinguish first-party logins from OIDC
 * third-party sessions.
 */
export type SessionWritable = {
  [key: string]: unknown
}

export type SettingsMappingWritable = {
  name: string
  key: string
  default_value?: string | null
  application_repository: number
}

export type SetupJobWritable = {
  status?: SetupJobStatusEnum
  job_type?: SetupJobTypeEnum
  organization?: number | null
  created_by?: number | null
  parent?: number | null
}

/**
 * Per-user sign-in policy: allowed methods, refresh-token TTL, trusted device TTL.
 */
export type SignInPolicyWritable = {
  allow_password_sign_in?: boolean
  allow_social_sign_in?: boolean
  refresh_token_lifetime_days?: number
  trusted_device_validity_days?: number
}

export type SyncPermissionsWritable = {
  permissions: Array<PermissionsWritable>
  default_roles?: Array<DefaultRole>
}

export type SyncRpcMethodsWritable = {
  rpc_methods: Array<RpcMethodCreateWritable>
}

export type TeamHeadUserWritable = {
  /**
   * Email address
   */
  email: string
  first_name: string
  last_name: string
}

export type TeamListWritable = {
  name: string
  type?: TeamTypeEnum | NullEnum | null
  abbreviation?: string | null
  description?: string
  parent?: number | null
  organization: number
  head_user?: number | null
}

export type TeamModifyWritable = {
  /**
   * upsert all users
   */
  new_users?: Array<TeamMember> | null
  name: string
  type?: TeamTypeEnum | NullEnum | null
  abbreviation?: string | null
  description?: string
  parent?: number | null
  organization: number
  head_user?: number | null
}

export type TeamShortWritable = {
  name: string
  parent?: number | null
}

export type TeamUsersWritable = {
  user: number
  team: number
}

export type TeamUsersDetailWritable = {
  team: TeamShortWritable
}

export type TrustedDeviceWritable = {
  user_agent?: string | null
  ip_address?: string | null
  device_name?: string | null
  expires_at: string
}

export type UserWritable = {
  last_login?: string | null
  /**
   * Active
   *
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active?: boolean
  date_joined?: string
  first_name: string
  last_name: string
  /**
   * Email address
   */
  email: string
  phone?: string | null
  position?: string | null
  sso_id?: string | null
  language?: LanguageEnum
  archived?: boolean
  photo?: number | null
  about_me?: string
  linkedin_url?: string | null
  birth_date?: string | null
}

export type UserArchiveJobWritable = {
  organization_id: number
  source_user_id: number
  target_user_id: number
  requested_by_id: number | null
  status: UserArchiveJobStatusEnum
}

export type UserArchiveJobDetailResponseWritable = {
  organization_id: number
  source_user_id: number
  target_user_id: number
  requested_by_id: number | null
  status: UserArchiveJobStatusEnum
  latest_dependency_summary?: ArchiveDependencyResponse
}

export type UserCreateWritable = {
  /**
   * Email address
   */
  email: string
  first_name: string
  last_name: string
  password?: string
  invite_url?: string
}

export type UserDetailWritable = {
  /**
   * Email address
   */
  email: string
  first_name: string
  last_name: string
  phone?: string | null
  last_login?: string | null
  sso_id?: string | null
  /**
   * Active
   *
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active?: boolean
  date_joined?: string
  language?: LanguageEnum
  archived?: boolean
}

export type UserGroupWritable = {
  [key: string]: unknown
}

export type UserGroupModifyWritable = {
  name: string
  organization: number
  applications?: Array<number>
  users?: Array<number>
  /**
   * Roles this group grants to its members. Replaces the current set.
   */
  roles?: Array<number>
}

export type UserGroupRoleShortWritable = {
  /**
   * Human-readable role name (e.g., 'Sales Manager')
   */
  name: string
  /**
   * Machine-readable role code name (e.g., 'sales_manager')
   */
  codename?: string | null
  application?: number | null
  /**
   * Admin flag: when True, role automatically has ALL permissions without explicit assignment for specified application (or if application is null, for organization-level permissions).
   */
  is_admin?: boolean
}

export type UserGroupShortWritable = {
  name: string
}

export type UserListWritable = {
  last_login?: string | null
  /**
   * Active
   *
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active?: boolean
  date_joined?: string
  first_name: string
  last_name: string
  /**
   * Email address
   */
  email: string
  phone?: string | null
  position?: string | null
  sso_id?: string | null
  language?: LanguageEnum
  archived?: boolean
  about_me?: string
  linkedin_url?: string | null
  birth_date?: string | null
}

export type UserLogWritable = {
  id?: number | null
  sso_id?: string | null
  first_name?: string | null
  last_name?: string | null
  email?: string | null
  teams?: Array<OrganizationTeamShortWritable>
}

export type UserOrganizationAccessWritable = {
  organization: OrganizationVeryShort
  blocked?: boolean
}

export type UserRetrieveWritable = {
  last_login?: string | null
  /**
   * Active
   *
   * Designates whether this user should be treated as active. Unselect this instead of deleting accounts.
   */
  is_active?: boolean
  date_joined?: string
  first_name: string
  last_name: string
  /**
   * Email address
   */
  email: string
  phone?: string | null
  position?: string | null
  sso_id?: string | null
  language?: LanguageEnum
  archived?: boolean
  about_me?: string
  linkedin_url?: string | null
  birth_date?: string | null
}

export type UserShortWritable = {
  /**
   * Email address
   */
  email: string
  first_name: string
  last_name: string
  phone?: string | null
  last_login?: string | null
  position?: string | null
  sso_id?: string | null
  language?: LanguageEnum
  application_role: string | null
  archived?: boolean
}

export type UserShortWithApplicationsWritable = {
  /**
   * Email address
   */
  email: string
  first_name: string
  last_name: string
  phone?: string | null
  last_login?: string | null
  position?: string | null
  sso_id?: string | null
  language?: LanguageEnum
  application_role: string | null
  archived?: boolean
}

export type UserShortWithSettingsWritable = {
  /**
   * Email address
   */
  email: string
  first_name: string
  last_name: string
  phone?: string | null
  last_login?: string | null
  position?: string | null
  sso_id?: string | null
  language?: LanguageEnum
  application_role: string | null
  archived?: boolean
}

export type UserShortWithoutLoginWritable = {
  /**
   * Email address
   */
  email: string
  first_name: string
  last_name: string
  phone?: string | null
  position?: string | null
  sso_id?: string | null
  language?: LanguageEnum
  application_role: string | null
  archived?: boolean
}

export type UserVeryShortWritable = {
  sso_id?: string | null
  /**
   * Email address
   */
  email: string
  first_name: string
  last_name: string
}

/**
 * Supplies localized, role-named descriptions for OrganizationRole audit events.
 */
export type WorkspaceRoleWritable = {
  /**
   * Human-readable role name (e.g., 'Sales Manager')
   */
  name: string
  codename: string
  description?: string
  organization: number
  permissions_ids?: Array<number>
}

export type AccountRetrieveData = {
  body?: never
  path?: never
  query?: never
  url: '/account/'
}

export type AccountRetrieveResponses = {
  200: CreateAccount
}

export type AccountRetrieveResponse =
  AccountRetrieveResponses[keyof AccountRetrieveResponses]

export type AccountCreateData = {
  body: CreateAccount
  path?: never
  query?: never
  url: '/account/'
}

export type AccountCreateResponses = {
  200: CreateAccount
}

export type AccountCreateResponse =
  AccountCreateResponses[keyof AccountCreateResponses]

export type AccountDestroyData = {
  body?: never
  path: {
    id: number
  }
  query?: never
  url: '/account/{id}/'
}

export type AccountDestroyResponses = {
  /**
   * No response body
   */
  204: void
}

export type AccountDestroyResponse =
  AccountDestroyResponses[keyof AccountDestroyResponses]

export type AccountPartialUpdateData = {
  body?: PatchedEditAccount
  path: {
    id: number
  }
  query?: never
  url: '/account/{id}/'
}

export type AccountPartialUpdateResponses = {
  200: EditAccount
}

export type AccountPartialUpdateResponse =
  AccountPartialUpdateResponses[keyof AccountPartialUpdateResponses]

export type AccountChangePasswordConfirmCreateData = {
  body: ChangePasswordConfirm
  path?: never
  query?: never
  url: '/account/change-password-confirm/'
}

export type AccountChangePasswordConfirmCreateResponses = {
  200: ChangePasswordConfirm
}

export type AccountChangePasswordConfirmCreateResponse =
  AccountChangePasswordConfirmCreateResponses[keyof AccountChangePasswordConfirmCreateResponses]

export type AccountChangePasswordRequestCreateData = {
  body?: never
  path?: never
  query?: never
  url: '/account/change-password-request/'
}

export type AccountChangePasswordRequestCreateResponses = {
  /**
   * No response body
   */
  200: unknown
}

export type AccountLinkedAuthMethodsRetrieveData = {
  body?: never
  path?: never
  query?: never
  url: '/account/linked-auth-methods/'
}

export type AccountLinkedAuthMethodsRetrieveResponses = {
  200: LinkedAuthMethods
}

export type AccountLinkedAuthMethodsRetrieveResponse =
  AccountLinkedAuthMethodsRetrieveResponses[keyof AccountLinkedAuthMethodsRetrieveResponses]

export type AccountNotificationPreferencesRetrieveData = {
  body?: never
  path?: never
  query?: never
  url: '/account/notification-preferences/'
}

export type AccountNotificationPreferencesRetrieveResponses = {
  200: NotificationPreferences
}

export type AccountNotificationPreferencesRetrieveResponse =
  AccountNotificationPreferencesRetrieveResponses[keyof AccountNotificationPreferencesRetrieveResponses]

export type AccountNotificationPreferencesUpdateData = {
  body: NotificationPreferences
  path?: never
  query?: never
  url: '/account/notification-preferences/'
}

export type AccountNotificationPreferencesUpdateResponses = {
  200: NotificationPreferences
}

export type AccountNotificationPreferencesUpdateResponse =
  AccountNotificationPreferencesUpdateResponses[keyof AccountNotificationPreferencesUpdateResponses]

export type AccountNotificationsRetrieveData = {
  body?: never
  path?: never
  query?: never
  url: '/account/notifications/'
}

export type AccountNotificationsRetrieveResponses = {
  200: InAppNotificationsList
}

export type AccountNotificationsRetrieveResponse =
  AccountNotificationsRetrieveResponses[keyof AccountNotificationsRetrieveResponses]

export type AccountNotificationsMarkReadCreateData = {
  body?: InAppNotificationsMarkReadRequest
  path?: never
  query?: never
  url: '/account/notifications/mark-read/'
}

export type AccountNotificationsMarkReadCreateResponses = {
  200: InAppNotificationsMarkReadResponse
}

export type AccountNotificationsMarkReadCreateResponse =
  AccountNotificationsMarkReadCreateResponses[keyof AccountNotificationsMarkReadCreateResponses]

export type AccountNotificationsUnreadCountRetrieveData = {
  body?: never
  path?: never
  query?: never
  url: '/account/notifications/unread-count/'
}

export type AccountNotificationsUnreadCountRetrieveResponses = {
  200: InAppNotificationsUnreadCount
}

export type AccountNotificationsUnreadCountRetrieveResponse =
  AccountNotificationsUnreadCountRetrieveResponses[keyof AccountNotificationsUnreadCountRetrieveResponses]

export type AccountOrgLinkedMethodsRetrieveData = {
  body?: never
  path: {
    org_id: number
  }
  query?: never
  url: '/account/org-linked-methods/{org_id}/'
}

export type AccountOrgLinkedMethodsRetrieveResponses = {
  200: OrgLinkedMethods
}

export type AccountOrgLinkedMethodsRetrieveResponse =
  AccountOrgLinkedMethodsRetrieveResponses[keyof AccountOrgLinkedMethodsRetrieveResponses]

export type AccountOrgSocialAccountsDestroyData = {
  body?: never
  path: {
    org_id: number
    provider: string
  }
  query?: never
  url: '/account/org-social-accounts/{org_id}/{provider}/'
}

export type AccountOrgSocialAccountsDestroyResponses = {
  /**
   * No response body
   */
  204: void
}

export type AccountOrgSocialAccountsDestroyResponse =
  AccountOrgSocialAccountsDestroyResponses[keyof AccountOrgSocialAccountsDestroyResponses]

export type AccountPasskeysListData = {
  body?: never
  path?: never
  query?: {
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * Comma-separated list of IDs to return first (in the given order), before the default ordering.
     */
    preference?: string
  }
  url: '/account/passkeys/'
}

export type AccountPasskeysListResponses = {
  200: PaginatedPasskeyList
}

export type AccountPasskeysListResponse =
  AccountPasskeysListResponses[keyof AccountPasskeysListResponses]

export type AccountPasskeysDestroyData = {
  body?: never
  path: {
    id: number
  }
  query?: never
  url: '/account/passkeys/{id}/'
}

export type AccountPasskeysDestroyResponses = {
  /**
   * No response body
   */
  204: void
}

export type AccountPasskeysDestroyResponse =
  AccountPasskeysDestroyResponses[keyof AccountPasskeysDestroyResponses]

export type AccountPasskeysPartialUpdateData = {
  body?: PatchedPasskeyRename
  path: {
    id: number
  }
  query?: never
  url: '/account/passkeys/{id}/'
}

export type AccountPasskeysPartialUpdateResponses = {
  200: Passkey
}

export type AccountPasskeysPartialUpdateResponse =
  AccountPasskeysPartialUpdateResponses[keyof AccountPasskeysPartialUpdateResponses]

export type AccountPasskeysRegisterStartCreateData = {
  body: PasskeyRegisterStart
  path?: never
  query?: never
  url: '/account/passkeys/register/start/'
}

export type AccountPasskeysRegisterStartCreateResponses = {
  200: PasskeyRegistrationOptions
}

export type AccountPasskeysRegisterStartCreateResponse =
  AccountPasskeysRegisterStartCreateResponses[keyof AccountPasskeysRegisterStartCreateResponses]

export type AccountPasskeysRegisterVerifyCreateData = {
  body: PasskeyRegisterVerify
  path?: never
  query?: never
  url: '/account/passkeys/register/verify/'
}

export type AccountPasskeysRegisterVerifyCreateResponses = {
  201: Passkey
}

export type AccountPasskeysRegisterVerifyCreateResponse =
  AccountPasskeysRegisterVerifyCreateResponses[keyof AccountPasskeysRegisterVerifyCreateResponses]

export type AccountServicesListData = {
  body?: never
  path?: never
  query?: {
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * Comma-separated list of IDs to return first (in the given order), before the default ordering.
     */
    preference?: string
  }
  url: '/account/services/'
}

export type AccountServicesListResponses = {
  200: PaginatedServiceList
}

export type AccountServicesListResponse =
  AccountServicesListResponses[keyof AccountServicesListResponses]

export type AccountSessionsListData = {
  body?: never
  path?: never
  query?: {
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
  }
  url: '/account/sessions/'
}

export type AccountSessionsListResponses = {
  200: PaginatedSessionList
}

export type AccountSessionsListResponse =
  AccountSessionsListResponses[keyof AccountSessionsListResponses]

export type AccountSessionsDestroyData = {
  body?: never
  path: {
    jti: string
  }
  query?: never
  url: '/account/sessions/{jti}/'
}

export type AccountSessionsDestroyResponses = {
  /**
   * No response body
   */
  204: void
}

export type AccountSessionsDestroyResponse =
  AccountSessionsDestroyResponses[keyof AccountSessionsDestroyResponses]

export type AccountSessionsRevokeAllOthersDestroyData = {
  body?: never
  path?: never
  query?: never
  url: '/account/sessions/revoke-all-others/'
}

export type AccountSessionsRevokeAllOthersDestroyResponses = {
  /**
   * No response body
   */
  204: void
}

export type AccountSessionsRevokeAllOthersDestroyResponse =
  AccountSessionsRevokeAllOthersDestroyResponses[keyof AccountSessionsRevokeAllOthersDestroyResponses]

export type AccountSignInPolicyRetrieveData = {
  body?: never
  path?: never
  query?: never
  url: '/account/sign-in-policy/'
}

export type AccountSignInPolicyRetrieveResponses = {
  200: SignInPolicy
}

export type AccountSignInPolicyRetrieveResponse =
  AccountSignInPolicyRetrieveResponses[keyof AccountSignInPolicyRetrieveResponses]

export type AccountSignInPolicyPartialUpdateData = {
  body?: PatchedSignInPolicyWritable
  path?: never
  query?: never
  url: '/account/sign-in-policy/'
}

export type AccountSignInPolicyPartialUpdateResponses = {
  200: SignInPolicy
}

export type AccountSignInPolicyPartialUpdateResponse =
  AccountSignInPolicyPartialUpdateResponses[keyof AccountSignInPolicyPartialUpdateResponses]

export type AccountSignInPolicyUpdateData = {
  body?: SignInPolicyWritable
  path?: never
  query?: never
  url: '/account/sign-in-policy/'
}

export type AccountSignInPolicyUpdateResponses = {
  200: SignInPolicy
}

export type AccountSignInPolicyUpdateResponse =
  AccountSignInPolicyUpdateResponses[keyof AccountSignInPolicyUpdateResponses]

export type AccountSocialAccountsDestroyData = {
  body?: never
  path: {
    provider: string
  }
  query?: never
  url: '/account/social-accounts/{provider}/'
}

export type AccountSocialAccountsDestroyResponses = {
  /**
   * No response body
   */
  204: void
}

export type AccountSocialAccountsDestroyResponse =
  AccountSocialAccountsDestroyResponses[keyof AccountSocialAccountsDestroyResponses]

export type AccountUnlinkPasswordCreateData = {
  body?: never
  path?: never
  query?: never
  url: '/account/unlink-password/'
}

export type AccountUnlinkPasswordCreateResponses = {
  /**
   * No response body
   */
  200: unknown
}

export type AccountUserPartialUpdateData = {
  body?: PatchedEditUser
  path?: never
  query?: never
  url: '/account/user/'
}

export type AccountUserPartialUpdateResponses = {
  200: EditUser
}

export type AccountUserPartialUpdateResponse =
  AccountUserPartialUpdateResponses[keyof AccountUserPartialUpdateResponses]

export type ApplicationsCreateData = {
  body: ApplicationCreate
  path?: never
  query?: never
  url: '/applications/'
}

export type ApplicationsCreateResponses = {
  201: Application
}

export type ApplicationsCreateResponse =
  ApplicationsCreateResponses[keyof ApplicationsCreateResponses]

export type ApplicationsAccessCreateData = {
  body: ApplicationAccessListCreate
  path: {
    organization_id: number
  }
  query?: {
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
  }
  url: '/applications/access/{organization_id}/'
}

export type ApplicationsAccessCreateResponses = {
  200: PaginatedApplicationAccessReadList
}

export type ApplicationsAccessCreateResponse =
  ApplicationsAccessCreateResponses[keyof ApplicationsAccessCreateResponses]

export type ApplicationsApplicationsListData = {
  body?: never
  path?: never
  query?: {
    /**
     * When true, return only applications the current user can actually access (explicit role-bearing grant or access row). Unlike the default listing, this does NOT include the org owner/admin bypass, so it matches what the user may launch.
     */
    accessible?: boolean
    /**
     * comma separated list
     */
    ids?: string
    /**
     * When true, also return system applications — Core's own plumbing, which carries a token and a permission catalogue but is never launched. Only the public-access credential screens need them; everything else gets the default listing without.
     */
    include_system?: boolean
    instance_type?: number
    /**
     * Filter by application instance type (e.g., 'networking')
     */
    instance_type__type?: string
    /**
     * Which field to use when ordering the results.
     */
    ordering?: string
    organization?: number
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * Comma-separated list of IDs to return first (in the given order), before the default ordering.
     */
    preference?: string
    /**
     * A search term.
     */
    search?: string
  }
  url: '/applications/applications/'
}

export type ApplicationsApplicationsListResponses = {
  200: PaginatedApplicationList
}

export type ApplicationsApplicationsListResponse =
  ApplicationsApplicationsListResponses[keyof ApplicationsApplicationsListResponses]

export type ApplicationsApplicationsAccessListData = {
  body?: never
  path?: never
  query?: {
    application?: number
    /**
     * comma separated list
     */
    ids?: string
    /**
     * Which field to use when ordering the results.
     */
    ordering?: string
    organization?: number
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * Comma-separated list of IDs to return first (in the given order), before the default ordering.
     */
    preference?: string
    role?: string
    /**
     * A search term.
     */
    search?: string
    user?: number
  }
  url: '/applications/applications-access/'
}

export type ApplicationsApplicationsAccessListResponses = {
  200: PaginatedApplicationAccessListList
}

export type ApplicationsApplicationsAccessListResponse =
  ApplicationsApplicationsAccessListResponses[keyof ApplicationsApplicationsAccessListResponses]

export type ApplicationsApplicationsAccessRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this Application Access.
     */
    id: number
  }
  query?: never
  url: '/applications/applications-access/{id}/'
}

export type ApplicationsApplicationsAccessRetrieveResponses = {
  200: ApplicationAccessList
}

export type ApplicationsApplicationsAccessRetrieveResponse =
  ApplicationsApplicationsAccessRetrieveResponses[keyof ApplicationsApplicationsAccessRetrieveResponses]

export type ApplicationsApplicationsRepositoriesListData = {
  body?: never
  path?: never
  query?: {
    /**
     * Which field to use when ordering the results.
     */
    ordering?: string
    organization?: string
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * Comma-separated list of IDs to return first (in the given order), before the default ordering.
     */
    preference?: string
    /**
     * A search term.
     */
    search?: string
  }
  url: '/applications/applications-repositories/'
}

export type ApplicationsApplicationsRepositoriesListResponses = {
  200: PaginatedApplicationRepositoryList
}

export type ApplicationsApplicationsRepositoriesListResponse =
  ApplicationsApplicationsRepositoriesListResponses[keyof ApplicationsApplicationsRepositoriesListResponses]

export type ApplicationsApplicationsRepositoriesRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this Application Repository.
     */
    id: number
  }
  query?: never
  url: '/applications/applications-repositories/{id}/'
}

export type ApplicationsApplicationsRepositoriesRetrieveResponses = {
  200: ApplicationRepositoryDetail
}

export type ApplicationsApplicationsRepositoriesRetrieveResponse =
  ApplicationsApplicationsRepositoriesRetrieveResponses[keyof ApplicationsApplicationsRepositoriesRetrieveResponses]

export type ApplicationsApplicationsCredentialsListData = {
  body?: never
  path: {
    /**
     * Application primary key.
     */
    application_id: number
  }
  query?: never
  url: '/applications/applications/{application_id}/credentials/'
}

export type ApplicationsApplicationsCredentialsListResponses = {
  200: Array<AppCredentialRead>
}

export type ApplicationsApplicationsCredentialsListResponse =
  ApplicationsApplicationsCredentialsListResponses[keyof ApplicationsApplicationsCredentialsListResponses]

export type ApplicationsApplicationsCredentialsCreateData = {
  body: AppCredentialCreate
  path: {
    application_id: number
  }
  query?: never
  url: '/applications/applications/{application_id}/credentials/'
}

export type ApplicationsApplicationsCredentialsCreateResponses = {
  201: AppCredentialCreateResponse
}

export type ApplicationsApplicationsCredentialsCreateResponse =
  ApplicationsApplicationsCredentialsCreateResponses[keyof ApplicationsApplicationsCredentialsCreateResponses]

export type ApplicationsApplicationsCredentialsRetrieveData = {
  body?: never
  path: {
    application_id: number
    id: string
  }
  query?: never
  url: '/applications/applications/{application_id}/credentials/{id}/'
}

export type ApplicationsApplicationsCredentialsRetrieveResponses = {
  200: AppCredentialRead
}

export type ApplicationsApplicationsCredentialsRetrieveResponse =
  ApplicationsApplicationsCredentialsRetrieveResponses[keyof ApplicationsApplicationsCredentialsRetrieveResponses]

export type ApplicationsApplicationsCredentialsPartialUpdateData = {
  body?: PatchedAppCredentialUpdate
  path: {
    application_id: number
    id: string
  }
  query?: never
  url: '/applications/applications/{application_id}/credentials/{id}/'
}

export type ApplicationsApplicationsCredentialsPartialUpdateResponses = {
  200: AppCredentialRead
}

export type ApplicationsApplicationsCredentialsPartialUpdateResponse =
  ApplicationsApplicationsCredentialsPartialUpdateResponses[keyof ApplicationsApplicationsCredentialsPartialUpdateResponses]

export type ApplicationsApplicationsCredentialsRevokeCreateData = {
  body?: AppCredentialRevoke
  path: {
    application_id: number
    id: string
  }
  query?: never
  url: '/applications/applications/{application_id}/credentials/{id}/revoke/'
}

export type ApplicationsApplicationsCredentialsRevokeCreateResponses = {
  200: AppCredentialRevokeResponse
}

export type ApplicationsApplicationsCredentialsRevokeCreateResponse =
  ApplicationsApplicationsCredentialsRevokeCreateResponses[keyof ApplicationsApplicationsCredentialsRevokeCreateResponses]

export type ApplicationsApplicationsCredentialsRotateCreateData = {
  body?: PublicAccessCredentialRotate
  path: {
    application_id: number
    id: string
  }
  query?: never
  url: '/applications/applications/{application_id}/credentials/{id}/rotate/'
}

export type ApplicationsApplicationsCredentialsRotateCreateResponses = {
  201: AppCredentialRotateResponse
}

export type ApplicationsApplicationsCredentialsRotateCreateResponse =
  ApplicationsApplicationsCredentialsRotateCreateResponses[keyof ApplicationsApplicationsCredentialsRotateCreateResponses]

export type ApplicationsApplicationsScimRetrieveData = {
  body?: never
  path: {
    id: number
  }
  query?: never
  url: '/applications/applications/{id}/scim/'
}

export type ApplicationsApplicationsScimRetrieveResponses = {
  200: ApplicationScimConfigRead
}

export type ApplicationsApplicationsScimRetrieveResponse =
  ApplicationsApplicationsScimRetrieveResponses[keyof ApplicationsApplicationsScimRetrieveResponses]

export type ApplicationsApplicationsScimPartialUpdateData = {
  body?: PatchedApplicationScimConfigUpdateWritable
  path: {
    id: number
  }
  query?: never
  url: '/applications/applications/{id}/scim/'
}

export type ApplicationsApplicationsScimPartialUpdateResponses = {
  200: ApplicationScimConfigRead
}

export type ApplicationsApplicationsScimPartialUpdateResponse =
  ApplicationsApplicationsScimPartialUpdateResponses[keyof ApplicationsApplicationsScimPartialUpdateResponses]

export type ApplicationsApplicationsScimRotateInboundTokenCreateData = {
  body?: ApplicationScimRotateTokenWritable
  path: {
    id: number
  }
  query?: never
  url: '/applications/applications/{id}/scim/rotate-inbound-token/'
}

export type ApplicationsApplicationsScimRotateInboundTokenCreateResponses = {
  200: ApplicationScimRotateToken
}

export type ApplicationsApplicationsScimRotateInboundTokenCreateResponse =
  ApplicationsApplicationsScimRotateInboundTokenCreateResponses[keyof ApplicationsApplicationsScimRotateInboundTokenCreateResponses]

export type ApplicationsApplicationsWebhookConfigRetrieveData = {
  body?: never
  path: {
    id: number
  }
  query?: never
  url: '/applications/applications/{id}/webhook-config/'
}

export type ApplicationsApplicationsWebhookConfigRetrieveResponses = {
  200: ApplicationWebhookConfigRead
}

export type ApplicationsApplicationsWebhookConfigRetrieveResponse =
  ApplicationsApplicationsWebhookConfigRetrieveResponses[keyof ApplicationsApplicationsWebhookConfigRetrieveResponses]

export type ApplicationsApplicationsWebhookConfigPartialUpdateData = {
  body?: PatchedApplicationWebhookConfigUpdate
  path: {
    id: number
  }
  query?: never
  url: '/applications/applications/{id}/webhook-config/'
}

export type ApplicationsApplicationsWebhookConfigPartialUpdateResponses = {
  200: ApplicationWebhookConfigRead
}

export type ApplicationsApplicationsWebhookConfigPartialUpdateResponse =
  ApplicationsApplicationsWebhookConfigPartialUpdateResponses[keyof ApplicationsApplicationsWebhookConfigPartialUpdateResponses]

export type ApplicationsApplicationsWebhookConfigClearSecretCreateData = {
  body?: ApplicationWebhookConfigRead
  path: {
    id: number
  }
  query?: never
  url: '/applications/applications/{id}/webhook-config/clear-secret/'
}

export type ApplicationsApplicationsWebhookConfigClearSecretCreateResponses = {
  200: ApplicationWebhookConfigRead
}

export type ApplicationsApplicationsWebhookConfigClearSecretCreateResponse =
  ApplicationsApplicationsWebhookConfigClearSecretCreateResponses[keyof ApplicationsApplicationsWebhookConfigClearSecretCreateResponses]

export type ApplicationsApplicationsWebhookConfigRotateSecretCreateData = {
  body?: ApplicationWebhookRotateSecret
  path: {
    id: number
  }
  query?: never
  url: '/applications/applications/{id}/webhook-config/rotate-secret/'
}

export type ApplicationsApplicationsWebhookConfigRotateSecretCreateResponses = {
  200: ApplicationWebhookRotateSecret
}

export type ApplicationsApplicationsWebhookConfigRotateSecretCreateResponse =
  ApplicationsApplicationsWebhookConfigRotateSecretCreateResponses[keyof ApplicationsApplicationsWebhookConfigRotateSecretCreateResponses]

export type ApplicationsApplicationsRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this Application.
     */
    id: number
  }
  query?: never
  url: '/applications/applications/{id}/'
}

export type ApplicationsApplicationsRetrieveResponses = {
  200: ApplicationDetail
}

export type ApplicationsApplicationsRetrieveResponse =
  ApplicationsApplicationsRetrieveResponses[keyof ApplicationsApplicationsRetrieveResponses]

export type ApplicationsApplicationsPartialUpdateData = {
  body?: PatchedApplicationUpdateWritable
  path: {
    /**
     * A unique integer value identifying this Application.
     */
    id: number
  }
  query?: never
  url: '/applications/applications/{id}/'
}

export type ApplicationsApplicationsPartialUpdateResponses = {
  200: ApplicationUpdate
}

export type ApplicationsApplicationsPartialUpdateResponse =
  ApplicationsApplicationsPartialUpdateResponses[keyof ApplicationsApplicationsPartialUpdateResponses]

export type ApplicationsApplicationsUpdateData = {
  body?: ApplicationUpdateWritable
  path: {
    /**
     * A unique integer value identifying this Application.
     */
    id: number
  }
  query?: never
  url: '/applications/applications/{id}/'
}

export type ApplicationsApplicationsUpdateResponses = {
  200: ApplicationUpdate
}

export type ApplicationsApplicationsUpdateResponse =
  ApplicationsApplicationsUpdateResponses[keyof ApplicationsApplicationsUpdateResponses]

export type ApplicationsMobileBuildsLatestRetrieveData = {
  body?: never
  path?: never
  query: {
    /**
     * * `skalx_calls` - Skalx Calls
     */
    app: 'skalx_calls'
  }
  url: '/applications/mobile-builds/latest/'
}

export type ApplicationsMobileBuildsLatestRetrieveErrors = {
  /**
   * No build of this app has been published yet
   */
  404: unknown
}

export type ApplicationsMobileBuildsLatestRetrieveResponses = {
  200: MobileAppBuild
}

export type ApplicationsMobileBuildsLatestRetrieveResponse =
  ApplicationsMobileBuildsLatestRetrieveResponses[keyof ApplicationsMobileBuildsLatestRetrieveResponses]

export type ApplicationsNetworkingOrganizationsListData = {
  body?: never
  path?: never
  query?: {
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
  }
  url: '/applications/networking/organizations/'
}

export type ApplicationsNetworkingOrganizationsListResponses = {
  200: PaginatedPublicNetworkingOrganizationList
}

export type ApplicationsNetworkingOrganizationsListResponse =
  ApplicationsNetworkingOrganizationsListResponses[keyof ApplicationsNetworkingOrganizationsListResponses]

export type ApplicationsNetworkingOrganizationsByEmailCreateData = {
  body: PublicNetworkingOrganizationsRequest
  path?: never
  query?: {
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
  }
  url: '/applications/networking/organizations/by-email/'
}

export type ApplicationsNetworkingOrganizationsByEmailCreateErrors = {
  /**
   * No response body
   */
  400: unknown
  /**
   * No response body
   */
  404: unknown
}

export type ApplicationsNetworkingOrganizationsByEmailCreateResponses = {
  200: PaginatedPublicNetworkingOrganizationList
}

export type ApplicationsNetworkingOrganizationsByEmailCreateResponse =
  ApplicationsNetworkingOrganizationsByEmailCreateResponses[keyof ApplicationsNetworkingOrganizationsByEmailCreateResponses]

export type ApplicationsRemoveDestroyData = {
  body?: never
  path: {
    app_token: string
  }
  query?: never
  url: '/applications/remove/{app_token}/'
}

export type ApplicationsRemoveDestroyResponses = {
  200: OperationStatus
}

export type ApplicationsRemoveDestroyResponse =
  ApplicationsRemoveDestroyResponses[keyof ApplicationsRemoveDestroyResponses]

export type ApplicationsTeamListListData = {
  body?: never
  path: {
    app_token: string
  }
  query?: {
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * Comma-separated list of IDs to return first (in the given order), before the default ordering.
     */
    preference?: string
  }
  url: '/applications/team/{app_token}/list/'
}

export type ApplicationsTeamListListResponses = {
  200: PaginatedOrganizationTeamShortList
}

export type ApplicationsTeamListListResponse =
  ApplicationsTeamListListResponses[keyof ApplicationsTeamListListResponses]

export type ApplicationsThirdPartyCreateData = {
  body: ThirdPartyApplicationCreate
  path?: never
  query?: never
  url: '/applications/third-party/'
}

export type ApplicationsThirdPartyCreateResponses = {
  201: ApplicationDetail
}

export type ApplicationsThirdPartyCreateResponse =
  ApplicationsThirdPartyCreateResponses[keyof ApplicationsThirdPartyCreateResponses]

export type ApplicationsUserCanAccessRetrieveData = {
  body?: never
  path: {
    app_token: string
  }
  query?: never
  url: '/applications/user/{app_token}/can-access/'
}

export type ApplicationsUserCanAccessRetrieveErrors = {
  401: {
    access?: boolean
  }
}

export type ApplicationsUserCanAccessRetrieveError =
  ApplicationsUserCanAccessRetrieveErrors[keyof ApplicationsUserCanAccessRetrieveErrors]

export type ApplicationsUserCanAccessRetrieveResponses = {
  200: {
    access?: boolean
  }
}

export type ApplicationsUserCanAccessRetrieveResponse =
  ApplicationsUserCanAccessRetrieveResponses[keyof ApplicationsUserCanAccessRetrieveResponses]

export type ApplicationsUserDetailsRetrieveData = {
  body?: never
  path: {
    app_token: string
  }
  query?: never
  url: '/applications/user/{app_token}/details'
}

export type ApplicationsUserDetailsRetrieveResponses = {
  200: ApplicationUserDetails
}

export type ApplicationsUserDetailsRetrieveResponse =
  ApplicationsUserDetailsRetrieveResponses[keyof ApplicationsUserDetailsRetrieveResponses]

export type ApplicationsUserListListData = {
  body?: never
  path: {
    app_token: string
  }
  query?: {
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * Comma-separated list of IDs to return first (in the given order), before the default ordering.
     */
    preference?: string
    /**
     * A search term.
     */
    search?: string
  }
  url: '/applications/user/{app_token}/list/'
}

export type ApplicationsUserListListResponses = {
  200: PaginatedUserShortList
}

export type ApplicationsUserListListResponse =
  ApplicationsUserListListResponses[keyof ApplicationsUserListListResponses]

export type ApplicationsUserMembershipRetrieveData = {
  body?: never
  path: {
    app_token: string
  }
  query?: never
  url: '/applications/user/{app_token}/membership/'
}

export type ApplicationsUserMembershipRetrieveResponses = {
  200: ApplicationUserMembership
}

export type ApplicationsUserMembershipRetrieveResponse =
  ApplicationsUserMembershipRetrieveResponses[keyof ApplicationsUserMembershipRetrieveResponses]

export type ApplicationsValidateRetrieveData = {
  body?: never
  path: {
    app_token: string
  }
  query?: never
  url: '/applications/validate/{app_token}/'
}

export type ApplicationsValidateRetrieveResponses = {
  200: ApplicationValidate
}

export type ApplicationsValidateRetrieveResponse =
  ApplicationsValidateRetrieveResponses[keyof ApplicationsValidateRetrieveResponses]

export type AttachmentsListData = {
  body?: never
  path?: never
  query?: {
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * Comma-separated list of IDs to return first (in the given order), before the default ordering.
     */
    preference?: string
  }
  url: '/attachments/'
}

export type AttachmentsListResponses = {
  200: PaginatedAttachmentList
}

export type AttachmentsListResponse =
  AttachmentsListResponses[keyof AttachmentsListResponses]

export type AttachmentsDestroyData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this Attachment.
     */
    id: number
  }
  query?: never
  url: '/attachments/{id}/'
}

export type AttachmentsDestroyResponses = {
  /**
   * No response body
   */
  204: void
}

export type AttachmentsDestroyResponse =
  AttachmentsDestroyResponses[keyof AttachmentsDestroyResponses]

export type AttachmentsRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this Attachment.
     */
    id: number
  }
  query?: never
  url: '/attachments/{id}/'
}

export type AttachmentsRetrieveResponses = {
  200: Attachment
}

export type AttachmentsRetrieveResponse =
  AttachmentsRetrieveResponses[keyof AttachmentsRetrieveResponses]

export type AttachmentsVersionDestroyData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this Attachment.
     */
    id: number
    version_id: string
  }
  query?: never
  url: '/attachments/{id}/version/{version_id}/'
}

export type AttachmentsVersionDestroyResponses = {
  /**
   * No response body
   */
  204: void
}

export type AttachmentsVersionDestroyResponse =
  AttachmentsVersionDestroyResponses[keyof AttachmentsVersionDestroyResponses]

export type AttachmentsVersionRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this Attachment.
     */
    id: number
    version_id: string
  }
  query?: never
  url: '/attachments/{id}/version/{version_id}/'
}

export type AttachmentsVersionRetrieveResponses = {
  200: Attachment
}

export type AttachmentsVersionRetrieveResponse =
  AttachmentsVersionRetrieveResponses[keyof AttachmentsVersionRetrieveResponses]

export type AttachmentsVisibilityPartialUpdateData = {
  body?: PatchedUpdateAttachmentVisibility
  path: {
    /**
     * A unique integer value identifying this Attachment.
     */
    id: number
  }
  query?: never
  url: '/attachments/{id}/visibility/'
}

export type AttachmentsVisibilityPartialUpdateResponses = {
  200: Attachment
}

export type AttachmentsVisibilityPartialUpdateResponse =
  AttachmentsVisibilityPartialUpdateResponses[keyof AttachmentsVisibilityPartialUpdateResponses]

export type AttachmentsCreateAvatarSignedUrlCreateData = {
  body?: CreateAvatarUploadUrlWritable
  path?: never
  query?: never
  url: '/attachments/create-avatar-signed-url/'
}

export type AttachmentsCreateAvatarSignedUrlCreateResponses = {
  201: AttachmentWebhook
}

export type AttachmentsCreateAvatarSignedUrlCreateResponse =
  AttachmentsCreateAvatarSignedUrlCreateResponses[keyof AttachmentsCreateAvatarSignedUrlCreateResponses]

export type AttachmentsCreateSignedUrlCreateData = {
  body?: CreateUploadUrlWritable
  path?: never
  query?: never
  url: '/attachments/create-signed-url/'
}

export type AttachmentsCreateSignedUrlCreateResponses = {
  201: AttachmentWebhook
}

export type AttachmentsCreateSignedUrlCreateResponse =
  AttachmentsCreateSignedUrlCreateResponses[keyof AttachmentsCreateSignedUrlCreateResponses]

export type AttachmentsWebhookCreateData = {
  body: WebhookData
  path?: never
  query?: never
  url: '/attachments/webhook/'
}

export type AttachmentsWebhookCreateErrors = {
  /**
   * No response body
   */
  400: unknown
}

export type AttachmentsWebhookCreateResponses = {
  200: unknown
}

export type AuthorizationClientAuthMethodsRetrieveData = {
  body?: never
  path?: never
  query?: never
  url: '/authorization/client/auth-methods/'
}

export type AuthorizationClientAuthMethodsRetrieveResponses = {
  200: ClientAuthMethods
}

export type AuthorizationClientAuthMethodsRetrieveResponse =
  AuthorizationClientAuthMethodsRetrieveResponses[keyof AuthorizationClientAuthMethodsRetrieveResponses]

export type AuthorizationPasskeyAuthenticateStartCreateData = {
  body?: PasskeyAuthenticateStart
  path?: never
  query?: never
  url: '/authorization/passkey/authenticate/start/'
}

export type AuthorizationPasskeyAuthenticateStartCreateResponses = {
  200: PasskeyAuthenticationOptions
}

export type AuthorizationPasskeyAuthenticateStartCreateResponse =
  AuthorizationPasskeyAuthenticateStartCreateResponses[keyof AuthorizationPasskeyAuthenticateStartCreateResponses]

export type AuthorizationPasskeyAuthenticateVerifyCreateData = {
  body: PasskeyAuthenticateVerify
  path?: never
  query?: never
  url: '/authorization/passkey/authenticate/verify/'
}

export type AuthorizationPasskeyAuthenticateVerifyCreateResponses = {
  200: PasskeyAuthTokens
}

export type AuthorizationPasskeyAuthenticateVerifyCreateResponse =
  AuthorizationPasskeyAuthenticateVerifyCreateResponses[keyof AuthorizationPasskeyAuthenticateVerifyCreateResponses]

export type AuthorizationQrApproveCreateData = {
  body: QrLoginApprove
  path?: never
  query?: never
  url: '/authorization/qr/approve/'
}

export type AuthorizationQrApproveCreateErrors = {
  403: QrLoginErrorResponse
  404: QrLoginErrorResponse
  409: QrLoginErrorResponse
  429: QrLoginErrorResponse
}

export type AuthorizationQrApproveCreateError =
  AuthorizationQrApproveCreateErrors[keyof AuthorizationQrApproveCreateErrors]

export type AuthorizationQrApproveCreateResponses = {
  200: QrLoginStatusResponse
}

export type AuthorizationQrApproveCreateResponse =
  AuthorizationQrApproveCreateResponses[keyof AuthorizationQrApproveCreateResponses]

export type AuthorizationQrCancelCreateData = {
  body: QrLoginCancel
  path?: never
  query?: never
  url: '/authorization/qr/cancel/'
}

export type AuthorizationQrCancelCreateErrors = {
  403: QrLoginErrorResponse
  404: QrLoginErrorResponse
  429: QrLoginErrorResponse
}

export type AuthorizationQrCancelCreateError =
  AuthorizationQrCancelCreateErrors[keyof AuthorizationQrCancelCreateErrors]

export type AuthorizationQrCancelCreateResponses = {
  200: QrLoginStatusResponse
}

export type AuthorizationQrCancelCreateResponse =
  AuthorizationQrCancelCreateResponses[keyof AuthorizationQrCancelCreateResponses]

export type AuthorizationQrDenyCreateData = {
  body: QrLoginDeny
  path?: never
  query?: never
  url: '/authorization/qr/deny/'
}

export type AuthorizationQrDenyCreateErrors = {
  403: QrLoginErrorResponse
  404: QrLoginErrorResponse
  409: QrLoginErrorResponse
  429: QrLoginErrorResponse
}

export type AuthorizationQrDenyCreateError =
  AuthorizationQrDenyCreateErrors[keyof AuthorizationQrDenyCreateErrors]

export type AuthorizationQrDenyCreateResponses = {
  200: QrLoginStatusResponse
}

export type AuthorizationQrDenyCreateResponse =
  AuthorizationQrDenyCreateResponses[keyof AuthorizationQrDenyCreateResponses]

export type AuthorizationQrScanCreateData = {
  body: QrLoginScan
  path?: never
  query?: never
  url: '/authorization/qr/scan/'
}

export type AuthorizationQrScanCreateErrors = {
  403: QrLoginErrorResponse
  404: QrLoginErrorResponse
  409: QrLoginErrorResponse
  429: QrLoginErrorResponse
}

export type AuthorizationQrScanCreateError =
  AuthorizationQrScanCreateErrors[keyof AuthorizationQrScanCreateErrors]

export type AuthorizationQrScanCreateResponses = {
  200: QrLoginScanResponse
}

export type AuthorizationQrScanCreateResponse =
  AuthorizationQrScanCreateResponses[keyof AuthorizationQrScanCreateResponses]

export type AuthorizationQrStartCreateData = {
  body?: QrLoginStart
  path?: never
  query?: never
  url: '/authorization/qr/start/'
}

export type AuthorizationQrStartCreateErrors = {
  403: QrLoginErrorResponse
  404: QrLoginErrorResponse
  429: QrLoginErrorResponse
}

export type AuthorizationQrStartCreateError =
  AuthorizationQrStartCreateErrors[keyof AuthorizationQrStartCreateErrors]

export type AuthorizationQrStartCreateResponses = {
  200: QrLoginStartResponse
}

export type AuthorizationQrStartCreateResponse =
  AuthorizationQrStartCreateResponses[keyof AuthorizationQrStartCreateResponses]

export type AuthorizationQrWaitCreateData = {
  body: QrLoginWait
  path?: never
  query?: never
  url: '/authorization/qr/wait/'
}

export type AuthorizationQrWaitCreateErrors = {
  403: QrLoginErrorResponse
  404: QrLoginErrorResponse
  429: QrLoginErrorResponse
}

export type AuthorizationQrWaitCreateError =
  AuthorizationQrWaitCreateErrors[keyof AuthorizationQrWaitCreateErrors]

export type AuthorizationQrWaitCreateResponses = {
  200: QrLoginWaitResponse
}

export type AuthorizationQrWaitCreateResponse =
  AuthorizationQrWaitCreateResponses[keyof AuthorizationQrWaitCreateResponses]

export type AuthorizationSocialAppleConnectCreateData = {
  body: SocialExchange
  path?: never
  query?: never
  url: '/authorization/social/apple/connect/'
}

export type AuthorizationSocialAppleConnectCreateResponses = {
  200: SocialExchange
}

export type AuthorizationSocialAppleConnectCreateResponse =
  AuthorizationSocialAppleConnectCreateResponses[keyof AuthorizationSocialAppleConnectCreateResponses]

export type AuthorizationSocialAppleConnectUrlCreateData = {
  body: SocialAppleConnectStart
  path?: never
  query?: never
  url: '/authorization/social/apple/connect/url/'
}

export type AuthorizationSocialAppleConnectUrlCreateResponses = {
  200: SocialAppleConnectStart
}

export type AuthorizationSocialAppleConnectUrlCreateResponse =
  AuthorizationSocialAppleConnectUrlCreateResponses[keyof AuthorizationSocialAppleConnectUrlCreateResponses]

export type AuthorizationSocialAppleOrgConnectCreateData = {
  body: SocialExchange
  path?: never
  query?: never
  url: '/authorization/social/apple/org-connect/'
}

export type AuthorizationSocialAppleOrgConnectCreateResponses = {
  200: SocialExchange
}

export type AuthorizationSocialAppleOrgConnectCreateResponse =
  AuthorizationSocialAppleOrgConnectCreateResponses[keyof AuthorizationSocialAppleOrgConnectCreateResponses]

export type AuthorizationSocialAppleOrgConnectUrlCreateData = {
  body: SocialAppleStart
  path?: never
  query?: never
  url: '/authorization/social/apple/org-connect/url/'
}

export type AuthorizationSocialAppleOrgConnectUrlCreateResponses = {
  200: SocialAppleStart
}

export type AuthorizationSocialAppleOrgConnectUrlCreateResponse =
  AuthorizationSocialAppleOrgConnectUrlCreateResponses[keyof AuthorizationSocialAppleOrgConnectUrlCreateResponses]

export type AuthorizationSocialAppleStartCreateData = {
  body: SocialAppleStart
  path?: never
  query?: never
  url: '/authorization/social/apple/start/'
}

export type AuthorizationSocialAppleStartCreateResponses = {
  200: SocialAppleStart
}

export type AuthorizationSocialAppleStartCreateResponse =
  AuthorizationSocialAppleStartCreateResponses[keyof AuthorizationSocialAppleStartCreateResponses]

export type AuthorizationSocialAppleStartClientCreateData = {
  body: SocialGoogleStart
  path?: never
  query?: never
  url: '/authorization/social/apple/start/client/'
}

export type AuthorizationSocialAppleStartClientCreateResponses = {
  200: SocialGoogleStart
}

export type AuthorizationSocialAppleStartClientCreateResponse =
  AuthorizationSocialAppleStartClientCreateResponses[keyof AuthorizationSocialAppleStartClientCreateResponses]

export type AuthorizationSocialAppleStateInfoRetrieveData = {
  body?: never
  path?: never
  query?: never
  url: '/authorization/social/apple/state-info/'
}

export type AuthorizationSocialAppleStateInfoRetrieveResponses = {
  /**
   * No response body
   */
  200: unknown
}

export type AuthorizationSocialCustomStartCreateData = {
  body: CustomAuthStart
  path: {
    provider_id: number
  }
  query?: never
  url: '/authorization/social/custom/{provider_id}/start/'
}

export type AuthorizationSocialCustomStartCreateResponses = {
  200: CustomAuthStartResponse
}

export type AuthorizationSocialCustomStartCreateResponse =
  AuthorizationSocialCustomStartCreateResponses[keyof AuthorizationSocialCustomStartCreateResponses]

export type AuthorizationSocialCustomExchangeCreateData = {
  body: CustomAuthExchange
  path?: never
  query?: never
  url: '/authorization/social/custom/exchange/'
}

export type AuthorizationSocialCustomExchangeCreateResponses = {
  200: CustomAuthExchange
}

export type AuthorizationSocialCustomExchangeCreateResponse =
  AuthorizationSocialCustomExchangeCreateResponses[keyof AuthorizationSocialCustomExchangeCreateResponses]

export type AuthorizationSocialCustomSamlExchangeCreateData = {
  body: CustomAuthSamlExchange
  path?: never
  query?: never
  url: '/authorization/social/custom/saml/exchange/'
}

export type AuthorizationSocialCustomSamlExchangeCreateResponses = {
  200: CustomAuthSamlExchange
}

export type AuthorizationSocialCustomSamlExchangeCreateResponse =
  AuthorizationSocialCustomSamlExchangeCreateResponses[keyof AuthorizationSocialCustomSamlExchangeCreateResponses]

export type AuthorizationSocialEntraidConnectCreateData = {
  body: SocialExchange
  path?: never
  query?: never
  url: '/authorization/social/entraid/connect/'
}

export type AuthorizationSocialEntraidConnectCreateResponses = {
  200: SocialExchange
}

export type AuthorizationSocialEntraidConnectCreateResponse =
  AuthorizationSocialEntraidConnectCreateResponses[keyof AuthorizationSocialEntraidConnectCreateResponses]

export type AuthorizationSocialEntraidConnectUrlCreateData = {
  body: SocialEntraIdConnectStart
  path?: never
  query?: never
  url: '/authorization/social/entraid/connect/url/'
}

export type AuthorizationSocialEntraidConnectUrlCreateResponses = {
  200: SocialEntraIdConnectStart
}

export type AuthorizationSocialEntraidConnectUrlCreateResponse =
  AuthorizationSocialEntraidConnectUrlCreateResponses[keyof AuthorizationSocialEntraidConnectUrlCreateResponses]

export type AuthorizationSocialEntraidStartCreateData = {
  body: SocialEntraIdStart
  path?: never
  query?: never
  url: '/authorization/social/entraid/start/'
}

export type AuthorizationSocialEntraidStartCreateResponses = {
  200: SocialEntraIdStart
}

export type AuthorizationSocialEntraidStartCreateResponse =
  AuthorizationSocialEntraidStartCreateResponses[keyof AuthorizationSocialEntraidStartCreateResponses]

export type AuthorizationSocialEntraidStateInfoRetrieveData = {
  body?: never
  path?: never
  query?: never
  url: '/authorization/social/entraid/state-info/'
}

export type AuthorizationSocialEntraidStateInfoRetrieveResponses = {
  /**
   * No response body
   */
  200: unknown
}

export type AuthorizationSocialExchangeCreateData = {
  body: SocialExchange
  path?: never
  query?: never
  url: '/authorization/social/exchange/'
}

export type AuthorizationSocialExchangeCreateResponses = {
  200: SocialExchange
}

export type AuthorizationSocialExchangeCreateResponse =
  AuthorizationSocialExchangeCreateResponses[keyof AuthorizationSocialExchangeCreateResponses]

export type AuthorizationSocialExchangeClientCreateData = {
  body: SocialExchange
  path?: never
  query?: never
  url: '/authorization/social/exchange/client/'
}

export type AuthorizationSocialExchangeClientCreateResponses = {
  200: SocialExchange
}

export type AuthorizationSocialExchangeClientCreateResponse =
  AuthorizationSocialExchangeClientCreateResponses[keyof AuthorizationSocialExchangeClientCreateResponses]

export type AuthorizationSocialGoogleClientConnectCreateData = {
  body: SocialExchange
  path?: never
  query?: never
  url: '/authorization/social/google/client-connect/'
}

export type AuthorizationSocialGoogleClientConnectCreateResponses = {
  200: SocialExchange
}

export type AuthorizationSocialGoogleClientConnectCreateResponse =
  AuthorizationSocialGoogleClientConnectCreateResponses[keyof AuthorizationSocialGoogleClientConnectCreateResponses]

export type AuthorizationSocialGoogleClientConnectUrlCreateData = {
  body: SocialGoogleConnectStart
  path?: never
  query?: never
  url: '/authorization/social/google/client-connect/url/'
}

export type AuthorizationSocialGoogleClientConnectUrlCreateResponses = {
  200: SocialGoogleConnectStart
}

export type AuthorizationSocialGoogleClientConnectUrlCreateResponse =
  AuthorizationSocialGoogleClientConnectUrlCreateResponses[keyof AuthorizationSocialGoogleClientConnectUrlCreateResponses]

export type AuthorizationSocialGoogleConnectCreateData = {
  body: SocialExchange
  path?: never
  query?: never
  url: '/authorization/social/google/connect/'
}

export type AuthorizationSocialGoogleConnectCreateResponses = {
  200: SocialExchange
}

export type AuthorizationSocialGoogleConnectCreateResponse =
  AuthorizationSocialGoogleConnectCreateResponses[keyof AuthorizationSocialGoogleConnectCreateResponses]

export type AuthorizationSocialGoogleConnectUrlCreateData = {
  body: SocialGoogleOrgStart
  path?: never
  query?: never
  url: '/authorization/social/google/connect/url/'
}

export type AuthorizationSocialGoogleConnectUrlCreateResponses = {
  200: SocialGoogleOrgStart
}

export type AuthorizationSocialGoogleConnectUrlCreateResponse =
  AuthorizationSocialGoogleConnectUrlCreateResponses[keyof AuthorizationSocialGoogleConnectUrlCreateResponses]

export type AuthorizationSocialGoogleStartCreateData = {
  body: SocialGoogleOrgStart
  path?: never
  query?: never
  url: '/authorization/social/google/start/'
}

export type AuthorizationSocialGoogleStartCreateResponses = {
  200: SocialGoogleOrgStart
}

export type AuthorizationSocialGoogleStartCreateResponse =
  AuthorizationSocialGoogleStartCreateResponses[keyof AuthorizationSocialGoogleStartCreateResponses]

export type AuthorizationSocialGoogleStartClientCreateData = {
  body: SocialGoogleStart
  path?: never
  query?: never
  url: '/authorization/social/google/start/client/'
}

export type AuthorizationSocialGoogleStartClientCreateResponses = {
  200: SocialGoogleStart
}

export type AuthorizationSocialGoogleStartClientCreateResponse =
  AuthorizationSocialGoogleStartClientCreateResponses[keyof AuthorizationSocialGoogleStartClientCreateResponses]

export type AuthorizationSocialGoogleStateInfoRetrieveData = {
  body?: never
  path?: never
  query?: never
  url: '/authorization/social/google/state-info/'
}

export type AuthorizationSocialGoogleStateInfoRetrieveResponses = {
  /**
   * No response body
   */
  200: unknown
}

export type AuthorizationTokenCreateData = {
  body: DomainTokenObtainWritable
  path?: never
  query?: never
  url: '/authorization/token/'
}

export type AuthorizationTokenCreateResponses = {
  200: DomainTokenObtain
}

export type AuthorizationTokenCreateResponse =
  AuthorizationTokenCreateResponses[keyof AuthorizationTokenCreateResponses]

export type AuthorizationTokenClientCreateData = {
  body: AccountUiTokenObtainWritable
  path?: never
  query?: never
  url: '/authorization/token/client/'
}

export type AuthorizationTokenClientCreateResponses = {
  200: AccountUiTokenObtain
}

export type AuthorizationTokenClientCreateResponse =
  AuthorizationTokenClientCreateResponses[keyof AuthorizationTokenClientCreateResponses]

export type AuthorizationTokenCookieRetrieveData = {
  body?: never
  path?: never
  query?: never
  url: '/authorization/token/cookie/'
}

export type AuthorizationTokenCookieRetrieveResponses = {
  200: CustomTokenRefresh
}

export type AuthorizationTokenCookieRetrieveResponse =
  AuthorizationTokenCookieRetrieveResponses[keyof AuthorizationTokenCookieRetrieveResponses]

export type AuthorizationTokenLogoutRetrieveData = {
  body?: never
  path?: never
  query?: never
  url: '/authorization/token/logout/'
}

export type AuthorizationTokenLogoutRetrieveResponses = {
  /**
   * No response body
   */
  200: unknown
}

export type AuthorizationTokenOtpLinkVerifyCreateData = {
  body: EmailOtpLinkVerify
  path?: never
  query?: never
  url: '/authorization/token/otp/link/verify/'
}

export type AuthorizationTokenOtpLinkVerifyCreateResponses = {
  200: EmailOtpLinkVerify
}

export type AuthorizationTokenOtpLinkVerifyCreateResponse =
  AuthorizationTokenOtpLinkVerifyCreateResponses[keyof AuthorizationTokenOtpLinkVerifyCreateResponses]

export type AuthorizationTokenOtpRequestCreateData = {
  body: EmailOtpRequest
  path?: never
  query?: never
  url: '/authorization/token/otp/request/'
}

export type AuthorizationTokenOtpRequestCreateResponses = {
  200: EmailOtpRequest
}

export type AuthorizationTokenOtpRequestCreateResponse =
  AuthorizationTokenOtpRequestCreateResponses[keyof AuthorizationTokenOtpRequestCreateResponses]

export type AuthorizationTokenOtpVerifyCreateData = {
  body: EmailOtpVerify
  path?: never
  query?: never
  url: '/authorization/token/otp/verify/'
}

export type AuthorizationTokenOtpVerifyCreateResponses = {
  200: EmailOtpVerify
}

export type AuthorizationTokenOtpVerifyCreateResponse =
  AuthorizationTokenOtpVerifyCreateResponses[keyof AuthorizationTokenOtpVerifyCreateResponses]

export type AuthorizationTokenRefreshCreateData = {
  body: CustomTokenRefreshWritable
  path?: never
  query?: never
  url: '/authorization/token/refresh/'
}

export type AuthorizationTokenRefreshCreateResponses = {
  200: CustomTokenRefresh
}

export type AuthorizationTokenRefreshCreateResponse =
  AuthorizationTokenRefreshCreateResponses[keyof AuthorizationTokenRefreshCreateResponses]

export type AuthorizationTokenServiceVerifyCreateData = {
  body: TokenServiceVerify
  path?: never
  query?: never
  url: '/authorization/token/service/verify/'
}

export type AuthorizationTokenServiceVerifyCreateResponses = {
  200: TokenServiceVerify
}

export type AuthorizationTokenServiceVerifyCreateResponse =
  AuthorizationTokenServiceVerifyCreateResponses[keyof AuthorizationTokenServiceVerifyCreateResponses]

export type AuthorizationTokenVerifyCreateData = {
  body: TokenVerify
  path?: never
  query?: never
  url: '/authorization/token/verify/'
}

export type AuthorizationTokenVerifyCreateResponses = {
  200: TokenVerify
}

export type AuthorizationTokenVerifyCreateResponse =
  AuthorizationTokenVerifyCreateResponses[keyof AuthorizationTokenVerifyCreateResponses]

export type AuthorizationV2ClientsListData = {
  body?: never
  path?: never
  query?: never
  url: '/authorization/v2/clients/'
}

export type AuthorizationV2ClientsListResponses = {
  200: AuthorizationV2oAuthClientRead
}

export type AuthorizationV2ClientsListResponse =
  AuthorizationV2ClientsListResponses[keyof AuthorizationV2ClientsListResponses]

export type AuthorizationV2ClientsCreateData = {
  body: AuthorizationV2oAuthClientCreate
  path?: never
  query?: never
  url: '/authorization/v2/clients/'
}

export type AuthorizationV2ClientsCreateResponses = {
  201: AuthorizationV2oAuthClientRead
}

export type AuthorizationV2ClientsCreateResponse =
  AuthorizationV2ClientsCreateResponses[keyof AuthorizationV2ClientsCreateResponses]

export type AuthorizationV2ClientsRetrieveData = {
  body?: never
  path: {
    id: number
  }
  query?: never
  url: '/authorization/v2/clients/{id}/'
}

export type AuthorizationV2ClientsRetrieveResponses = {
  200: AuthorizationV2oAuthClientRead
}

export type AuthorizationV2ClientsRetrieveResponse =
  AuthorizationV2ClientsRetrieveResponses[keyof AuthorizationV2ClientsRetrieveResponses]

export type AuthorizationV2ClientsPartialUpdateData = {
  body?: PatchedAuthorizationV2oAuthClientUpdate
  path: {
    id: number
  }
  query?: never
  url: '/authorization/v2/clients/{id}/'
}

export type AuthorizationV2ClientsPartialUpdateResponses = {
  200: AuthorizationV2oAuthClientRead
}

export type AuthorizationV2ClientsPartialUpdateResponse =
  AuthorizationV2ClientsPartialUpdateResponses[keyof AuthorizationV2ClientsPartialUpdateResponses]

export type AuthorizationV2ClientsRevokeCreateData = {
  body: AuthorizationV2oAuthClientReadWritable
  path: {
    id: number
  }
  query?: never
  url: '/authorization/v2/clients/{id}/revoke/'
}

export type AuthorizationV2ClientsRevokeCreateResponses = {
  200: AuthorizationV2oAuthClientRead
}

export type AuthorizationV2ClientsRevokeCreateResponse =
  AuthorizationV2ClientsRevokeCreateResponses[keyof AuthorizationV2ClientsRevokeCreateResponses]

export type AuthorizationV2ClientsRotateCreateData = {
  body: AuthorizationV2oAuthClientReadWritable
  path: {
    id: number
  }
  query?: never
  url: '/authorization/v2/clients/{id}/rotate/'
}

export type AuthorizationV2ClientsRotateCreateResponses = {
  200: AuthorizationV2oAuthClientRead
}

export type AuthorizationV2ClientsRotateCreateResponse =
  AuthorizationV2ClientsRotateCreateResponses[keyof AuthorizationV2ClientsRotateCreateResponses]

export type AuthorizationV2EmailVerifyCreateData = {
  body: ConfirmUser
  path?: never
  query?: never
  url: '/authorization/v2/email/verify/'
}

export type AuthorizationV2EmailVerifyCreateErrors = {
  400: EmailVerifyV2Error
}

export type AuthorizationV2EmailVerifyCreateError =
  AuthorizationV2EmailVerifyCreateErrors[keyof AuthorizationV2EmailVerifyCreateErrors]

export type AuthorizationV2EmailVerifyCreateResponses = {
  200: EmailVerifyV2Response
}

export type AuthorizationV2EmailVerifyCreateResponse =
  AuthorizationV2EmailVerifyCreateResponses[keyof AuthorizationV2EmailVerifyCreateResponses]

export type AuthorizationV2InviteAcceptCreateData = {
  body: UserInviteAccept
  path?: never
  query?: never
  url: '/authorization/v2/invite/accept/'
}

export type AuthorizationV2InviteAcceptCreateResponses = {
  200: UserShort
}

export type AuthorizationV2InviteAcceptCreateResponse =
  AuthorizationV2InviteAcceptCreateResponses[keyof AuthorizationV2InviteAcceptCreateResponses]

export type AuthorizationV2InviteInfoRetrieveData = {
  body?: never
  path?: never
  query: {
    token: string
  }
  url: '/authorization/v2/invite/info/'
}

export type AuthorizationV2InviteInfoRetrieveErrors = {
  400: EmailVerifyV2Error
}

export type AuthorizationV2InviteInfoRetrieveError =
  AuthorizationV2InviteInfoRetrieveErrors[keyof AuthorizationV2InviteInfoRetrieveErrors]

export type AuthorizationV2InviteInfoRetrieveResponses = {
  200: InviteInfoV2Response
}

export type AuthorizationV2InviteInfoRetrieveResponse =
  AuthorizationV2InviteInfoRetrieveResponses[keyof AuthorizationV2InviteInfoRetrieveResponses]

export type AuthorizationV2InviteRequestCreateData = {
  body: UserInvite
  path?: never
  query?: {
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
  }
  url: '/authorization/v2/invite/request/'
}

export type AuthorizationV2InviteRequestCreateResponses = {
  200: PaginatedOrganizationUsersList
}

export type AuthorizationV2InviteRequestCreateResponse =
  AuthorizationV2InviteRequestCreateResponses[keyof AuthorizationV2InviteRequestCreateResponses]

export type McpConsentRetrieveData = {
  body?: never
  path: {
    request_id: string
  }
  query?: never
  url: '/authorization/v2/mcp/consent/{request_id}/'
}

export type McpConsentRetrieveResponses = {
  200: McpConsentRequest
}

export type McpConsentRetrieveResponse =
  McpConsentRetrieveResponses[keyof McpConsentRetrieveResponses]

export type McpConsentApproveData = {
  body: McpConsentApprove
  path: {
    request_id: string
  }
  query?: never
  url: '/authorization/v2/mcp/consent/{request_id}/approve/'
}

export type McpConsentApproveResponses = {
  200: McpConsentDecision
}

export type McpConsentApproveResponse =
  McpConsentApproveResponses[keyof McpConsentApproveResponses]

export type McpConsentDenyData = {
  body?: never
  path: {
    request_id: string
  }
  query?: never
  url: '/authorization/v2/mcp/consent/{request_id}/deny/'
}

export type McpConsentDenyResponses = {
  200: McpConsentDecision
}

export type McpConsentDenyResponse =
  McpConsentDenyResponses[keyof McpConsentDenyResponses]

export type AuthorizationV2PasswordRequestCreateData = {
  body: RestorePass
  path?: never
  query?: never
  url: '/authorization/v2/password/request/'
}

export type AuthorizationV2PasswordRequestCreateResponses = {
  200: RestorePass
}

export type AuthorizationV2PasswordRequestCreateResponse =
  AuthorizationV2PasswordRequestCreateResponses[keyof AuthorizationV2PasswordRequestCreateResponses]

export type AuthorizationV2PasswordResetCreateData = {
  body: RestorePasswordConfirm
  path?: never
  query?: never
  url: '/authorization/v2/password/reset/'
}

export type AuthorizationV2PasswordResetCreateResponses = {
  200: RestorePasswordConfirm
}

export type AuthorizationV2PasswordResetCreateResponse =
  AuthorizationV2PasswordResetCreateResponses[keyof AuthorizationV2PasswordResetCreateResponses]

export type AuthorizationV2SchemaRetrieveData = {
  body?: never
  path?: never
  query?: {
    format?: 'json' | 'yaml'
    lang?: 'en' | 'ro' | 'ru'
  }
  url: '/authorization/v2/schema'
}

export type AuthorizationV2SchemaRetrieveResponses = {
  200: {
    [key: string]: unknown
  }
}

export type AuthorizationV2SchemaRetrieveResponse =
  AuthorizationV2SchemaRetrieveResponses[keyof AuthorizationV2SchemaRetrieveResponses]

export type AuthorizationV2SignupConfirmCreateData = {
  body: UserRegisterConfirm
  path?: never
  query?: never
  url: '/authorization/v2/signup/confirm/'
}

export type AuthorizationV2SignupConfirmCreateResponses = {
  200: TokenPairWithStatus
}

export type AuthorizationV2SignupConfirmCreateResponse =
  AuthorizationV2SignupConfirmCreateResponses[keyof AuthorizationV2SignupConfirmCreateResponses]

export type AuthorizationV2SignupStartCreateData = {
  body: UserRegisterStart
  path?: never
  query?: never
  url: '/authorization/v2/signup/start/'
}

export type AuthorizationV2SignupStartCreateResponses = {
  200: UserVeryShort
}

export type AuthorizationV2SignupStartCreateResponse =
  AuthorizationV2SignupStartCreateResponses[keyof AuthorizationV2SignupStartCreateResponses]

export type CommonCountriesListData = {
  body?: never
  path?: never
  query?: {
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * A search term
     */
    search?: string
  }
  url: '/common/countries/'
}

export type CommonCountriesListResponses = {
  200: PaginatedCountryList
}

export type CommonCountriesListResponse =
  CommonCountriesListResponses[keyof CommonCountriesListResponses]

export type CommonCountriesRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this country.
     */
    id: number
  }
  query?: never
  url: '/common/countries/{id}/'
}

export type CommonCountriesRetrieveResponses = {
  200: CountryDetail
}

export type CommonCountriesRetrieveResponse =
  CommonCountriesRetrieveResponses[keyof CommonCountriesRetrieveResponses]

export type CommonHealthRetrieveData = {
  body?: never
  path?: never
  query?: never
  url: '/common/health/'
}

export type CommonHealthRetrieveResponses = {
  200: {
    live?: boolean
    version?: string
  }
}

export type CommonHealthRetrieveResponse =
  CommonHealthRetrieveResponses[keyof CommonHealthRetrieveResponses]

export type CommonIndustriesListData = {
  body?: never
  path?: never
  query?: {
    /**
     * Which field to use when ordering the results.
     */
    ordering?: string
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * Comma-separated list of IDs to return first (in the given order), before the default ordering.
     */
    preference?: string
    /**
     * A search term.
     */
    search?: string
  }
  url: '/common/industries/'
}

export type CommonIndustriesListResponses = {
  200: PaginatedIndustryList
}

export type CommonIndustriesListResponse =
  CommonIndustriesListResponses[keyof CommonIndustriesListResponses]

export type CommonIndustriesRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this industry.
     */
    id: number
  }
  query?: never
  url: '/common/industries/{id}/'
}

export type CommonIndustriesRetrieveResponses = {
  200: Industry
}

export type CommonIndustriesRetrieveResponse =
  CommonIndustriesRetrieveResponses[keyof CommonIndustriesRetrieveResponses]

export type CommonProtectedRetrieveData = {
  body?: never
  path?: never
  query?: never
  url: '/common/protected/'
}

export type CommonProtectedRetrieveResponses = {
  200: {
    live?: boolean
  }
}

export type CommonProtectedRetrieveResponse =
  CommonProtectedRetrieveResponses[keyof CommonProtectedRetrieveResponses]

export type IntegrationApplicationPublishCreateData = {
  body: ApplicationPublish
  path?: never
  query?: never
  url: '/integration/application/publish/'
}

export type IntegrationApplicationPublishCreateResponses = {
  200: Application
}

export type IntegrationApplicationPublishCreateResponse =
  IntegrationApplicationPublishCreateResponses[keyof IntegrationApplicationPublishCreateResponses]

export type IntegrationApplicationsListData = {
  body?: never
  path?: never
  query?: {
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    type?: string
  }
  url: '/integration/applications/'
}

export type IntegrationApplicationsListResponses = {
  200: PaginatedIntegrationApplicationList
}

export type IntegrationApplicationsListResponse =
  IntegrationApplicationsListResponses[keyof IntegrationApplicationsListResponses]

export type IntegrationApplicationsRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this Application.
     */
    id: number
  }
  query?: never
  url: '/integration/applications/{id}/'
}

export type IntegrationApplicationsRetrieveResponses = {
  200: IntegrationApplication
}

export type IntegrationApplicationsRetrieveResponse =
  IntegrationApplicationsRetrieveResponses[keyof IntegrationApplicationsRetrieveResponses]

export type IntegrationApplicationsDataRetrieveData = {
  body?: never
  path?: never
  query?: never
  url: '/integration/applications/data/'
}

export type IntegrationApplicationsDataRetrieveResponses = {
  200: IntegrationApplicationDetail
}

export type IntegrationApplicationsDataRetrieveResponse =
  IntegrationApplicationsDataRetrieveResponses[keyof IntegrationApplicationsDataRetrieveResponses]

export type IntegrationApplicationsNotificationsCreateData = {
  body: IntegrationNotifications
  path?: never
  query?: never
  url: '/integration/applications/notifications/'
}

export type IntegrationApplicationsNotificationsCreateResponses = {
  200: IntegrationApplicationType
}

export type IntegrationApplicationsNotificationsCreateResponse =
  IntegrationApplicationsNotificationsCreateResponses[keyof IntegrationApplicationsNotificationsCreateResponses]

export type IntegrationApplicationsRolePermissionsCreateData = {
  body: IntegrationRolesPermissions
  path?: never
  query?: never
  url: '/integration/applications/role-permissions/'
}

export type IntegrationApplicationsRolePermissionsCreateResponses = {
  200: IntegrationApplicationType
}

export type IntegrationApplicationsRolePermissionsCreateResponse =
  IntegrationApplicationsRolePermissionsCreateResponses[keyof IntegrationApplicationsRolePermissionsCreateResponses]

export type IntegrationApplicationsRolesCreateData = {
  body: IntegrationApplicationRoles
  path?: never
  query?: never
  url: '/integration/applications/roles/'
}

export type IntegrationApplicationsRolesCreateResponses = {
  200: IntegrationApplicationRoles
}

export type IntegrationApplicationsRolesCreateResponse =
  IntegrationApplicationsRolesCreateResponses[keyof IntegrationApplicationsRolesCreateResponses]

export type IntegrationFeedRetrieveData = {
  body?: never
  path?: never
  query?: {
    application_type?: string
    limit?: number
    offset_date?: string
  }
  url: '/integration/feed/'
}

export type IntegrationFeedRetrieveResponses = {
  200: ResponseAuditLog
}

export type IntegrationFeedRetrieveResponse =
  IntegrationFeedRetrieveResponses[keyof IntegrationFeedRetrieveResponses]

export type IntegrationGoogleMailCallbackRetrieveData = {
  body?: never
  path?: never
  query?: never
  url: '/integration/google/mail/callback/'
}

export type IntegrationGoogleMailConnectStartCreateData = {
  body: GoogleMailConnectStart
  path?: never
  query?: never
  url: '/integration/google/mail/connect/start/'
}

export type IntegrationGoogleMailConnectStartCreateErrors = {
  400: GoogleMailError
}

export type IntegrationGoogleMailConnectStartCreateError =
  IntegrationGoogleMailConnectStartCreateErrors[keyof IntegrationGoogleMailConnectStartCreateErrors]

export type IntegrationGoogleMailConnectStartCreateResponses = {
  200: GoogleMailConnectStartResponse
}

export type IntegrationGoogleMailConnectStartCreateResponse =
  IntegrationGoogleMailConnectStartCreateResponses[keyof IntegrationGoogleMailConnectStartCreateResponses]

export type IntegrationGoogleMailConnectionsRetrieveData = {
  body?: never
  path?: never
  query: {
    domain_name: string
  }
  url: '/integration/google/mail/connections/'
}

export type IntegrationGoogleMailConnectionsRetrieveResponses = {
  200: GoogleMailConnectionList
}

export type IntegrationGoogleMailConnectionsRetrieveResponse =
  IntegrationGoogleMailConnectionsRetrieveResponses[keyof IntegrationGoogleMailConnectionsRetrieveResponses]

export type IntegrationGoogleMailConnectionsDisconnectCreateData = {
  body: GoogleMailDisconnect
  path: {
    connection_id: number
  }
  query?: never
  url: '/integration/google/mail/connections/{connection_id}/disconnect/'
}

export type IntegrationGoogleMailConnectionsDisconnectCreateResponses = {
  200: GoogleMailConnection
}

export type IntegrationGoogleMailConnectionsDisconnectCreateResponse =
  IntegrationGoogleMailConnectionsDisconnectCreateResponses[keyof IntegrationGoogleMailConnectionsDisconnectCreateResponses]

export type IntegrationMicrosoftGraphAvailabilityRetrieveData = {
  body?: never
  path?: never
  query: {
    domain_name: string
  }
  url: '/integration/microsoft/graph/availability/'
}

export type IntegrationMicrosoftGraphAvailabilityRetrieveResponses = {
  200: MicrosoftGraphAvailability
}

export type IntegrationMicrosoftGraphAvailabilityRetrieveResponse =
  IntegrationMicrosoftGraphAvailabilityRetrieveResponses[keyof IntegrationMicrosoftGraphAvailabilityRetrieveResponses]

export type IntegrationMicrosoftGraphCallbackRetrieveData = {
  body?: never
  path?: never
  query?: never
  url: '/integration/microsoft/graph/callback/'
}

export type IntegrationMicrosoftGraphConnectStartCreateData = {
  body: MicrosoftGraphConnectStart
  path?: never
  query?: never
  url: '/integration/microsoft/graph/connect/start/'
}

export type IntegrationMicrosoftGraphConnectStartCreateResponses = {
  200: MicrosoftGraphConnectStartResponse
}

export type IntegrationMicrosoftGraphConnectStartCreateResponse =
  IntegrationMicrosoftGraphConnectStartCreateResponses[keyof IntegrationMicrosoftGraphConnectStartCreateResponses]

export type IntegrationMicrosoftGraphConnectionsListData = {
  body?: never
  path?: never
  query: {
    domain_name: string
  }
  url: '/integration/microsoft/graph/connections/'
}

export type IntegrationMicrosoftGraphConnectionsListResponses = {
  200: Array<MicrosoftGraphConnection>
}

export type IntegrationMicrosoftGraphConnectionsListResponse =
  IntegrationMicrosoftGraphConnectionsListResponses[keyof IntegrationMicrosoftGraphConnectionsListResponses]

export type IntegrationMicrosoftGraphConnectionsDisconnectCreateData = {
  body: MicrosoftGraphDisconnect
  path: {
    connection_id: number
  }
  query?: never
  url: '/integration/microsoft/graph/connections/{connection_id}/disconnect/'
}

export type IntegrationMicrosoftGraphConnectionsDisconnectCreateResponses = {
  200: MicrosoftGraphConnection
}

export type IntegrationMicrosoftGraphConnectionsDisconnectCreateResponse =
  IntegrationMicrosoftGraphConnectionsDisconnectCreateResponses[keyof IntegrationMicrosoftGraphConnectionsDisconnectCreateResponses]

export type IntegrationMicrosoftGraphDisconnectCreateData = {
  body: MicrosoftGraphDisconnect
  path?: never
  query?: never
  url: '/integration/microsoft/graph/disconnect/'
}

export type IntegrationMicrosoftGraphDisconnectCreateResponses = {
  200: MicrosoftGraphStatus
}

export type IntegrationMicrosoftGraphDisconnectCreateResponse =
  IntegrationMicrosoftGraphDisconnectCreateResponses[keyof IntegrationMicrosoftGraphDisconnectCreateResponses]

export type IntegrationMicrosoftGraphStatusRetrieveData = {
  body?: never
  path?: never
  query: {
    domain_name: string
  }
  url: '/integration/microsoft/graph/status/'
}

export type IntegrationMicrosoftGraphStatusRetrieveResponses = {
  200: MicrosoftGraphStatus
}

export type IntegrationMicrosoftGraphStatusRetrieveResponse =
  IntegrationMicrosoftGraphStatusRetrieveResponses[keyof IntegrationMicrosoftGraphStatusRetrieveResponses]

export type IntegrationPermissionMigrationsApplyCreateData = {
  body: PermissionMigrationApply
  path?: never
  query?: never
  url: '/integration/permission-migrations/apply/'
}

export type IntegrationPermissionMigrationsApplyCreateResponses = {
  200: PermissionMigrationApply
}

export type IntegrationPermissionMigrationsApplyCreateResponse =
  IntegrationPermissionMigrationsApplyCreateResponses[keyof IntegrationPermissionMigrationsApplyCreateResponses]

export type IntegrationRpcPresetsListData = {
  body?: never
  path?: never
  query?: {
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
  }
  url: '/integration/rpc/presets/'
}

export type IntegrationRpcPresetsListResponses = {
  200: PaginatedRpcPresetList
}

export type IntegrationRpcPresetsListResponse =
  IntegrationRpcPresetsListResponses[keyof IntegrationRpcPresetsListResponses]

export type IntegrationRpcPresetsRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this rpc preset.
     */
    id: number
  }
  query?: never
  url: '/integration/rpc/presets/{id}/'
}

export type IntegrationRpcPresetsRetrieveResponses = {
  200: RpcPreset
}

export type IntegrationRpcPresetsRetrieveResponse =
  IntegrationRpcPresetsRetrieveResponses[keyof IntegrationRpcPresetsRetrieveResponses]

export type IntegrationSetupJobsListData = {
  body?: never
  path?: never
  query?: {
    /**
     * * `build` - Build
     * * `rebuild` - Rebuild
     */
    job_type?: 'build' | 'rebuild'
    /**
     * A page number within the paginated result set.
     */
    page?: number
    parent?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * * `pending` - Pending
     * * `in_process` - In Process
     * * `failed` - Failed
     * * `canceled` - Canceled
     * * `finished` - Finished
     */
    status?: 'canceled' | 'failed' | 'finished' | 'in_process' | 'pending'
  }
  url: '/integration/setup/jobs/'
}

export type IntegrationSetupJobsListResponses = {
  200: PaginatedSetupJobList
}

export type IntegrationSetupJobsListResponse =
  IntegrationSetupJobsListResponses[keyof IntegrationSetupJobsListResponses]

export type IntegrationSetupJobsDestroyData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this setup job.
     */
    id: number
  }
  query?: never
  url: '/integration/setup/jobs/{id}/'
}

export type IntegrationSetupJobsDestroyResponses = {
  /**
   * No response body
   */
  204: void
}

export type IntegrationSetupJobsDestroyResponse =
  IntegrationSetupJobsDestroyResponses[keyof IntegrationSetupJobsDestroyResponses]

export type IntegrationSetupJobsRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this setup job.
     */
    id: number
  }
  query?: never
  url: '/integration/setup/jobs/{id}/'
}

export type IntegrationSetupJobsRetrieveResponses = {
  200: SetupJob
}

export type IntegrationSetupJobsRetrieveResponse =
  IntegrationSetupJobsRetrieveResponses[keyof IntegrationSetupJobsRetrieveResponses]

export type IntegrationSetupJobsRetryCreateData = {
  body?: SetupJobWritable
  path: {
    /**
     * A unique integer value identifying this setup job.
     */
    id: number
  }
  query?: never
  url: '/integration/setup/jobs/{id}/retry/'
}

export type IntegrationSetupJobsRetryCreateResponses = {
  200: SetupJob
}

export type IntegrationSetupJobsRetryCreateResponse =
  IntegrationSetupJobsRetryCreateResponses[keyof IntegrationSetupJobsRetryCreateResponses]

export type IntegrationSyncPermissionsCreateData = {
  body: SyncPermissionsWritable
  path?: never
  query?: never
  url: '/integration/sync-permissions/'
}

export type IntegrationSyncPermissionsCreateResponses = {
  200: SyncPermissionsResponse
}

export type IntegrationSyncPermissionsCreateResponse =
  IntegrationSyncPermissionsCreateResponses[keyof IntegrationSyncPermissionsCreateResponses]

export type IntegrationSyncRpcMethodsCreateData = {
  body: SyncRpcMethodsWritable
  path?: never
  query?: never
  url: '/integration/sync-rpc-methods/'
}

export type IntegrationSyncRpcMethodsCreateResponses = {
  200: SyncRpcMethodsResponse
}

export type IntegrationSyncRpcMethodsCreateResponse =
  IntegrationSyncRpcMethodsCreateResponses[keyof IntegrationSyncRpcMethodsCreateResponses]

export type IntegrationSyncRpcTemplatesCreateData = {
  body: SyncRpcTemplates
  path?: never
  query?: never
  url: '/integration/sync-rpc-templates/'
}

export type IntegrationSyncRpcTemplatesCreateResponses = {
  200: SyncRpcTemplatesResponse
}

export type IntegrationSyncRpcTemplatesCreateResponse =
  IntegrationSyncRpcTemplatesCreateResponses[keyof IntegrationSyncRpcTemplatesCreateResponses]

export type IntegrationTeamsListData = {
  body?: never
  path?: never
  query?: {
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
  }
  url: '/integration/teams/'
}

export type IntegrationTeamsListResponses = {
  200: PaginatedOrganizationTeamShortList
}

export type IntegrationTeamsListResponse =
  IntegrationTeamsListResponses[keyof IntegrationTeamsListResponses]

export type IntegrationTeamsRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this team.
     */
    id: number
  }
  query?: never
  url: '/integration/teams/{id}/'
}

export type IntegrationTeamsRetrieveResponses = {
  200: OrganizationTeamShort
}

export type IntegrationTeamsRetrieveResponse =
  IntegrationTeamsRetrieveResponses[keyof IntegrationTeamsRetrieveResponses]

export type IntegrationUserGroupsListData = {
  body?: never
  path?: never
  query?: {
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
  }
  url: '/integration/user-groups/'
}

export type IntegrationUserGroupsListResponses = {
  200: PaginatedAppUserGroupList
}

export type IntegrationUserGroupsListResponse =
  IntegrationUserGroupsListResponses[keyof IntegrationUserGroupsListResponses]

export type IntegrationUserGroupsRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this user group.
     */
    id: number
  }
  query?: never
  url: '/integration/user-groups/{id}/'
}

export type IntegrationUserGroupsRetrieveResponses = {
  200: AppUserGroup
}

export type IntegrationUserGroupsRetrieveResponse =
  IntegrationUserGroupsRetrieveResponses[keyof IntegrationUserGroupsRetrieveResponses]

export type IntegrationUserRetrieveData = {
  body?: never
  path: {
    sso_id: string
  }
  query?: never
  url: '/integration/user/{sso_id}/'
}

export type IntegrationUserRetrieveResponses = {
  200: UserShortWithApplications
}

export type IntegrationUserRetrieveResponse =
  IntegrationUserRetrieveResponses[keyof IntegrationUserRetrieveResponses]

export type IntegrationUsersListData = {
  body?: never
  path?: never
  query?: {
    /**
     * List of "sso_id" to include to list
     */
    include?: Array<string>
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * Common search filter
     */
    search?: string
    /**
     * * `application` - Application
     * * `organization` - Organization
     */
    source?: 'application' | 'organization'
  }
  url: '/integration/users/'
}

export type IntegrationUsersListResponses = {
  200: PaginatedUserShortList
}

export type IntegrationUsersListResponse =
  IntegrationUsersListResponses[keyof IntegrationUsersListResponses]

export type IntegrationUsersCreateData = {
  body: UserCreateWritable
  path?: never
  query?: never
  url: '/integration/users/'
}

export type IntegrationUsersCreateResponses = {
  201: UserDetail
}

export type IntegrationUsersCreateResponse =
  IntegrationUsersCreateResponses[keyof IntegrationUsersCreateResponses]

export type IntegrationUsersRetrieveData = {
  body?: never
  path: {
    sso_id: string
  }
  query?: never
  url: '/integration/users/{sso_id}/'
}

export type IntegrationUsersRetrieveResponses = {
  200: UserDetail
}

export type IntegrationUsersRetrieveResponse =
  IntegrationUsersRetrieveResponses[keyof IntegrationUsersRetrieveResponses]

export type IntegrationUsersPartialUpdateData = {
  body?: PatchedIntegrationPatchUser
  path: {
    sso_id: string
  }
  query?: never
  url: '/integration/users/{sso_id}/'
}

export type IntegrationUsersPartialUpdateResponses = {
  200: UserDetail
}

export type IntegrationUsersPartialUpdateResponse =
  IntegrationUsersPartialUpdateResponses[keyof IntegrationUsersPartialUpdateResponses]

export type IntegrationUsersInviteCreateData = {
  body: AppUserInvite
  path?: never
  query?: {
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
  }
  url: '/integration/users/invite/'
}

export type IntegrationUsersInviteCreateResponses = {
  200: PaginatedOrganizationUsersList
}

export type IntegrationUsersInviteCreateResponse =
  IntegrationUsersInviteCreateResponses[keyof IntegrationUsersInviteCreateResponses]

export type IntegrationV2ApplicationPublishCreateData = {
  body: ApplicationPublish
  path?: never
  query?: never
  url: '/integration/v2/application/publish/'
}

export type IntegrationV2ApplicationPublishCreateResponses = {
  200: Application
}

export type IntegrationV2ApplicationPublishCreateResponse =
  IntegrationV2ApplicationPublishCreateResponses[keyof IntegrationV2ApplicationPublishCreateResponses]

export type IntegrationV2ApplicationsListData = {
  body?: never
  path?: never
  query?: {
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    type?: string
  }
  url: '/integration/v2/applications/'
}

export type IntegrationV2ApplicationsListResponses = {
  200: PaginatedIntegrationApplicationList
}

export type IntegrationV2ApplicationsListResponse =
  IntegrationV2ApplicationsListResponses[keyof IntegrationV2ApplicationsListResponses]

export type IntegrationV2ApplicationsRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this Application.
     */
    id: number
  }
  query?: never
  url: '/integration/v2/applications/{id}/'
}

export type IntegrationV2ApplicationsRetrieveResponses = {
  200: IntegrationApplication
}

export type IntegrationV2ApplicationsRetrieveResponse =
  IntegrationV2ApplicationsRetrieveResponses[keyof IntegrationV2ApplicationsRetrieveResponses]

export type IntegrationV2ApplicationsDataRetrieveData = {
  body?: never
  path?: never
  query?: never
  url: '/integration/v2/applications/data/'
}

export type IntegrationV2ApplicationsDataRetrieveResponses = {
  200: IntegrationApplicationDetail
}

export type IntegrationV2ApplicationsDataRetrieveResponse =
  IntegrationV2ApplicationsDataRetrieveResponses[keyof IntegrationV2ApplicationsDataRetrieveResponses]

export type IntegrationV2ApplicationsNotificationsCreateData = {
  body: IntegrationNotifications
  path?: never
  query?: never
  url: '/integration/v2/applications/notifications/'
}

export type IntegrationV2ApplicationsNotificationsCreateResponses = {
  200: IntegrationApplicationType
}

export type IntegrationV2ApplicationsNotificationsCreateResponse =
  IntegrationV2ApplicationsNotificationsCreateResponses[keyof IntegrationV2ApplicationsNotificationsCreateResponses]

export type IntegrationV2ApplicationsRolePermissionsCreateData = {
  body: IntegrationRolesPermissions
  path?: never
  query?: never
  url: '/integration/v2/applications/role-permissions/'
}

export type IntegrationV2ApplicationsRolePermissionsCreateResponses = {
  200: IntegrationApplicationType
}

export type IntegrationV2ApplicationsRolePermissionsCreateResponse =
  IntegrationV2ApplicationsRolePermissionsCreateResponses[keyof IntegrationV2ApplicationsRolePermissionsCreateResponses]

export type IntegrationV2ApplicationsRolesCreateData = {
  body: IntegrationApplicationRoles
  path?: never
  query?: never
  url: '/integration/v2/applications/roles/'
}

export type IntegrationV2ApplicationsRolesCreateResponses = {
  200: IntegrationApplicationRoles
}

export type IntegrationV2ApplicationsRolesCreateResponse =
  IntegrationV2ApplicationsRolesCreateResponses[keyof IntegrationV2ApplicationsRolesCreateResponses]

export type IntegrationV2AppsRolesListData = {
  body?: never
  path: {
    application_id: number
  }
  query?: {
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
  }
  url: '/integration/v2/apps/{application_id}/roles/'
}

export type IntegrationV2AppsRolesListResponses = {
  200: PaginatedAppRoleList
}

export type IntegrationV2AppsRolesListResponse =
  IntegrationV2AppsRolesListResponses[keyof IntegrationV2AppsRolesListResponses]

export type IntegrationV2AppsRolesCreateData = {
  body?: AppRoleWrite
  path: {
    application_id: number
  }
  query?: never
  url: '/integration/v2/apps/{application_id}/roles/'
}

export type IntegrationV2AppsRolesCreateResponses = {
  201: AppRole
}

export type IntegrationV2AppsRolesCreateResponse =
  IntegrationV2AppsRolesCreateResponses[keyof IntegrationV2AppsRolesCreateResponses]

export type IntegrationV2AppsRolesDestroyData = {
  body?: never
  path: {
    application_id: number
    role_id: number
  }
  query?: never
  url: '/integration/v2/apps/{application_id}/roles/{role_id}/'
}

export type IntegrationV2AppsRolesDestroyResponses = {
  /**
   * No response body
   */
  204: void
}

export type IntegrationV2AppsRolesDestroyResponse =
  IntegrationV2AppsRolesDestroyResponses[keyof IntegrationV2AppsRolesDestroyResponses]

export type IntegrationV2AppsRolesRetrieveData = {
  body?: never
  path: {
    application_id: number
    role_id: number
  }
  query?: never
  url: '/integration/v2/apps/{application_id}/roles/{role_id}/'
}

export type IntegrationV2AppsRolesRetrieveResponses = {
  200: AppRole
}

export type IntegrationV2AppsRolesRetrieveResponse =
  IntegrationV2AppsRolesRetrieveResponses[keyof IntegrationV2AppsRolesRetrieveResponses]

export type IntegrationV2AppsRolesPartialUpdateData = {
  body?: PatchedAppRoleWrite
  path: {
    application_id: number
    role_id: number
  }
  query?: never
  url: '/integration/v2/apps/{application_id}/roles/{role_id}/'
}

export type IntegrationV2AppsRolesPartialUpdateResponses = {
  200: AppRole
}

export type IntegrationV2AppsRolesPartialUpdateResponse =
  IntegrationV2AppsRolesPartialUpdateResponses[keyof IntegrationV2AppsRolesPartialUpdateResponses]

export type IntegrationV2AppsRolesMembersRetrieveData = {
  body?: never
  path: {
    application_id: number
    role_id: number
  }
  query?: never
  url: '/integration/v2/apps/{application_id}/roles/{role_id}/members/'
}

export type IntegrationV2AppsRolesMembersRetrieveResponses = {
  200: AppRoleMembers
}

export type IntegrationV2AppsRolesMembersRetrieveResponse =
  IntegrationV2AppsRolesMembersRetrieveResponses[keyof IntegrationV2AppsRolesMembersRetrieveResponses]

export type IntegrationV2AppsRolesMembersUpdateData = {
  body: AppRoleMembers
  path: {
    application_id: number
    role_id: number
  }
  query?: never
  url: '/integration/v2/apps/{application_id}/roles/{role_id}/members/'
}

export type IntegrationV2AppsRolesMembersUpdateResponses = {
  200: AppRoleMembers
}

export type IntegrationV2AppsRolesMembersUpdateResponse =
  IntegrationV2AppsRolesMembersUpdateResponses[keyof IntegrationV2AppsRolesMembersUpdateResponses]

export type IntegrationV2FeedRetrieveData = {
  body?: never
  path?: never
  query?: {
    application_type?: string
    limit?: number
    offset_date?: string
  }
  url: '/integration/v2/feed/'
}

export type IntegrationV2FeedRetrieveResponses = {
  200: ResponseAuditLog
}

export type IntegrationV2FeedRetrieveResponse =
  IntegrationV2FeedRetrieveResponses[keyof IntegrationV2FeedRetrieveResponses]

export type IntegrationV2RpcPresetsListData = {
  body?: never
  path?: never
  query?: {
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
  }
  url: '/integration/v2/rpc/presets/'
}

export type IntegrationV2RpcPresetsListResponses = {
  200: PaginatedRpcPresetList
}

export type IntegrationV2RpcPresetsListResponse =
  IntegrationV2RpcPresetsListResponses[keyof IntegrationV2RpcPresetsListResponses]

export type IntegrationV2RpcPresetsRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this rpc preset.
     */
    id: number
  }
  query?: never
  url: '/integration/v2/rpc/presets/{id}/'
}

export type IntegrationV2RpcPresetsRetrieveResponses = {
  200: RpcPreset
}

export type IntegrationV2RpcPresetsRetrieveResponse =
  IntegrationV2RpcPresetsRetrieveResponses[keyof IntegrationV2RpcPresetsRetrieveResponses]

export type IntegrationV2SetupJobsListData = {
  body?: never
  path?: never
  query?: {
    /**
     * * `build` - Build
     * * `rebuild` - Rebuild
     */
    job_type?: 'build' | 'rebuild'
    /**
     * A page number within the paginated result set.
     */
    page?: number
    parent?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * * `pending` - Pending
     * * `in_process` - In Process
     * * `failed` - Failed
     * * `canceled` - Canceled
     * * `finished` - Finished
     */
    status?: 'canceled' | 'failed' | 'finished' | 'in_process' | 'pending'
  }
  url: '/integration/v2/setup/jobs/'
}

export type IntegrationV2SetupJobsListResponses = {
  200: PaginatedSetupJobList
}

export type IntegrationV2SetupJobsListResponse =
  IntegrationV2SetupJobsListResponses[keyof IntegrationV2SetupJobsListResponses]

export type IntegrationV2SetupJobsDestroyData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this setup job.
     */
    id: number
  }
  query?: never
  url: '/integration/v2/setup/jobs/{id}/'
}

export type IntegrationV2SetupJobsDestroyResponses = {
  /**
   * No response body
   */
  204: void
}

export type IntegrationV2SetupJobsDestroyResponse =
  IntegrationV2SetupJobsDestroyResponses[keyof IntegrationV2SetupJobsDestroyResponses]

export type IntegrationV2SetupJobsRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this setup job.
     */
    id: number
  }
  query?: never
  url: '/integration/v2/setup/jobs/{id}/'
}

export type IntegrationV2SetupJobsRetrieveResponses = {
  200: SetupJob
}

export type IntegrationV2SetupJobsRetrieveResponse =
  IntegrationV2SetupJobsRetrieveResponses[keyof IntegrationV2SetupJobsRetrieveResponses]

export type IntegrationV2SetupJobsRetryCreateData = {
  body?: SetupJobWritable
  path: {
    /**
     * A unique integer value identifying this setup job.
     */
    id: number
  }
  query?: never
  url: '/integration/v2/setup/jobs/{id}/retry/'
}

export type IntegrationV2SetupJobsRetryCreateResponses = {
  200: SetupJob
}

export type IntegrationV2SetupJobsRetryCreateResponse =
  IntegrationV2SetupJobsRetryCreateResponses[keyof IntegrationV2SetupJobsRetryCreateResponses]

export type IntegrationV2SyncPermissionsCreateData = {
  body: SyncPermissionsWritable
  path?: never
  query?: never
  url: '/integration/v2/sync-permissions/'
}

export type IntegrationV2SyncPermissionsCreateResponses = {
  200: SyncPermissionsResponse
}

export type IntegrationV2SyncPermissionsCreateResponse =
  IntegrationV2SyncPermissionsCreateResponses[keyof IntegrationV2SyncPermissionsCreateResponses]

export type IntegrationV2SyncRpcMethodsCreateData = {
  body: SyncRpcMethodsWritable
  path?: never
  query?: never
  url: '/integration/v2/sync-rpc-methods/'
}

export type IntegrationV2SyncRpcMethodsCreateResponses = {
  200: SyncRpcMethodsResponse
}

export type IntegrationV2SyncRpcMethodsCreateResponse =
  IntegrationV2SyncRpcMethodsCreateResponses[keyof IntegrationV2SyncRpcMethodsCreateResponses]

export type IntegrationV2TeamsListData = {
  body?: never
  path?: never
  query?: {
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
  }
  url: '/integration/v2/teams/'
}

export type IntegrationV2TeamsListResponses = {
  200: PaginatedOrganizationTeamShortList
}

export type IntegrationV2TeamsListResponse =
  IntegrationV2TeamsListResponses[keyof IntegrationV2TeamsListResponses]

export type IntegrationV2TeamsRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this team.
     */
    id: number
  }
  query?: never
  url: '/integration/v2/teams/{id}/'
}

export type IntegrationV2TeamsRetrieveResponses = {
  200: OrganizationTeamShort
}

export type IntegrationV2TeamsRetrieveResponse =
  IntegrationV2TeamsRetrieveResponses[keyof IntegrationV2TeamsRetrieveResponses]

export type IntegrationV2UserRetrieveData = {
  body?: never
  path: {
    sso_id: string
  }
  query?: never
  url: '/integration/v2/user/{sso_id}/'
}

export type IntegrationV2UserRetrieveResponses = {
  200: UserShortWithApplications
}

export type IntegrationV2UserRetrieveResponse =
  IntegrationV2UserRetrieveResponses[keyof IntegrationV2UserRetrieveResponses]

export type IntegrationV2UsersListData = {
  body?: never
  path?: never
  query?: {
    /**
     * List of "sso_id" to include to list
     */
    include?: Array<string>
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * Common search filter
     */
    search?: string
    /**
     * * `application` - Application
     * * `organization` - Organization
     */
    source?: 'application' | 'organization'
  }
  url: '/integration/v2/users/'
}

export type IntegrationV2UsersListResponses = {
  200: PaginatedUserShortList
}

export type IntegrationV2UsersListResponse =
  IntegrationV2UsersListResponses[keyof IntegrationV2UsersListResponses]

export type IntegrationV2UsersCreateData = {
  body: UserCreateWritable
  path?: never
  query?: never
  url: '/integration/v2/users/'
}

export type IntegrationV2UsersCreateResponses = {
  201: UserDetail
}

export type IntegrationV2UsersCreateResponse =
  IntegrationV2UsersCreateResponses[keyof IntegrationV2UsersCreateResponses]

export type IntegrationV2UsersRetrieveData = {
  body?: never
  path: {
    sso_id: string
  }
  query?: never
  url: '/integration/v2/users/{sso_id}/'
}

export type IntegrationV2UsersRetrieveResponses = {
  200: UserDetail
}

export type IntegrationV2UsersRetrieveResponse =
  IntegrationV2UsersRetrieveResponses[keyof IntegrationV2UsersRetrieveResponses]

export type IntegrationV2UsersPartialUpdateData = {
  body?: PatchedIntegrationPatchUser
  path: {
    sso_id: string
  }
  query?: never
  url: '/integration/v2/users/{sso_id}/'
}

export type IntegrationV2UsersPartialUpdateResponses = {
  200: UserDetail
}

export type IntegrationV2UsersPartialUpdateResponse =
  IntegrationV2UsersPartialUpdateResponses[keyof IntegrationV2UsersPartialUpdateResponses]

export type IntegrationV2UsersInviteCreateData = {
  body: AppUserInvite
  path?: never
  query?: {
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
  }
  url: '/integration/v2/users/invite/'
}

export type IntegrationV2UsersInviteCreateResponses = {
  200: PaginatedOrganizationUsersList
}

export type IntegrationV2UsersInviteCreateResponse =
  IntegrationV2UsersInviteCreateResponses[keyof IntegrationV2UsersInviteCreateResponses]

export type InternalGoogleMailTokenCreateData = {
  body: GoogleMailTokenRequest
  path?: never
  query?: never
  url: '/internal/google/mail/token/'
}

export type InternalGoogleMailTokenCreateErrors = {
  400: GoogleMailTokenError
}

export type InternalGoogleMailTokenCreateError =
  InternalGoogleMailTokenCreateErrors[keyof InternalGoogleMailTokenCreateErrors]

export type InternalGoogleMailTokenCreateResponses = {
  200: GoogleMailTokenResponse
}

export type InternalGoogleMailTokenCreateResponse =
  InternalGoogleMailTokenCreateResponses[keyof InternalGoogleMailTokenCreateResponses]

export type InternalMcpConnectionRetrieveData = {
  body?: never
  path: {
    cid: string
  }
  query?: never
  url: '/internal/mcp/connections/{cid}/'
}

export type InternalMcpConnectionRetrieveResponses = {
  200: McpConnectionStatus
}

export type InternalMcpConnectionRetrieveResponse =
  InternalMcpConnectionRetrieveResponses[keyof InternalMcpConnectionRetrieveResponses]

export type InternalMicrosoftGraphTokenCreateData = {
  body: MicrosoftGraphTokenRequest
  path?: never
  query?: never
  url: '/internal/microsoft/graph/token/'
}

export type InternalMicrosoftGraphTokenCreateErrors = {
  400: MicrosoftGraphTokenError
}

export type InternalMicrosoftGraphTokenCreateError =
  InternalMicrosoftGraphTokenCreateErrors[keyof InternalMicrosoftGraphTokenCreateErrors]

export type InternalMicrosoftGraphTokenCreateResponses = {
  200: MicrosoftGraphTokenResponse
}

export type InternalMicrosoftGraphTokenCreateResponse =
  InternalMicrosoftGraphTokenCreateResponses[keyof InternalMicrosoftGraphTokenCreateResponses]

export type InternalPublicAccessCredentialsSnapshotRetrieveData = {
  body?: never
  path: {
    credential_id: string
  }
  query: {
    app_code: string
  }
  url: '/internal/public-access/credentials/{credential_id}/snapshot/'
}

export type InternalPublicAccessCredentialsSnapshotRetrieveResponses = {
  200: PublicAccessSnapshot
}

export type InternalPublicAccessCredentialsSnapshotRetrieveResponse =
  InternalPublicAccessCredentialsSnapshotRetrieveResponses[keyof InternalPublicAccessCredentialsSnapshotRetrieveResponses]

export type InternalPublicAccessCredentialsUsageCreateData = {
  body: PublicAccessUsage
  path: {
    credential_id: string
  }
  query?: never
  url: '/internal/public-access/credentials/{credential_id}/usage/'
}

export type InternalPublicAccessCredentialsUsageCreateResponses = {
  /**
   * No response body
   */
  204: void
}

export type InternalPublicAccessCredentialsUsageCreateResponse =
  InternalPublicAccessCredentialsUsageCreateResponses[keyof InternalPublicAccessCredentialsUsageCreateResponses]

export type McpMyConnectionsListData = {
  body?: never
  path?: never
  query: {
    organization_id: number
  }
  url: '/mcp-access/me/connections/'
}

export type McpMyConnectionsListResponses = {
  200: Array<McpConnection>
}

export type McpMyConnectionsListResponse =
  McpMyConnectionsListResponses[keyof McpMyConnectionsListResponses]

export type McpMyConnectionsRevokeData = {
  body?: McpConnectionRevoke
  path: {
    id: string
  }
  query?: never
  url: '/mcp-access/me/connections/{id}/revoke/'
}

export type McpMyConnectionsRevokeResponses = {
  200: McpConnection
}

export type McpMyConnectionsRevokeResponse =
  McpMyConnectionsRevokeResponses[keyof McpMyConnectionsRevokeResponses]

export type McpMyConnectionsScopesUpdateData = {
  body: McpConnectionScopesUpdate
  path: {
    id: string
  }
  query?: never
  url: '/mcp-access/me/connections/{id}/scopes/'
}

export type McpMyConnectionsScopesUpdateResponses = {
  200: McpConnection
}

export type McpMyConnectionsScopesUpdateResponse =
  McpMyConnectionsScopesUpdateResponses[keyof McpMyConnectionsScopesUpdateResponses]

export type McpMyServicesListData = {
  body?: never
  path?: never
  query: {
    organization_id: number
  }
  url: '/mcp-access/me/services/'
}

export type McpMyServicesListResponses = {
  200: Array<McpMemberService>
}

export type McpMyServicesListResponse =
  McpMyServicesListResponses[keyof McpMyServicesListResponses]

export type McpPersonalTokensCreateData = {
  body: McpPersonalTokenCreate
  path?: never
  query?: never
  url: '/mcp-access/me/tokens/'
}

export type McpPersonalTokensCreateResponses = {
  201: McpPersonalTokenCreated
}

export type McpPersonalTokensCreateResponse =
  McpPersonalTokensCreateResponses[keyof McpPersonalTokensCreateResponses]

export type McpToolsRetrieveData = {
  body?: never
  path?: never
  query?: never
  url: '/mcp/tools/'
}

export type McpToolsRetrieveResponses = {
  /**
   * No response body
   */
  200: unknown
}

export type McpToolsCallCreateData = {
  body?: never
  path: {
    tool_name: string
  }
  query?: never
  url: '/mcp/tools/{tool_name}/call/'
}

export type McpToolsCallCreateResponses = {
  /**
   * No response body
   */
  200: unknown
}

export type MfaConfigRetrieveData = {
  body?: never
  path?: never
  query?: never
  url: '/mfa/config/'
}

export type MfaConfigRetrieveResponses = {
  200: MfaConfig
}

export type MfaConfigRetrieveResponse =
  MfaConfigRetrieveResponses[keyof MfaConfigRetrieveResponses]

export type MfaConfigPartialUpdateData = {
  body?: PatchedMfaConfigCreate
  path?: never
  query?: never
  url: '/mfa/config/'
}

export type MfaConfigPartialUpdateResponses = {
  200: MfaConfigCreate
}

export type MfaConfigPartialUpdateResponse =
  MfaConfigPartialUpdateResponses[keyof MfaConfigPartialUpdateResponses]

export type MfaConfigUpdateData = {
  body?: MfaConfigCreate
  path?: never
  query?: never
  url: '/mfa/config/'
}

export type MfaConfigUpdateResponses = {
  200: MfaConfigCreate
}

export type MfaConfigUpdateResponse =
  MfaConfigUpdateResponses[keyof MfaConfigUpdateResponses]

export type MfaConfigDownloadBackupCodesCreateData = {
  body?: MfaConfigWritable
  path?: never
  query?: never
  url: '/mfa/config/download-backup-codes/'
}

export type MfaConfigDownloadBackupCodesCreateResponses = {
  201: MfaConfig
}

export type MfaConfigDownloadBackupCodesCreateResponse =
  MfaConfigDownloadBackupCodesCreateResponses[keyof MfaConfigDownloadBackupCodesCreateResponses]

export type MfaConfigGenerateBackupCodesCreateData = {
  body?: MfaConfigGenerateBackupCodes
  path?: never
  query?: never
  url: '/mfa/config/generate-backup-codes/'
}

export type MfaConfigGenerateBackupCodesCreateResponses = {
  201: MfaConfigGenerateBackupCodes
}

export type MfaConfigGenerateBackupCodesCreateResponse =
  MfaConfigGenerateBackupCodesCreateResponses[keyof MfaConfigGenerateBackupCodesCreateResponses]

export type MfaInternalAuditLogsListData = {
  body?: never
  path?: never
  query?: {
    /**
     * Number of results to return per page.
     */
    limit?: number
    /**
     * The initial index from which to return the results.
     */
    offset?: number
  }
  url: '/mfa/internal/audit-logs/'
}

export type MfaInternalAuditLogsListResponses = {
  200: PaginatedAuditLogListList
}

export type MfaInternalAuditLogsListResponse =
  MfaInternalAuditLogsListResponses[keyof MfaInternalAuditLogsListResponses]

export type MfaInternalAuditLogsExportCreateData = {
  body: AuditLogListWritable
  path?: never
  query?: never
  url: '/mfa/internal/audit-logs/export/'
}

export type MfaInternalAuditLogsExportCreateResponses = {
  200: AuditLogList
}

export type MfaInternalAuditLogsExportCreateResponse =
  MfaInternalAuditLogsExportCreateResponses[keyof MfaInternalAuditLogsExportCreateResponses]

export type MfaInternalConfigsListData = {
  body?: never
  path?: never
  query?: {
    enabled?: boolean
    /**
     * Which field to use when ordering the results.
     */
    ordering?: string
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    preferred_method?: string
    /**
     * A search term.
     */
    search?: string
    user?: string
  }
  url: '/mfa/internal/configs/'
}

export type MfaInternalConfigsListResponses = {
  200: PaginatedInternalMfaConfigList
}

export type MfaInternalConfigsListResponse =
  MfaInternalConfigsListResponses[keyof MfaInternalConfigsListResponses]

export type MfaInternalConfigsRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this mfa config.
     */
    id: number
  }
  query?: never
  url: '/mfa/internal/configs/{id}/'
}

export type MfaInternalConfigsRetrieveResponses = {
  200: InternalMfaConfig
}

export type MfaInternalConfigsRetrieveResponse =
  MfaInternalConfigsRetrieveResponses[keyof MfaInternalConfigsRetrieveResponses]

export type MfaInternalConfigsPartialUpdateData = {
  body?: PatchedInternalMfaConfigWritable
  path: {
    /**
     * A unique integer value identifying this mfa config.
     */
    id: number
  }
  query?: never
  url: '/mfa/internal/configs/{id}/'
}

export type MfaInternalConfigsPartialUpdateResponses = {
  200: InternalMfaConfig
}

export type MfaInternalConfigsPartialUpdateResponse =
  MfaInternalConfigsPartialUpdateResponses[keyof MfaInternalConfigsPartialUpdateResponses]

export type MfaInternalConfigsUpdateData = {
  body?: InternalMfaConfigWritable
  path: {
    /**
     * A unique integer value identifying this mfa config.
     */
    id: number
  }
  query?: never
  url: '/mfa/internal/configs/{id}/'
}

export type MfaInternalConfigsUpdateResponses = {
  200: InternalMfaConfig
}

export type MfaInternalConfigsUpdateResponse =
  MfaInternalConfigsUpdateResponses[keyof MfaInternalConfigsUpdateResponses]

export type MfaInternalLockoutsListData = {
  body?: never
  path?: never
  query?: {
    locked_until_gte?: string
    locked_until_lte?: string
    lockout_type?: string
    /**
     * Which field to use when ordering the results.
     */
    ordering?: string
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * A search term.
     */
    search?: string
    user?: string
  }
  url: '/mfa/internal/lockouts/'
}

export type MfaInternalLockoutsListResponses = {
  200: PaginatedInternalMfaLockoutList
}

export type MfaInternalLockoutsListResponse =
  MfaInternalLockoutsListResponses[keyof MfaInternalLockoutsListResponses]

export type MfaInternalLockoutsDestroyData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this mfa lockout.
     */
    id: number
  }
  query?: never
  url: '/mfa/internal/lockouts/{id}/'
}

export type MfaInternalLockoutsDestroyResponses = {
  /**
   * No response body
   */
  204: void
}

export type MfaInternalLockoutsDestroyResponse =
  MfaInternalLockoutsDestroyResponses[keyof MfaInternalLockoutsDestroyResponses]

export type MfaSendOtpCreateData = {
  body?: MfaSendOtp
  path?: never
  query?: never
  url: '/mfa/send-otp/'
}

export type MfaSendOtpCreateResponses = {
  201: MfaSendOtp
}

export type MfaSendOtpCreateResponse =
  MfaSendOtpCreateResponses[keyof MfaSendOtpCreateResponses]

export type MfaTrustedDevicesListData = {
  body?: never
  path?: never
  query?: {
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * Comma-separated list of IDs to return first (in the given order), before the default ordering.
     */
    preference?: string
  }
  url: '/mfa/trusted-devices/'
}

export type MfaTrustedDevicesListResponses = {
  200: PaginatedTrustedDeviceList
}

export type MfaTrustedDevicesListResponse =
  MfaTrustedDevicesListResponses[keyof MfaTrustedDevicesListResponses]

export type MfaTrustedDevicesDestroyData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this trusted device.
     */
    id: number
  }
  query?: never
  url: '/mfa/trusted-devices/{id}/'
}

export type MfaTrustedDevicesDestroyResponses = {
  /**
   * No response body
   */
  204: void
}

export type MfaTrustedDevicesDestroyResponse =
  MfaTrustedDevicesDestroyResponses[keyof MfaTrustedDevicesDestroyResponses]

export type MfaTrustedDevicesDeleteAllDestroyData = {
  body?: never
  path?: never
  query?: never
  url: '/mfa/trusted-devices/delete-all/'
}

export type MfaTrustedDevicesDeleteAllDestroyResponses = {
  /**
   * No response body
   */
  204: void
}

export type MfaTrustedDevicesDeleteAllDestroyResponse =
  MfaTrustedDevicesDeleteAllDestroyResponses[keyof MfaTrustedDevicesDeleteAllDestroyResponses]

export type MfaVerifyBackupCodeCreateData = {
  body: MfaVerifyBackupCode
  path?: never
  query?: never
  url: '/mfa/verify-backup-code/'
}

export type MfaVerifyBackupCodeCreateResponses = {
  201: MfaVerifyBackupCode
}

export type MfaVerifyBackupCodeCreateResponse =
  MfaVerifyBackupCodeCreateResponses[keyof MfaVerifyBackupCodeCreateResponses]

export type MfaVerifyOtpCreateData = {
  body: MfaVerifyOtp
  path?: never
  query?: never
  url: '/mfa/verify-otp/'
}

export type MfaVerifyOtpCreateResponses = {
  201: MfaVerifyOtp
}

export type MfaVerifyOtpCreateResponse =
  MfaVerifyOtpCreateResponses[keyof MfaVerifyOtpCreateResponses]

export type OnboardingInternalStepsCreateData = {
  body: AppStepReport
  path?: never
  query?: never
  url: '/onboarding/internal/steps/'
}

export type OnboardingInternalStepsCreateResponses = {
  200: {
    [key: string]: unknown
  }
}

export type OnboardingInternalStepsCreateResponse =
  OnboardingInternalStepsCreateResponses[keyof OnboardingInternalStepsCreateResponses]

export type OnboardingJourneysRetrieveData = {
  body?: never
  path: {
    scope: string
  }
  query?: {
    /**
     * Workspace this call is about. Falls back to the token's organization, then to the user's only one.
     */
    organization?: number
  }
  url: '/onboarding/journeys/{scope}/'
}

export type OnboardingJourneysRetrieveResponses = {
  200: OnboardingJourney
}

export type OnboardingJourneysRetrieveResponse =
  OnboardingJourneysRetrieveResponses[keyof OnboardingJourneysRetrieveResponses]

export type OnboardingJourneysPartialUpdateData = {
  body?: PatchedJourneyAction
  path: {
    scope: string
  }
  query?: {
    /**
     * Workspace this call is about. Falls back to the token's organization, then to the user's only one.
     */
    organization?: number
  }
  url: '/onboarding/journeys/{scope}/'
}

export type OnboardingJourneysPartialUpdateResponses = {
  200: OnboardingJourney
}

export type OnboardingJourneysPartialUpdateResponse =
  OnboardingJourneysPartialUpdateResponses[keyof OnboardingJourneysPartialUpdateResponses]

export type OnboardingProfileRetrieveData = {
  body?: never
  path?: never
  query?: {
    /**
     * Workspace this call is about. Falls back to the token's organization, then to the user's only one.
     */
    organization?: number
  }
  url: '/onboarding/profile/'
}

export type OnboardingProfileRetrieveResponses = {
  200: OnboardingProfileResponse
}

export type OnboardingProfileRetrieveResponse =
  OnboardingProfileRetrieveResponses[keyof OnboardingProfileRetrieveResponses]

export type OnboardingProfilePartialUpdateData = {
  body?: PatchedOnboardingProfileWritable
  path?: never
  query?: {
    /**
     * Workspace this call is about. Falls back to the token's organization, then to the user's only one.
     */
    organization?: number
  }
  url: '/onboarding/profile/'
}

export type OnboardingProfilePartialUpdateResponses = {
  200: OnboardingProfileResponse
}

export type OnboardingProfilePartialUpdateResponse =
  OnboardingProfilePartialUpdateResponses[keyof OnboardingProfilePartialUpdateResponses]

export type OnboardingProfileCreateData = {
  body: OnboardingProfileWritable
  path?: never
  query?: {
    /**
     * Workspace this call is about. Falls back to the token's organization, then to the user's only one.
     */
    organization?: number
  }
  url: '/onboarding/profile/'
}

export type OnboardingProfileCreateErrors = {
  409: OnboardingProfileResponse
}

export type OnboardingProfileCreateError =
  OnboardingProfileCreateErrors[keyof OnboardingProfileCreateErrors]

export type OnboardingProfileCreateResponses = {
  201: OnboardingProfileResponse
}

export type OnboardingProfileCreateResponse =
  OnboardingProfileCreateResponses[keyof OnboardingProfileCreateResponses]

export type OrganizationsAuditArchivesListData = {
  body?: never
  path: {
    organization_id: number
  }
  query?: {
    /**
     * Include app-scoped archives too (default: only org-wide).
     */
    all?: boolean
    application?: number
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * Comma-separated list of IDs to return first (in the given order), before the default ordering.
     */
    preference?: string
    /**
     * * `pending` - Pending
     * * `processing` - Processing
     * * `ready` - Ready
     * * `failed` - Failed
     */
    status?: 'failed' | 'pending' | 'processing' | 'ready'
    /**
     * * `periodic` - Periodic archive
     * * `export` - User export
     */
    type?: 'export' | 'periodic'
  }
  url: '/organizations/{organization_id}/audit-archives/'
}

export type OrganizationsAuditArchivesListResponses = {
  200: PaginatedAuditLogArchiveList
}

export type OrganizationsAuditArchivesListResponse =
  OrganizationsAuditArchivesListResponses[keyof OrganizationsAuditArchivesListResponses]

export type OrganizationsAuditArchivesCreateData = {
  body: AuditLogArchiveCreate
  path: {
    organization_id: number
  }
  query?: never
  url: '/organizations/{organization_id}/audit-archives/'
}

export type OrganizationsAuditArchivesCreateResponses = {
  201: AuditLogArchive
}

export type OrganizationsAuditArchivesCreateResponse =
  OrganizationsAuditArchivesCreateResponses[keyof OrganizationsAuditArchivesCreateResponses]

export type OrganizationsAuditArchivesDestroyData = {
  body?: never
  path: {
    id: number
    organization_id: number
  }
  query?: never
  url: '/organizations/{organization_id}/audit-archives/{id}/'
}

export type OrganizationsAuditArchivesDestroyResponses = {
  /**
   * No response body
   */
  204: void
}

export type OrganizationsAuditArchivesDestroyResponse =
  OrganizationsAuditArchivesDestroyResponses[keyof OrganizationsAuditArchivesDestroyResponses]

export type OrganizationsAuditArchivesRetrieveData = {
  body?: never
  path: {
    id: number
    organization_id: number
  }
  query?: never
  url: '/organizations/{organization_id}/audit-archives/{id}/'
}

export type OrganizationsAuditArchivesRetrieveResponses = {
  200: AuditLogArchive
}

export type OrganizationsAuditArchivesRetrieveResponse =
  OrganizationsAuditArchivesRetrieveResponses[keyof OrganizationsAuditArchivesRetrieveResponses]

export type OrganizationsAuditArchivesDownloadRetrieveData = {
  body?: never
  path: {
    id: number
    organization_id: number
  }
  query?: never
  url: '/organizations/{organization_id}/audit-archives/{id}/download/'
}

export type OrganizationsAuditArchivesDownloadRetrieveErrors = {
  409: ArchiveNotReady
}

export type OrganizationsAuditArchivesDownloadRetrieveError =
  OrganizationsAuditArchivesDownloadRetrieveErrors[keyof OrganizationsAuditArchivesDownloadRetrieveErrors]

export type OrganizationsAuditArchivesDownloadRetrieveResponses = {
  200: ArchiveDownloadUrl
}

export type OrganizationsAuditArchivesDownloadRetrieveResponse =
  OrganizationsAuditArchivesDownloadRetrieveResponses[keyof OrganizationsAuditArchivesDownloadRetrieveResponses]

export type OrganizationsAuditArchivesBulkDeleteDestroyData = {
  body?: never
  path: {
    organization_id: number
  }
  query: {
    ids: Array<number>
  }
  url: '/organizations/{organization_id}/audit-archives/bulk-delete/'
}

export type OrganizationsAuditArchivesBulkDeleteDestroyResponses = {
  /**
   * No response body
   */
  204: void
}

export type OrganizationsAuditArchivesBulkDeleteDestroyResponse =
  OrganizationsAuditArchivesBulkDeleteDestroyResponses[keyof OrganizationsAuditArchivesBulkDeleteDestroyResponses]

export type OrganizationsAuditLogsListData = {
  body?: never
  headers?: {
    /**
     * Language used to resolve description. Falls back to the stored description.
     */
    'Accept-Language'?: 'en' | 'it' | 'ro' | 'ru'
  }
  path: {
    /**
     * Organization ID.
     */
    organization_id: number
  }
  query?: {
    /**
     * Filter by action.
     */
    action?: string
    /**
     * Filter by actor ID.
     */
    actor_id?: string
    /**
     * Filter by the kind of principal: a person, an agent, an API key, or the platform.
     *
     * * `user` - user
     * * `agent` - agent
     * * `api_key` - api_key
     * * `system` - system
     */
    actor_type?: 'user' | 'agent' | 'api_key' | 'system'
    /**
     * Filter by application ID.
     */
    application_id?: number
    /**
     * Filter by category.
     */
    category?: string
    /**
     * Filter by channel.
     */
    channel?: string
    /**
     * Filter by entity ID.
     */
    entity_id?: string
    /**
     * Filter by entity type.
     */
    entity_type?: string
    /**
     * Filter by event type.
     */
    event_type?: string
    /**
     * Number of results to return per page.
     */
    limit?: number
    /**
     * The initial index from which to return the results.
     */
    offset?: number
    /**
     * Case-insensitive search over descriptions, actor email, and actor full name.
     */
    search?: string
    /**
     * Filter by service name.
     */
    service?: string
    /**
     * Filter by severity.
     *
     * * `low` - low
     * * `medium` - medium
     * * `high` - high
     */
    severity?: 'low' | 'medium' | 'high'
    /**
     * Return logs created at or after this ISO-8601 timestamp.
     */
    timestamp_gte?: string
    /**
     * Return logs created at or before this ISO-8601 timestamp.
     */
    timestamp_lte?: string
  }
  url: '/organizations/{organization_id}/audit-logs/'
}

export type OrganizationsAuditLogsListResponses = {
  200: PaginatedAuditLogListList
}

export type OrganizationsAuditLogsListResponse =
  OrganizationsAuditLogsListResponses[keyof OrganizationsAuditLogsListResponses]

export type OrganizationsAuditLogsRetrieveData = {
  body?: never
  headers?: {
    /**
     * Language used to resolve description. Falls back to the stored description.
     */
    'Accept-Language'?: 'en' | 'it' | 'ro' | 'ru'
  }
  path: {
    /**
     * Audit log ObjectId.
     */
    id: string
    /**
     * Organization ID.
     */
    organization_id: number
  }
  query?: never
  url: '/organizations/{organization_id}/audit-logs/{id}/'
}

export type OrganizationsAuditLogsRetrieveResponses = {
  200: AuditLogDetail
}

export type OrganizationsAuditLogsRetrieveResponse =
  OrganizationsAuditLogsRetrieveResponses[keyof OrganizationsAuditLogsRetrieveResponses]

export type OrganizationsAuditLogsFiltersRetrieveData = {
  body?: never
  path: {
    /**
     * Organization ID.
     */
    organization_id: number
  }
  query: {
    /**
     * Scope the values to a single application.
     */
    application_id?: number
    /**
     * Which field's distinct values to return.
     */
    field:
      | 'action'
      | 'actor_type'
      | 'category'
      | 'channel'
      | 'entity_type'
      | 'event_type'
      | 'service'
      | 'severity'
    /**
     * Case-insensitive substring filter on the returned values.
     */
    search?: string
  }
  url: '/organizations/{organization_id}/audit-logs/filters/'
}

export type OrganizationsAuditLogsFiltersRetrieveResponses = {
  200: AuditLogFilterOptions
}

export type OrganizationsAuditLogsFiltersRetrieveResponse =
  OrganizationsAuditLogsFiltersRetrieveResponses[keyof OrganizationsAuditLogsFiltersRetrieveResponses]

export type OrganizationsAuditLogsSignInActivityRetrieveData = {
  body?: never
  path: {
    organization_id: number
  }
  query?: {
    application_id?: number
    days?: number
    include_comparison?: boolean
    service?: string
    timezone?: string
  }
  url: '/organizations/{organization_id}/audit-logs/sign-in-activity/'
}

export type OrganizationsAuditLogsSignInActivityRetrieveResponses = {
  200: AuditLogSignInActivity
}

export type OrganizationsAuditLogsSignInActivityRetrieveResponse =
  OrganizationsAuditLogsSignInActivityRetrieveResponses[keyof OrganizationsAuditLogsSignInActivityRetrieveResponses]

export type OrganizationsJoinRequestsListData = {
  body?: never
  path: {
    organization_id: number
  }
  query?: {
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * Match by requester email or name (case-insensitive).
     */
    search?: string
    /**
     * Filter by state. Omitted: every request, newest first.
     */
    status?: 'approved' | 'pending' | 'rejected'
  }
  url: '/organizations/{organization_id}/join-requests/'
}

export type OrganizationsJoinRequestsListResponses = {
  200: PaginatedOrganizationJoinRequestList
}

export type OrganizationsJoinRequestsListResponse =
  OrganizationsJoinRequestsListResponses[keyof OrganizationsJoinRequestsListResponses]

export type OrganizationsJoinRequestsCreateData = {
  body?: never
  path: {
    organization_id: number
  }
  query?: never
  url: '/organizations/{organization_id}/join-requests/'
}

export type OrganizationsJoinRequestsCreateResponses = {
  202: OrganizationJoinRequest
}

export type OrganizationsJoinRequestsCreateResponse =
  OrganizationsJoinRequestsCreateResponses[keyof OrganizationsJoinRequestsCreateResponses]

export type OrganizationsJoinRequestsApproveCreateData = {
  body?: never
  path: {
    id: number
    organization_id: number
  }
  query?: never
  url: '/organizations/{organization_id}/join-requests/{id}/approve/'
}

export type OrganizationsJoinRequestsApproveCreateResponses = {
  200: OrganizationJoinRequest
}

export type OrganizationsJoinRequestsApproveCreateResponse =
  OrganizationsJoinRequestsApproveCreateResponses[keyof OrganizationsJoinRequestsApproveCreateResponses]

export type OrganizationsJoinRequestsRejectCreateData = {
  body?: never
  path: {
    id: number
    organization_id: number
  }
  query?: never
  url: '/organizations/{organization_id}/join-requests/{id}/reject/'
}

export type OrganizationsJoinRequestsRejectCreateResponses = {
  200: OrganizationJoinRequest
}

export type OrganizationsJoinRequestsRejectCreateResponse =
  OrganizationsJoinRequestsRejectCreateResponses[keyof OrganizationsJoinRequestsRejectCreateResponses]

export type OrganizationsAccessListData = {
  body?: never
  path?: never
  query?: {
    joined_date_after?: string
    joined_date_before?: string
    /**
     * Which field to use when ordering the results.
     */
    ordering?: string
    organization?: number
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * Comma-separated list of IDs to return first (in the given order), before the default ordering.
     */
    preference?: string
    /**
     * A search term.
     */
    search?: string
    /**
     * * `online` - Online
     * * `offline` - Offline
     * * `invited` - Invited
     * * `all` - All
     * * `active` - Active
     * * `inactive` - Inactive
     * * `blocked` - Blocked
     */
    status?:
      | 'active'
      | 'all'
      | 'blocked'
      | 'inactive'
      | 'invited'
      | 'offline'
      | 'online'
    team?: Array<number>
    user?: number
    /**
     * Multiple values may be separated by commas.
     */
    user_preference?: Array<number>
  }
  url: '/organizations/access/'
}

export type OrganizationsAccessListResponses = {
  200: PaginatedOrganizationUsersList
}

export type OrganizationsAccessListResponse =
  OrganizationsAccessListResponses[keyof OrganizationsAccessListResponses]

export type OrganizationsAccessDestroyData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this organization access.
     */
    id: number
  }
  query?: never
  url: '/organizations/access/{id}/'
}

export type OrganizationsAccessDestroyResponses = {
  /**
   * No response body
   */
  204: void
}

export type OrganizationsAccessDestroyResponse =
  OrganizationsAccessDestroyResponses[keyof OrganizationsAccessDestroyResponses]

export type OrganizationsAccessRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this organization access.
     */
    id: number
  }
  query?: never
  url: '/organizations/access/{id}/'
}

export type OrganizationsAccessRetrieveResponses = {
  200: OrganizationUsers
}

export type OrganizationsAccessRetrieveResponse =
  OrganizationsAccessRetrieveResponses[keyof OrganizationsAccessRetrieveResponses]

export type OrganizationsAccessPartialUpdateData = {
  body?: PatchedOrganizationAccessWritable
  path: {
    /**
     * A unique integer value identifying this organization access.
     */
    id: number
  }
  query?: never
  url: '/organizations/access/{id}/'
}

export type OrganizationsAccessPartialUpdateResponses = {
  200: OrganizationAccess
}

export type OrganizationsAccessPartialUpdateResponse =
  OrganizationsAccessPartialUpdateResponses[keyof OrganizationsAccessPartialUpdateResponses]

export type OrganizationsAccessBlockCreateData = {
  body: OrganizationAccessBlock
  path: {
    /**
     * A unique integer value identifying this organization access.
     */
    id: number
  }
  query?: never
  url: '/organizations/access/{id}/block/'
}

export type OrganizationsAccessBlockCreateResponses = {
  200: OrganizationUsers
}

export type OrganizationsAccessBlockCreateResponse =
  OrganizationsAccessBlockCreateResponses[keyof OrganizationsAccessBlockCreateResponses]

export type OrganizationsAccessDisableMfaCreateData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this organization access.
     */
    id: number
  }
  query?: never
  url: '/organizations/access/{id}/disable-mfa/'
}

export type OrganizationsAccessDisableMfaCreateResponses = {
  200: OrganizationUsers
}

export type OrganizationsAccessDisableMfaCreateResponse =
  OrganizationsAccessDisableMfaCreateResponses[keyof OrganizationsAccessDisableMfaCreateResponses]

export type OrganizationsAccessImpersonateCreateData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this organization access.
     */
    id: number
  }
  query?: never
  url: '/organizations/access/{id}/impersonate/'
}

export type OrganizationsAccessImpersonateCreateResponses = {
  200: ImpersonateResponse
}

export type OrganizationsAccessImpersonateCreateResponse =
  OrganizationsAccessImpersonateCreateResponses[keyof OrganizationsAccessImpersonateCreateResponses]

export type OrganizationsAccessRequireMfaCreateData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this organization access.
     */
    id: number
  }
  query?: never
  url: '/organizations/access/{id}/require-mfa/'
}

export type OrganizationsAccessRequireMfaCreateResponses = {
  200: OrganizationUsers
}

export type OrganizationsAccessRequireMfaCreateResponse =
  OrganizationsAccessRequireMfaCreateResponses[keyof OrganizationsAccessRequireMfaCreateResponses]

export type OrganizationsAccessResendInviteCreateData = {
  body: ResendInvite
  path: {
    /**
     * A unique integer value identifying this organization access.
     */
    id: number
  }
  query?: never
  url: '/organizations/access/{id}/resend-invite/'
}

export type OrganizationsAccessResendInviteCreateResponses = {
  200: OrganizationUsers
}

export type OrganizationsAccessResendInviteCreateResponse =
  OrganizationsAccessResendInviteCreateResponses[keyof OrganizationsAccessResendInviteCreateResponses]

export type OrganizationsAccessResetMfaCreateData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this organization access.
     */
    id: number
  }
  query?: never
  url: '/organizations/access/{id}/reset-mfa/'
}

export type OrganizationsAccessResetMfaCreateResponses = {
  200: OrganizationUsers
}

export type OrganizationsAccessResetMfaCreateResponse =
  OrganizationsAccessResetMfaCreateResponses[keyof OrganizationsAccessResetMfaCreateResponses]

export type OrganizationsAccessResetPasswordCreateData = {
  body: AdminResetPassword
  path: {
    /**
     * A unique integer value identifying this organization access.
     */
    id: number
  }
  query?: never
  url: '/organizations/access/{id}/reset-password/'
}

export type OrganizationsAccessResetPasswordCreateResponses = {
  200: AdminResetPasswordResponse
}

export type OrganizationsAccessResetPasswordCreateResponse =
  OrganizationsAccessResetPasswordCreateResponses[keyof OrganizationsAccessResetPasswordCreateResponses]

export type OrganizationsAccessRevokeInviteCreateData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this organization access.
     */
    id: number
  }
  query?: never
  url: '/organizations/access/{id}/revoke-invite/'
}

export type OrganizationsAccessRevokeInviteCreateResponses = {
  200: OrganizationUsers
}

export type OrganizationsAccessRevokeInviteCreateResponse =
  OrganizationsAccessRevokeInviteCreateResponses[keyof OrganizationsAccessRevokeInviteCreateResponses]

export type OrganizationsAccessRolesCreateData = {
  body: AssignUserRoles
  path: {
    /**
     * A unique integer value identifying this organization access.
     */
    id: number
  }
  query?: never
  url: '/organizations/access/{id}/roles/'
}

export type OrganizationsAccessRolesCreateResponses = {
  /**
   * No response body
   */
  204: void
}

export type OrganizationsAccessRolesCreateResponse =
  OrganizationsAccessRolesCreateResponses[keyof OrganizationsAccessRolesCreateResponses]

export type OrganizationsAccessSessionsListData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this organization access.
     */
    id: number
  }
  query?: {
    joined_date_after?: string
    joined_date_before?: string
    /**
     * Which field to use when ordering the results.
     */
    ordering?: string
    organization?: number
    /**
     * Comma-separated list of IDs to return first (in the given order), before the default ordering.
     */
    preference?: string
    /**
     * A search term.
     */
    search?: string
    /**
     * * `online` - Online
     * * `offline` - Offline
     * * `invited` - Invited
     * * `all` - All
     * * `active` - Active
     * * `inactive` - Inactive
     * * `blocked` - Blocked
     */
    status?:
      | 'active'
      | 'all'
      | 'blocked'
      | 'inactive'
      | 'invited'
      | 'offline'
      | 'online'
    team?: Array<number>
    user?: number
    /**
     * Multiple values may be separated by commas.
     */
    user_preference?: Array<number>
  }
  url: '/organizations/access/{id}/sessions/'
}

export type OrganizationsAccessSessionsListResponses = {
  200: Array<Session>
}

export type OrganizationsAccessSessionsListResponse =
  OrganizationsAccessSessionsListResponses[keyof OrganizationsAccessSessionsListResponses]

export type OrganizationsAccessSessionsDestroyData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this organization access.
     */
    id: number
    jti: string
  }
  query?: never
  url: '/organizations/access/{id}/sessions/{jti}/'
}

export type OrganizationsAccessSessionsDestroyResponses = {
  200: SessionRevokeResponse
}

export type OrganizationsAccessSessionsDestroyResponse =
  OrganizationsAccessSessionsDestroyResponses[keyof OrganizationsAccessSessionsDestroyResponses]

export type OrganizationsAccessSessionsRevokeAllCreateData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this organization access.
     */
    id: number
  }
  query?: never
  url: '/organizations/access/{id}/sessions/revoke-all/'
}

export type OrganizationsAccessSessionsRevokeAllCreateResponses = {
  200: SessionRevokeResponse
}

export type OrganizationsAccessSessionsRevokeAllCreateResponse =
  OrganizationsAccessSessionsRevokeAllCreateResponses[keyof OrganizationsAccessSessionsRevokeAllCreateResponses]

export type OrganizationsAccessSignInActivityRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this organization access.
     */
    id: number
  }
  query?: never
  url: '/organizations/access/{id}/sign-in-activity/'
}

export type OrganizationsAccessSignInActivityRetrieveResponses = {
  200: MemberSignInActivity
}

export type OrganizationsAccessSignInActivityRetrieveResponse =
  OrganizationsAccessSignInActivityRetrieveResponses[keyof OrganizationsAccessSignInActivityRetrieveResponses]

export type OrganizationsAccessAddCreateData = {
  body: OrganizationInviteUser
  path?: never
  query?: {
    joined_date_after?: string
    joined_date_before?: string
    /**
     * Which field to use when ordering the results.
     */
    ordering?: string
    organization?: number
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * Comma-separated list of IDs to return first (in the given order), before the default ordering.
     */
    preference?: string
    /**
     * A search term.
     */
    search?: string
    /**
     * * `online` - Online
     * * `offline` - Offline
     * * `invited` - Invited
     * * `all` - All
     * * `active` - Active
     * * `inactive` - Inactive
     * * `blocked` - Blocked
     */
    status?:
      | 'active'
      | 'all'
      | 'blocked'
      | 'inactive'
      | 'invited'
      | 'offline'
      | 'online'
    team?: Array<number>
    user?: number
    /**
     * Multiple values may be separated by commas.
     */
    user_preference?: Array<number>
  }
  url: '/organizations/access/add/'
}

export type OrganizationsAccessAddCreateResponses = {
  200: PaginatedOrganizationUsersList
}

export type OrganizationsAccessAddCreateResponse =
  OrganizationsAccessAddCreateResponses[keyof OrganizationsAccessAddCreateResponses]

export type OrganizationsAccessOrganigramaListData = {
  body?: never
  path?: never
  query: {
    head_user?: number | null
    organization: number
  }
  url: '/organizations/access/organigrama/'
}

export type OrganizationsAccessOrganigramaListResponses = {
  200: Array<OrganizationOrganigrama>
}

export type OrganizationsAccessOrganigramaListResponse =
  OrganizationsAccessOrganigramaListResponses[keyof OrganizationsAccessOrganigramaListResponses]

export type OrganizationsDomainAvailabilityRetrieveData = {
  body?: never
  path?: never
  query: {
    domain_name: string
  }
  url: '/organizations/domain-availability/'
}

export type OrganizationsDomainAvailabilityRetrieveResponses = {
  200: DomainAvailability
}

export type OrganizationsDomainAvailabilityRetrieveResponse =
  OrganizationsDomainAvailabilityRetrieveResponses[keyof OrganizationsDomainAvailabilityRetrieveResponses]

export type OrganizationsDomainMatchRetrieveData = {
  body?: never
  path?: never
  query?: never
  url: '/organizations/domain-match/'
}

export type OrganizationsDomainMatchRetrieveResponses = {
  200: DomainMatch
}

export type OrganizationsDomainMatchRetrieveResponse =
  OrganizationsDomainMatchRetrieveResponses[keyof OrganizationsDomainMatchRetrieveResponses]

export type OrganizationsInfoRetrieveData = {
  body?: never
  path: {
    domain_name: string
  }
  query?: never
  url: '/organizations/info/{domain_name}/'
}

export type OrganizationsInfoRetrieveResponses = {
  200: OrganizationShort
}

export type OrganizationsInfoRetrieveResponse =
  OrganizationsInfoRetrieveResponses[keyof OrganizationsInfoRetrieveResponses]

export type OrganizationsOrganizationsListData = {
  body?: never
  path?: never
  query?: {
    /**
     * Filter organizations that have an application with this type (e.g., 'networking')
     */
    application_type?: string
    /**
     * comma separated list
     */
    ids?: string
    /**
     * Which field to use when ordering the results.
     */
    ordering?: string
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * Comma-separated list of IDs to return first (in the given order), before the default ordering.
     */
    preference?: string
    /**
     * A search term.
     */
    search?: string
  }
  url: '/organizations/organizations/'
}

export type OrganizationsOrganizationsListResponses = {
  200: PaginatedOrganizationList
}

export type OrganizationsOrganizationsListResponse =
  OrganizationsOrganizationsListResponses[keyof OrganizationsOrganizationsListResponses]

export type OrganizationsOrganizationsCreateData = {
  body: OrganizationCreateWritable
  path?: never
  query?: never
  url: '/organizations/organizations/'
}

export type OrganizationsOrganizationsCreateResponses = {
  201: OrganizationCreate
}

export type OrganizationsOrganizationsCreateResponse =
  OrganizationsOrganizationsCreateResponses[keyof OrganizationsOrganizationsCreateResponses]

export type OrganizationsOrganizationsDestroyData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this organization.
     */
    id: number
  }
  query?: never
  url: '/organizations/organizations/{id}/'
}

export type OrganizationsOrganizationsDestroyResponses = {
  /**
   * No response body
   */
  204: void
}

export type OrganizationsOrganizationsDestroyResponse =
  OrganizationsOrganizationsDestroyResponses[keyof OrganizationsOrganizationsDestroyResponses]

export type OrganizationsOrganizationsRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this organization.
     */
    id: number
  }
  query?: never
  url: '/organizations/organizations/{id}/'
}

export type OrganizationsOrganizationsRetrieveResponses = {
  200: Organization
}

export type OrganizationsOrganizationsRetrieveResponse =
  OrganizationsOrganizationsRetrieveResponses[keyof OrganizationsOrganizationsRetrieveResponses]

export type OrganizationsOrganizationsPartialUpdateData = {
  body?: PatchedOrganizationUpdateWritable
  path: {
    /**
     * A unique integer value identifying this organization.
     */
    id: number
  }
  query?: never
  url: '/organizations/organizations/{id}/'
}

export type OrganizationsOrganizationsPartialUpdateResponses = {
  200: OrganizationUpdate
}

export type OrganizationsOrganizationsPartialUpdateResponse =
  OrganizationsOrganizationsPartialUpdateResponses[keyof OrganizationsOrganizationsPartialUpdateResponses]

export type OrganizationsOrganizationsAuthMethodsRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this organization.
     */
    id: number
  }
  query?: never
  url: '/organizations/organizations/{id}/auth-methods/'
}

export type OrganizationsOrganizationsAuthMethodsRetrieveResponses = {
  200: OrganizationAuthMethodsResponse
}

export type OrganizationsOrganizationsAuthMethodsRetrieveResponse =
  OrganizationsOrganizationsAuthMethodsRetrieveResponses[keyof OrganizationsOrganizationsAuthMethodsRetrieveResponses]

export type OrganizationsOrganizationsAuthMethodsPartialUpdateData = {
  body?: PatchedOrganizationAuthMethodsPatchWritable
  path: {
    /**
     * A unique integer value identifying this organization.
     */
    id: number
  }
  query?: never
  url: '/organizations/organizations/{id}/auth-methods/'
}

export type OrganizationsOrganizationsAuthMethodsPartialUpdateResponses = {
  200: OrganizationAuthMethodsResponse
}

export type OrganizationsOrganizationsAuthMethodsPartialUpdateResponse =
  OrganizationsOrganizationsAuthMethodsPartialUpdateResponses[keyof OrganizationsOrganizationsAuthMethodsPartialUpdateResponses]

export type OrganizationsOrganizationsCustomAuthProvidersListData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this organization.
     */
    id: number
  }
  query?: {
    /**
     * Filter organizations that have an application with this type (e.g., 'networking')
     */
    application_type?: string
    /**
     * comma separated list
     */
    ids?: string
    /**
     * Which field to use when ordering the results.
     */
    ordering?: string
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * Comma-separated list of IDs to return first (in the given order), before the default ordering.
     */
    preference?: string
    /**
     * A search term.
     */
    search?: string
  }
  url: '/organizations/organizations/{id}/custom-auth-providers/'
}

export type OrganizationsOrganizationsCustomAuthProvidersListResponses = {
  200: PaginatedOrganizationCustomAuthProviderList
}

export type OrganizationsOrganizationsCustomAuthProvidersListResponse =
  OrganizationsOrganizationsCustomAuthProvidersListResponses[keyof OrganizationsOrganizationsCustomAuthProvidersListResponses]

export type OrganizationsOrganizationsCustomAuthProvidersCreateData = {
  body: OrganizationCustomAuthProviderWriteWritable
  path: {
    /**
     * A unique integer value identifying this organization.
     */
    id: number
  }
  query?: never
  url: '/organizations/organizations/{id}/custom-auth-providers/'
}

export type OrganizationsOrganizationsCustomAuthProvidersCreateResponses = {
  201: OrganizationCustomAuthProvider
}

export type OrganizationsOrganizationsCustomAuthProvidersCreateResponse =
  OrganizationsOrganizationsCustomAuthProvidersCreateResponses[keyof OrganizationsOrganizationsCustomAuthProvidersCreateResponses]

export type OrganizationsOrganizationsCustomAuthProvidersDestroyData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this organization.
     */
    id: number
    provider_id: string
  }
  query?: never
  url: '/organizations/organizations/{id}/custom-auth-providers/{provider_id}/'
}

export type OrganizationsOrganizationsCustomAuthProvidersDestroyResponses = {
  /**
   * No response body
   */
  204: void
}

export type OrganizationsOrganizationsCustomAuthProvidersDestroyResponse =
  OrganizationsOrganizationsCustomAuthProvidersDestroyResponses[keyof OrganizationsOrganizationsCustomAuthProvidersDestroyResponses]

export type OrganizationsOrganizationsCustomAuthProvidersRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this organization.
     */
    id: number
    provider_id: string
  }
  query?: never
  url: '/organizations/organizations/{id}/custom-auth-providers/{provider_id}/'
}

export type OrganizationsOrganizationsCustomAuthProvidersRetrieveResponses = {
  200: OrganizationCustomAuthProvider
}

export type OrganizationsOrganizationsCustomAuthProvidersRetrieveResponse =
  OrganizationsOrganizationsCustomAuthProvidersRetrieveResponses[keyof OrganizationsOrganizationsCustomAuthProvidersRetrieveResponses]

export type OrganizationsOrganizationsCustomAuthProvidersPartialUpdateData = {
  body?: PatchedOrganizationCustomAuthProviderWriteWritable
  path: {
    /**
     * A unique integer value identifying this organization.
     */
    id: number
    provider_id: string
  }
  query?: never
  url: '/organizations/organizations/{id}/custom-auth-providers/{provider_id}/'
}

export type OrganizationsOrganizationsCustomAuthProvidersPartialUpdateResponses =
  {
    200: OrganizationCustomAuthProvider
  }

export type OrganizationsOrganizationsCustomAuthProvidersPartialUpdateResponse =
  OrganizationsOrganizationsCustomAuthProvidersPartialUpdateResponses[keyof OrganizationsOrganizationsCustomAuthProvidersPartialUpdateResponses]

export type OrganizationsOrganizationsDemoDisableCreateData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this organization.
     */
    id: number
  }
  query?: never
  url: '/organizations/organizations/{id}/demo/disable/'
}

export type OrganizationsOrganizationsDemoDisableCreateErrors = {
  /**
   * No response body
   */
  400: unknown
  /**
   * No response body
   */
  409: unknown
  /**
   * No response body
   */
  503: unknown
}

export type OrganizationsOrganizationsDemoDisableCreateResponses = {
  /**
   * No response body
   */
  200: unknown
  202: SetupJob
}

export type OrganizationsOrganizationsDemoDisableCreateResponse =
  OrganizationsOrganizationsDemoDisableCreateResponses[keyof OrganizationsOrganizationsDemoDisableCreateResponses]

export type OrganizationsOrganizationsDemoEnableCreateData = {
  body?: EnableDemo
  path: {
    /**
     * A unique integer value identifying this organization.
     */
    id: number
  }
  query?: never
  url: '/organizations/organizations/{id}/demo/enable/'
}

export type OrganizationsOrganizationsDemoEnableCreateErrors = {
  /**
   * No response body
   */
  400: unknown
  /**
   * No response body
   */
  409: unknown
}

export type OrganizationsOrganizationsDemoEnableCreateResponses = {
  /**
   * No response body
   */
  200: unknown
}

export type OrganizationsOrganizationsEnvironmentBuildCreateData = {
  body: EnvironmentBuild
  path: {
    /**
     * A unique integer value identifying this organization.
     */
    id: number
  }
  query?: never
  url: '/organizations/organizations/{id}/environment/build/'
}

export type OrganizationsOrganizationsEnvironmentBuildCreateErrors = {
  /**
   * No response body
   */
  400: unknown
  /**
   * No response body
   */
  403: unknown
  /**
   * No response body
   */
  503: unknown
}

export type OrganizationsOrganizationsEnvironmentBuildCreateResponses = {
  200: SetupJob
}

export type OrganizationsOrganizationsEnvironmentBuildCreateResponse =
  OrganizationsOrganizationsEnvironmentBuildCreateResponses[keyof OrganizationsOrganizationsEnvironmentBuildCreateResponses]

export type OrganizationsOrganizationsEnvironmentClearCreateData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this organization.
     */
    id: number
  }
  query?: never
  url: '/organizations/organizations/{id}/environment/clear/'
}

export type OrganizationsOrganizationsEnvironmentClearCreateErrors = {
  /**
   * No response body
   */
  400: unknown
  /**
   * No response body
   */
  403: unknown
  /**
   * No response body
   */
  503: unknown
}

export type OrganizationsOrganizationsEnvironmentClearCreateResponses = {
  200: SetupJob
}

export type OrganizationsOrganizationsEnvironmentClearCreateResponse =
  OrganizationsOrganizationsEnvironmentClearCreateResponses[keyof OrganizationsOrganizationsEnvironmentClearCreateResponses]

export type OrganizationsOrganizationsMemberAuthMethodsListData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this organization.
     */
    id: number
  }
  query?: {
    /**
     * Filter organizations that have an application with this type (e.g., 'networking')
     */
    application_type?: string
    /**
     * comma separated list
     */
    ids?: string
    /**
     * Which field to use when ordering the results.
     */
    ordering?: string
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * Comma-separated list of IDs to return first (in the given order), before the default ordering.
     */
    preference?: string
    /**
     * A search term.
     */
    search?: string
  }
  url: '/organizations/organizations/{id}/member-auth-methods/'
}

export type OrganizationsOrganizationsMemberAuthMethodsListResponses = {
  200: PaginatedOrganizationAuthMethodPublicList
}

export type OrganizationsOrganizationsMemberAuthMethodsListResponse =
  OrganizationsOrganizationsMemberAuthMethodsListResponses[keyof OrganizationsOrganizationsMemberAuthMethodsListResponses]

export type OrganizationsOrganizationsMicrosoftAppRegistrationRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this organization.
     */
    id: number
  }
  query?: never
  url: '/organizations/organizations/{id}/microsoft/app-registration/'
}

export type OrganizationsOrganizationsMicrosoftAppRegistrationRetrieveResponses =
  {
    200: OrganizationMicrosoftGraphAppCredentials
  }

export type OrganizationsOrganizationsMicrosoftAppRegistrationRetrieveResponse =
  OrganizationsOrganizationsMicrosoftAppRegistrationRetrieveResponses[keyof OrganizationsOrganizationsMicrosoftAppRegistrationRetrieveResponses]

export type OrganizationsOrganizationsMicrosoftAppRegistrationPartialUpdateData =
  {
    body?: PatchedOrganizationMicrosoftGraphAppCredentialsPatchWritable
    path: {
      /**
       * A unique integer value identifying this organization.
       */
      id: number
    }
    query?: never
    url: '/organizations/organizations/{id}/microsoft/app-registration/'
  }

export type OrganizationsOrganizationsMicrosoftAppRegistrationPartialUpdateResponses =
  {
    200: OrganizationMicrosoftGraphAppCredentials
  }

export type OrganizationsOrganizationsMicrosoftAppRegistrationPartialUpdateResponse =
  OrganizationsOrganizationsMicrosoftAppRegistrationPartialUpdateResponses[keyof OrganizationsOrganizationsMicrosoftAppRegistrationPartialUpdateResponses]

export type OrganizationsOrganizationsTransferOwnershipCreateData = {
  body: OrganizationOwnershipTransfer
  path: {
    /**
     * A unique integer value identifying this organization.
     */
    id: number
  }
  query?: never
  url: '/organizations/organizations/{id}/transfer-ownership/'
}

export type OrganizationsOrganizationsTransferOwnershipCreateResponses = {
  200: OrganizationOwnershipTransferResponse
}

export type OrganizationsOrganizationsTransferOwnershipCreateResponse =
  OrganizationsOrganizationsTransferOwnershipCreateResponses[keyof OrganizationsOrganizationsTransferOwnershipCreateResponses]

export type PermissionsListData = {
  body?: never
  path?: never
  query?: {
    /**
     * Filter permissions by per-instance Application (third-party custom apps).
     */
    application?: number
    application_repository?: number
    is_externally_grantable?: boolean
    /**
     * Which field to use when ordering the results.
     */
    ordering?: string
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * Comma-separated list of IDs to return first (in the given order), before the default ordering.
     */
    preference?: string
    /**
     * A search term.
     */
    search?: string
  }
  url: '/organizations/permissions/'
}

export type PermissionsListResponses = {
  200: PaginatedPermissionsLocalizedList
}

export type PermissionsListResponse =
  PermissionsListResponses[keyof PermissionsListResponses]

export type PermissionsCreateData = {
  body: ApplicationPermissionWritable
  path?: never
  query?: never
  url: '/organizations/permissions/'
}

export type PermissionsCreateResponses = {
  201: ApplicationPermission
}

export type PermissionsCreateResponse =
  PermissionsCreateResponses[keyof PermissionsCreateResponses]

export type PermissionsDestroyData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this permission.
     */
    id: number
  }
  query?: never
  url: '/organizations/permissions/{id}/'
}

export type PermissionsDestroyResponses = {
  /**
   * No response body
   */
  204: void
}

export type PermissionsDestroyResponse =
  PermissionsDestroyResponses[keyof PermissionsDestroyResponses]

export type PermissionsRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this permission.
     */
    id: number
  }
  query?: never
  url: '/organizations/permissions/{id}/'
}

export type PermissionsRetrieveResponses = {
  200: PermissionsLocalized
}

export type PermissionsRetrieveResponse =
  PermissionsRetrieveResponses[keyof PermissionsRetrieveResponses]

export type PermissionsPartialUpdateData = {
  body?: PatchedApplicationPermissionWritable
  path: {
    /**
     * A unique integer value identifying this permission.
     */
    id: number
  }
  query?: never
  url: '/organizations/permissions/{id}/'
}

export type PermissionsPartialUpdateResponses = {
  200: ApplicationPermission
}

export type PermissionsPartialUpdateResponse =
  PermissionsPartialUpdateResponses[keyof PermissionsPartialUpdateResponses]

export type OrganizationsPermissionsUserListData = {
  body?: never
  path?: never
  query: {
    /**
     * Filter permissions by per-instance Application (third-party custom apps).
     */
    application?: number
    application_repository?: number
    is_externally_grantable?: boolean
    /**
     * Which field to use when ordering the results.
     */
    ordering?: string
    organization: number
    /**
     * Comma-separated list of IDs to return first (in the given order), before the default ordering.
     */
    preference?: string
    /**
     * A search term.
     */
    search?: string
  }
  url: '/organizations/permissions/user/'
}

export type OrganizationsPermissionsUserListResponses = {
  200: Array<PermissionsLocalized>
}

export type OrganizationsPermissionsUserListResponse =
  OrganizationsPermissionsUserListResponses[keyof OrganizationsPermissionsUserListResponses]

export type OrganizationsRolesListData = {
  body?: never
  path?: never
  query?: {
    /**
     * Filter roles by related application. Use 'null' to filter roles without an application.
     */
    application?: number
    /**
     * comma separated list
     */
    ids?: string
    /**
     * Which field to use when ordering the results.
     */
    ordering?: string
    organization?: number
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * Comma-separated list of IDs to return first (in the given order), before the default ordering.
     */
    preference?: string
    /**
     * A search term.
     */
    search?: string
  }
  url: '/organizations/roles/'
}

export type OrganizationsRolesListResponses = {
  200: PaginatedOrganizationRoleFullList
}

export type OrganizationsRolesListResponse =
  OrganizationsRolesListResponses[keyof OrganizationsRolesListResponses]

export type OrganizationsRolesCreateData = {
  body: OrganizationRoleFullWritable
  path?: never
  query?: never
  url: '/organizations/roles/'
}

export type OrganizationsRolesCreateResponses = {
  201: OrganizationRoleFull
}

export type OrganizationsRolesCreateResponse =
  OrganizationsRolesCreateResponses[keyof OrganizationsRolesCreateResponses]

export type OrganizationsRolesDestroyData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this Organization Role.
     */
    id: number
  }
  query?: never
  url: '/organizations/roles/{id}/'
}

export type OrganizationsRolesDestroyResponses = {
  /**
   * No response body
   */
  204: void
}

export type OrganizationsRolesDestroyResponse =
  OrganizationsRolesDestroyResponses[keyof OrganizationsRolesDestroyResponses]

export type OrganizationsRolesRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this Organization Role.
     */
    id: number
  }
  query?: never
  url: '/organizations/roles/{id}/'
}

export type OrganizationsRolesRetrieveResponses = {
  200: OrganizationRoleRetrieve
}

export type OrganizationsRolesRetrieveResponse =
  OrganizationsRolesRetrieveResponses[keyof OrganizationsRolesRetrieveResponses]

export type OrganizationsRolesPartialUpdateData = {
  body?: PatchedOrganizationRoleEditWritable
  path: {
    /**
     * A unique integer value identifying this Organization Role.
     */
    id: number
  }
  query?: never
  url: '/organizations/roles/{id}/'
}

export type OrganizationsRolesPartialUpdateResponses = {
  200: OrganizationRoleEdit
}

export type OrganizationsRolesPartialUpdateResponse =
  OrganizationsRolesPartialUpdateResponses[keyof OrganizationsRolesPartialUpdateResponses]

export type OrganizationsRolesReplaceCreateData = {
  body: ReplaceOrganizationRole
  path: {
    /**
     * A unique integer value identifying this Organization Role.
     */
    id: number
  }
  query?: never
  url: '/organizations/roles/{id}/replace/'
}

export type OrganizationsRolesReplaceCreateResponses = {
  /**
   * No response body
   */
  204: void
}

export type OrganizationsRolesReplaceCreateResponse =
  OrganizationsRolesReplaceCreateResponses[keyof OrganizationsRolesReplaceCreateResponses]

export type OrganizationsTeamUsersListData = {
  body?: never
  path?: never
  query?: {
    /**
     * Which field to use when ordering the results.
     */
    ordering?: string
    organization?: number
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * Comma-separated list of IDs to return first (in the given order), before the default ordering.
     */
    preference?: string
    search?: string
    team?: number
    user?: number
  }
  url: '/organizations/team-users/'
}

export type OrganizationsTeamUsersListResponses = {
  200: PaginatedTeamUsersDetailList
}

export type OrganizationsTeamUsersListResponse =
  OrganizationsTeamUsersListResponses[keyof OrganizationsTeamUsersListResponses]

export type OrganizationsTeamUsersCreateData = {
  body: TeamUsersWritable
  path?: never
  query?: never
  url: '/organizations/team-users/'
}

export type OrganizationsTeamUsersCreateResponses = {
  201: TeamUsers
}

export type OrganizationsTeamUsersCreateResponse =
  OrganizationsTeamUsersCreateResponses[keyof OrganizationsTeamUsersCreateResponses]

export type OrganizationsTeamUsersDestroyData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this team users.
     */
    id: number
  }
  query?: never
  url: '/organizations/team-users/{id}/'
}

export type OrganizationsTeamUsersDestroyResponses = {
  /**
   * No response body
   */
  204: void
}

export type OrganizationsTeamUsersDestroyResponse =
  OrganizationsTeamUsersDestroyResponses[keyof OrganizationsTeamUsersDestroyResponses]

export type OrganizationsTeamUsersRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this team users.
     */
    id: number
  }
  query?: never
  url: '/organizations/team-users/{id}/'
}

export type OrganizationsTeamUsersRetrieveResponses = {
  200: TeamUsersDetail
}

export type OrganizationsTeamUsersRetrieveResponse =
  OrganizationsTeamUsersRetrieveResponses[keyof OrganizationsTeamUsersRetrieveResponses]

export type OrganizationsTeamsListData = {
  body?: never
  path?: never
  query?: {
    has_parent?: boolean
    /**
     * comma separated list
     */
    ids?: string
    my_teams?: boolean
    /**
     * Which field to use when ordering the results.
     */
    ordering?: string
    organization?: number
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * Comma-separated list of IDs to return first (in the given order), before the default ordering.
     */
    preference?: string
    search?: string
    /**
     * * `branch` - Branch
     * * `department` - Department
     * * `section` - Section
     */
    type?: Array<'branch' | 'department' | 'section'>
  }
  url: '/organizations/teams/'
}

export type OrganizationsTeamsListResponses = {
  200: PaginatedTeamListList
}

export type OrganizationsTeamsListResponse =
  OrganizationsTeamsListResponses[keyof OrganizationsTeamsListResponses]

export type OrganizationsTeamsCreateData = {
  body: TeamModifyWritable
  path?: never
  query?: never
  url: '/organizations/teams/'
}

export type OrganizationsTeamsCreateResponses = {
  201: TeamModify
}

export type OrganizationsTeamsCreateResponse =
  OrganizationsTeamsCreateResponses[keyof OrganizationsTeamsCreateResponses]

export type OrganizationsTeamsDestroyData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this team.
     */
    id: number
  }
  query?: never
  url: '/organizations/teams/{id}/'
}

export type OrganizationsTeamsDestroyResponses = {
  /**
   * No response body
   */
  204: void
}

export type OrganizationsTeamsDestroyResponse =
  OrganizationsTeamsDestroyResponses[keyof OrganizationsTeamsDestroyResponses]

export type OrganizationsTeamsRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this team.
     */
    id: number
  }
  query?: never
  url: '/organizations/teams/{id}/'
}

export type OrganizationsTeamsRetrieveResponses = {
  200: TeamList
}

export type OrganizationsTeamsRetrieveResponse =
  OrganizationsTeamsRetrieveResponses[keyof OrganizationsTeamsRetrieveResponses]

export type OrganizationsTeamsPartialUpdateData = {
  body?: PatchedTeamModifyWritable
  path: {
    /**
     * A unique integer value identifying this team.
     */
    id: number
  }
  query?: never
  url: '/organizations/teams/{id}/'
}

export type OrganizationsTeamsPartialUpdateResponses = {
  200: TeamModify
}

export type OrganizationsTeamsPartialUpdateResponse =
  OrganizationsTeamsPartialUpdateResponses[keyof OrganizationsTeamsPartialUpdateResponses]

export type OrganizationsTeamsOrganigramListData = {
  body?: never
  path?: never
  query: {
    organization: number
    parent?: number | null
  }
  url: '/organizations/teams/organigram/'
}

export type OrganizationsTeamsOrganigramListResponses = {
  200: Array<OrganizationTeamsOrganigram>
}

export type OrganizationsTeamsOrganigramListResponse =
  OrganizationsTeamsOrganigramListResponses[keyof OrganizationsTeamsOrganigramListResponses]

export type OrganizationsUserGroupsListData = {
  body?: never
  path?: never
  query?: {
    application?: number
    /**
     * comma separated list
     */
    ids?: string
    /**
     * Which field to use when ordering the results.
     */
    ordering?: string
    organization?: number
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * Comma-separated list of IDs to return first (in the given order), before the default ordering.
     */
    preference?: string
    /**
     * Groups that grant at least one role of this application.
     */
    role_application?: number
    search?: string
  }
  url: '/organizations/user-groups/'
}

export type OrganizationsUserGroupsListResponses = {
  200: PaginatedUserGroupList
}

export type OrganizationsUserGroupsListResponse =
  OrganizationsUserGroupsListResponses[keyof OrganizationsUserGroupsListResponses]

export type OrganizationsUserGroupsCreateData = {
  body: UserGroupModifyWritable
  path?: never
  query?: never
  url: '/organizations/user-groups/'
}

export type OrganizationsUserGroupsCreateResponses = {
  201: UserGroupModify
}

export type OrganizationsUserGroupsCreateResponse =
  OrganizationsUserGroupsCreateResponses[keyof OrganizationsUserGroupsCreateResponses]

export type OrganizationsUserGroupsDestroyData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this user group.
     */
    id: number
  }
  query?: never
  url: '/organizations/user-groups/{id}/'
}

export type OrganizationsUserGroupsDestroyResponses = {
  /**
   * No response body
   */
  204: void
}

export type OrganizationsUserGroupsDestroyResponse =
  OrganizationsUserGroupsDestroyResponses[keyof OrganizationsUserGroupsDestroyResponses]

export type OrganizationsUserGroupsRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this user group.
     */
    id: number
  }
  query?: never
  url: '/organizations/user-groups/{id}/'
}

export type OrganizationsUserGroupsRetrieveResponses = {
  200: UserGroup
}

export type OrganizationsUserGroupsRetrieveResponse =
  OrganizationsUserGroupsRetrieveResponses[keyof OrganizationsUserGroupsRetrieveResponses]

export type OrganizationsUserGroupsPartialUpdateData = {
  body?: PatchedUserGroupModifyWritable
  path: {
    /**
     * A unique integer value identifying this user group.
     */
    id: number
  }
  query?: never
  url: '/organizations/user-groups/{id}/'
}

export type OrganizationsUserGroupsPartialUpdateResponses = {
  200: UserGroupModify
}

export type OrganizationsUserGroupsPartialUpdateResponse =
  OrganizationsUserGroupsPartialUpdateResponses[keyof OrganizationsUserGroupsPartialUpdateResponses]

export type OrganizationsUserGroupsApplicationsRolesPartialUpdateData = {
  body?: PatchedUserGroupApplicationRoles
  path: {
    application_id: string
    /**
     * A unique integer value identifying this user group.
     */
    id: number
  }
  query?: never
  url: '/organizations/user-groups/{id}/applications/{application_id}/roles/'
}

export type OrganizationsUserGroupsApplicationsRolesPartialUpdateResponses = {
  200: UserGroup
}

export type OrganizationsUserGroupsApplicationsRolesPartialUpdateResponse =
  OrganizationsUserGroupsApplicationsRolesPartialUpdateResponses[keyof OrganizationsUserGroupsApplicationsRolesPartialUpdateResponses]

export type OrganizationsValidateRetrieveData = {
  body?: never
  path: {
    id: number
  }
  query?: never
  url: '/organizations/validate/{id}/'
}

export type OrganizationsValidateRetrieveResponses = {
  200: OrganizationTiny
}

export type OrganizationsValidateRetrieveResponse =
  OrganizationsValidateRetrieveResponses[keyof OrganizationsValidateRetrieveResponses]

export type OrganizationsWorkspacePermissionsListData = {
  body?: never
  path?: never
  query: {
    organization: number
  }
  url: '/organizations/workspace-permissions/'
}

export type OrganizationsWorkspacePermissionsListResponses = {
  200: Array<WorkspacePermissionCatalog>
}

export type OrganizationsWorkspacePermissionsListResponse =
  OrganizationsWorkspacePermissionsListResponses[keyof OrganizationsWorkspacePermissionsListResponses]

export type OrganizationsWorkspaceRolesListData = {
  body?: never
  path?: never
  query: {
    /**
     * Which field to use when ordering the results.
     */
    ordering?: string
    organization: number
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * A search term.
     */
    search?: string
  }
  url: '/organizations/workspace-roles/'
}

export type OrganizationsWorkspaceRolesListResponses = {
  200: PaginatedWorkspaceRoleList
}

export type OrganizationsWorkspaceRolesListResponse =
  OrganizationsWorkspaceRolesListResponses[keyof OrganizationsWorkspaceRolesListResponses]

export type OrganizationsWorkspaceRolesCreateData = {
  body: WorkspaceRoleWritable
  path?: never
  query?: never
  url: '/organizations/workspace-roles/'
}

export type OrganizationsWorkspaceRolesCreateResponses = {
  201: WorkspaceRole
}

export type OrganizationsWorkspaceRolesCreateResponse =
  OrganizationsWorkspaceRolesCreateResponses[keyof OrganizationsWorkspaceRolesCreateResponses]

export type OrganizationsWorkspaceRolesDestroyData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this Organization Role.
     */
    id: number
  }
  query?: never
  url: '/organizations/workspace-roles/{id}/'
}

export type OrganizationsWorkspaceRolesDestroyResponses = {
  /**
   * No response body
   */
  204: void
}

export type OrganizationsWorkspaceRolesDestroyResponse =
  OrganizationsWorkspaceRolesDestroyResponses[keyof OrganizationsWorkspaceRolesDestroyResponses]

export type OrganizationsWorkspaceRolesRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this Organization Role.
     */
    id: number
  }
  query?: never
  url: '/organizations/workspace-roles/{id}/'
}

export type OrganizationsWorkspaceRolesRetrieveResponses = {
  200: WorkspaceRole
}

export type OrganizationsWorkspaceRolesRetrieveResponse =
  OrganizationsWorkspaceRolesRetrieveResponses[keyof OrganizationsWorkspaceRolesRetrieveResponses]

export type OrganizationsWorkspaceRolesPartialUpdateData = {
  body?: PatchedWorkspaceRoleWritable
  path: {
    /**
     * A unique integer value identifying this Organization Role.
     */
    id: number
  }
  query?: never
  url: '/organizations/workspace-roles/{id}/'
}

export type OrganizationsWorkspaceRolesPartialUpdateResponses = {
  200: WorkspaceRole
}

export type OrganizationsWorkspaceRolesPartialUpdateResponse =
  OrganizationsWorkspaceRolesPartialUpdateResponses[keyof OrganizationsWorkspaceRolesPartialUpdateResponses]

export type SchemaRetrieveData = {
  body?: never
  path?: never
  query?: {
    format?: 'json' | 'yaml'
    lang?: 'en' | 'ro' | 'ru'
  }
  url: '/schema'
}

export type SchemaRetrieveResponses = {
  200: {
    [key: string]: unknown
  }
}

export type SchemaRetrieveResponse =
  SchemaRetrieveResponses[keyof SchemaRetrieveResponses]

export type McpConnectionsListData = {
  body?: never
  path: {
    organization_id: number
  }
  query?: {
    service?: 'core' | 'crm' | 'notification' | 'squad'
    /**
     * Core user id.
     */
    user?: number
  }
  url: '/settings/mcp/{organization_id}/connections/'
}

export type McpConnectionsListResponses = {
  200: Array<McpConnection>
}

export type McpConnectionsListResponse =
  McpConnectionsListResponses[keyof McpConnectionsListResponses]

export type McpConnectionsRevokeData = {
  body?: McpConnectionRevoke
  path: {
    id: string
    organization_id: number
  }
  query?: never
  url: '/settings/mcp/{organization_id}/connections/{id}/revoke/'
}

export type McpConnectionsRevokeResponses = {
  200: McpConnection
}

export type McpConnectionsRevokeResponse =
  McpConnectionsRevokeResponses[keyof McpConnectionsRevokeResponses]

export type McpConnectionsScopesUpdateData = {
  body: McpConnectionScopesUpdate
  path: {
    id: string
    organization_id: number
  }
  query?: never
  url: '/settings/mcp/{organization_id}/connections/{id}/scopes/'
}

export type McpConnectionsScopesUpdateResponses = {
  200: McpConnection
}

export type McpConnectionsScopesUpdateResponse =
  McpConnectionsScopesUpdateResponses[keyof McpConnectionsScopesUpdateResponses]

export type McpPoliciesListData = {
  body?: never
  path: {
    organization_id: number
  }
  query?: never
  url: '/settings/mcp/{organization_id}/policies/'
}

export type McpPoliciesListResponses = {
  200: Array<McpServicePolicy>
}

export type McpPoliciesListResponse =
  McpPoliciesListResponses[keyof McpPoliciesListResponses]

export type McpPoliciesPartialUpdateData = {
  body?: PatchedMcpServicePolicy
  path: {
    organization_id: number
    service: string
  }
  query?: never
  url: '/settings/mcp/{organization_id}/policies/{service}/'
}

export type McpPoliciesPartialUpdateErrors = {
  /**
   * Unknown MCP service.
   */
  404: unknown
}

export type McpPoliciesPartialUpdateResponses = {
  200: McpServicePolicy
}

export type McpPoliciesPartialUpdateResponse =
  McpPoliciesPartialUpdateResponses[keyof McpPoliciesPartialUpdateResponses]

export type SettingsOutboundWebhooksWebhooksListData = {
  body?: never
  path: {
    application_id: number
  }
  query?: {
    managed_by?: string
    managed_ref?: string
  }
  url: '/settings/outbound-webhooks/{application_id}/webhooks/'
}

export type SettingsOutboundWebhooksWebhooksListResponses = {
  200: Array<OutboundWebhookRead>
}

export type SettingsOutboundWebhooksWebhooksListResponse =
  SettingsOutboundWebhooksWebhooksListResponses[keyof SettingsOutboundWebhooksWebhooksListResponses]

export type SettingsOutboundWebhooksWebhooksCreateData = {
  body: OutboundWebhookWriteWritable
  path: {
    application_id: number
  }
  query?: never
  url: '/settings/outbound-webhooks/{application_id}/webhooks/'
}

export type SettingsOutboundWebhooksWebhooksCreateResponses = {
  201: OutboundWebhookRead
}

export type SettingsOutboundWebhooksWebhooksCreateResponse =
  SettingsOutboundWebhooksWebhooksCreateResponses[keyof SettingsOutboundWebhooksWebhooksCreateResponses]

export type SettingsOutboundWebhooksWebhooksDestroyData = {
  body?: never
  path: {
    application_id: number
    /**
     * A unique integer value identifying this outbound webhook.
     */
    id: number
  }
  query?: never
  url: '/settings/outbound-webhooks/{application_id}/webhooks/{id}/'
}

export type SettingsOutboundWebhooksWebhooksDestroyResponses = {
  /**
   * No response body
   */
  204: void
}

export type SettingsOutboundWebhooksWebhooksDestroyResponse =
  SettingsOutboundWebhooksWebhooksDestroyResponses[keyof SettingsOutboundWebhooksWebhooksDestroyResponses]

export type SettingsOutboundWebhooksWebhooksRetrieveData = {
  body?: never
  path: {
    application_id: number
    /**
     * A unique integer value identifying this outbound webhook.
     */
    id: number
  }
  query?: never
  url: '/settings/outbound-webhooks/{application_id}/webhooks/{id}/'
}

export type SettingsOutboundWebhooksWebhooksRetrieveResponses = {
  200: OutboundWebhookRead
}

export type SettingsOutboundWebhooksWebhooksRetrieveResponse =
  SettingsOutboundWebhooksWebhooksRetrieveResponses[keyof SettingsOutboundWebhooksWebhooksRetrieveResponses]

export type SettingsOutboundWebhooksWebhooksPartialUpdateData = {
  body?: PatchedOutboundWebhookWriteWritable
  path: {
    application_id: number
    /**
     * A unique integer value identifying this outbound webhook.
     */
    id: number
  }
  query?: never
  url: '/settings/outbound-webhooks/{application_id}/webhooks/{id}/'
}

export type SettingsOutboundWebhooksWebhooksPartialUpdateResponses = {
  200: OutboundWebhookRead
}

export type SettingsOutboundWebhooksWebhooksPartialUpdateResponse =
  SettingsOutboundWebhooksWebhooksPartialUpdateResponses[keyof SettingsOutboundWebhooksWebhooksPartialUpdateResponses]

export type SettingsOutboundWebhooksWebhooksRevealSecretCreateData = {
  body?: OutboundWebhookSecret
  path: {
    application_id: number
    /**
     * A unique integer value identifying this outbound webhook.
     */
    id: number
  }
  query?: never
  url: '/settings/outbound-webhooks/{application_id}/webhooks/{id}/reveal-secret/'
}

export type SettingsOutboundWebhooksWebhooksRevealSecretCreateResponses = {
  200: OutboundWebhookSecret
}

export type SettingsOutboundWebhooksWebhooksRevealSecretCreateResponse =
  SettingsOutboundWebhooksWebhooksRevealSecretCreateResponses[keyof SettingsOutboundWebhooksWebhooksRevealSecretCreateResponses]

export type SettingsOutboundWebhooksWebhooksTestCreateData = {
  body?: never
  path: {
    application_id: number
    /**
     * A unique integer value identifying this outbound webhook.
     */
    id: number
  }
  query?: never
  url: '/settings/outbound-webhooks/{application_id}/webhooks/{id}/test/'
}

export type SettingsOutboundWebhooksWebhooksTestCreateResponses = {
  200: OutboundWebhookTestResult
}

export type SettingsOutboundWebhooksWebhooksTestCreateResponse =
  SettingsOutboundWebhooksWebhooksTestCreateResponses[keyof SettingsOutboundWebhooksWebhooksTestCreateResponses]

export type SettingsPublicAccessCredentialsListData = {
  body?: never
  path: {
    organization_id: number
  }
  query?: never
  url: '/settings/public-access/{organization_id}/credentials/'
}

export type SettingsPublicAccessCredentialsListResponses = {
  200: Array<PublicAccessCredentialRead>
}

export type SettingsPublicAccessCredentialsListResponse =
  SettingsPublicAccessCredentialsListResponses[keyof SettingsPublicAccessCredentialsListResponses]

export type SettingsPublicAccessCredentialsCreateData = {
  body: PublicAccessCredentialCreate
  path: {
    organization_id: number
  }
  query?: never
  url: '/settings/public-access/{organization_id}/credentials/'
}

export type SettingsPublicAccessCredentialsCreateResponses = {
  201: PublicAccessCredentialCreateResponse
}

export type SettingsPublicAccessCredentialsCreateResponse =
  SettingsPublicAccessCredentialsCreateResponses[keyof SettingsPublicAccessCredentialsCreateResponses]

export type SettingsPublicAccessCredentialsRetrieveData = {
  body?: never
  path: {
    /**
     * A unique value identifying this public access credential.
     */
    id: string
    organization_id: number
  }
  query?: never
  url: '/settings/public-access/{organization_id}/credentials/{id}/'
}

export type SettingsPublicAccessCredentialsRetrieveResponses = {
  200: PublicAccessCredentialRead
}

export type SettingsPublicAccessCredentialsRetrieveResponse =
  SettingsPublicAccessCredentialsRetrieveResponses[keyof SettingsPublicAccessCredentialsRetrieveResponses]

export type SettingsPublicAccessCredentialsPartialUpdateData = {
  body?: PatchedPublicAccessCredentialUpdate
  path: {
    /**
     * A unique value identifying this public access credential.
     */
    id: string
    organization_id: number
  }
  query?: never
  url: '/settings/public-access/{organization_id}/credentials/{id}/'
}

export type SettingsPublicAccessCredentialsPartialUpdateResponses = {
  200: PublicAccessCredentialRead
}

export type SettingsPublicAccessCredentialsPartialUpdateResponse =
  SettingsPublicAccessCredentialsPartialUpdateResponses[keyof SettingsPublicAccessCredentialsPartialUpdateResponses]

export type SettingsPublicAccessCredentialsRevokeCreateData = {
  body?: PublicAccessCredentialRevoke
  path: {
    /**
     * A unique value identifying this public access credential.
     */
    id: string
    organization_id: number
  }
  query?: never
  url: '/settings/public-access/{organization_id}/credentials/{id}/revoke/'
}

export type SettingsPublicAccessCredentialsRevokeCreateResponses = {
  200: PublicAccessCredentialRevokeResponse
}

export type SettingsPublicAccessCredentialsRevokeCreateResponse =
  SettingsPublicAccessCredentialsRevokeCreateResponses[keyof SettingsPublicAccessCredentialsRevokeCreateResponses]

export type SettingsPublicAccessCredentialsRotateCreateData = {
  body?: PublicAccessCredentialRotate
  path: {
    /**
     * A unique value identifying this public access credential.
     */
    id: string
    organization_id: number
  }
  query?: never
  url: '/settings/public-access/{organization_id}/credentials/{id}/rotate/'
}

export type SettingsPublicAccessCredentialsRotateCreateResponses = {
  201: PublicAccessCredentialRotateResponse
}

export type SettingsPublicAccessCredentialsRotateCreateResponse =
  SettingsPublicAccessCredentialsRotateCreateResponses[keyof SettingsPublicAccessCredentialsRotateCreateResponses]

export type StaticRetrieveData = {
  body?: never
  path: {
    /**
     * Path to the static file
     */
    path: string
  }
  query?: never
  url: '/static/{path}'
}

export type StaticRetrieveResponses = {
  /**
   * Static file content
   */
  200: Blob | File
}

export type StaticRetrieveResponse =
  StaticRetrieveResponses[keyof StaticRetrieveResponses]

export type SupportReportCreateData = {
  body: ReportCreate
  path?: never
  query?: never
  url: '/support/report/'
}

export type SupportReportCreateResponses = {
  201: ReportFull
}

export type SupportReportCreateResponse =
  SupportReportCreateResponses[keyof SupportReportCreateResponses]

export type SupportTicketsListData = {
  body?: never
  path?: never
  query?: {
    /**
     * * `-created_at` - -created_at
     * * `created_at` - created_at
     * * `-updated_at` - -updated_at
     * * `updated_at` - updated_at
     * * `priority` - priority
     * * `-priority` - -priority
     */
    ordering?:
      | '-created_at'
      | 'created_at'
      | '-updated_at'
      | 'updated_at'
      | 'priority'
      | '-priority'
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * 1 urgent, 2 high, 3 normal, 4 low.
     */
    priority?: number | null
    /**
     * Matches the title and the description.
     */
    search?: string
    /**
     * Which tickets to return. Defaults to the caller's open ones.
     *
     * * `open` - open
     * * `completed` - completed
     * * `all` - all
     */
    status?: 'open' | 'completed' | 'all'
  }
  url: '/support/tickets/'
}

export type SupportTicketsListResponses = {
  200: PaginatedSupportTicketList
}

export type SupportTicketsListResponse =
  SupportTicketsListResponses[keyof SupportTicketsListResponses]

export type SupportTicketsCreateData = {
  body: SupportTicketCreate
  path?: never
  query?: never
  url: '/support/tickets/'
}

export type SupportTicketsCreateResponses = {
  201: SupportTicketCreated
}

export type SupportTicketsCreateResponse =
  SupportTicketsCreateResponses[keyof SupportTicketsCreateResponses]

export type SupportTicketsRetrieveData = {
  body?: never
  path: {
    ticket_id: number
  }
  query?: never
  url: '/support/tickets/{ticket_id}/'
}

export type SupportTicketsRetrieveResponses = {
  200: SupportTicket
}

export type SupportTicketsRetrieveResponse =
  SupportTicketsRetrieveResponses[keyof SupportTicketsRetrieveResponses]

export type SupportTicketsCommentsListData = {
  body?: never
  path: {
    ticket_id: number
  }
  query?: never
  url: '/support/tickets/{ticket_id}/comments/'
}

export type SupportTicketsCommentsListResponses = {
  200: Array<SupportTicketComment>
}

export type SupportTicketsCommentsListResponse =
  SupportTicketsCommentsListResponses[keyof SupportTicketsCommentsListResponses]

export type SupportTicketsCommentsCreateData = {
  body: SupportTicketCommentCreate
  path: {
    ticket_id: number
  }
  query?: never
  url: '/support/tickets/{ticket_id}/comments/'
}

export type SupportTicketsCommentsCreateResponses = {
  201: Array<SupportTicketComment>
}

export type SupportTicketsCommentsCreateResponse =
  SupportTicketsCommentsCreateResponses[keyof SupportTicketsCommentsCreateResponses]

export type SupportTicketsUnseenCountRetrieveData = {
  body?: never
  path?: never
  query?: never
  url: '/support/tickets/unseen-count/'
}

export type SupportTicketsUnseenCountRetrieveResponses = {
  200: SupportTicketUnseenCount
}

export type SupportTicketsUnseenCountRetrieveResponse =
  SupportTicketsUnseenCountRetrieveResponses[keyof SupportTicketsUnseenCountRetrieveResponses]

export type SupportTicketsUploadsCreateData = {
  body: SupportTicketUploadCreate
  path?: never
  query?: never
  url: '/support/tickets/uploads/'
}

export type SupportTicketsUploadsCreateResponses = {
  201: SupportTicketUpload
}

export type SupportTicketsUploadsCreateResponse =
  SupportTicketsUploadsCreateResponses[keyof SupportTicketsUploadsCreateResponses]

export type UsersListData = {
  body?: never
  path?: never
  query?: {
    email?: string
    /**
     * Which field to use when ordering the results.
     */
    ordering?: string
    organization?: number
    /**
     * A page number within the paginated result set.
     */
    page?: number
    /**
     * Number of results to return per page.
     */
    per_page?: number
    /**
     * Comma-separated list of IDs to return first (in the given order), before the default ordering.
     */
    preference?: string
    search?: string
    /**
     * Comma-separated list of SSO IDs, exact match.
     */
    sso_id?: Array<string>
    team?: number
  }
  url: '/users/'
}

export type UsersListResponses = {
  200: PaginatedUserListList
}

export type UsersListResponse = UsersListResponses[keyof UsersListResponses]

export type UsersRetrieveData = {
  body?: never
  path: {
    /**
     * A unique integer value identifying this User.
     */
    id: number
  }
  query?: never
  url: '/users/{id}/'
}

export type UsersRetrieveResponses = {
  200: UserRetrieve
}

export type UsersRetrieveResponse =
  UsersRetrieveResponses[keyof UsersRetrieveResponses]

export type UsersPartialUpdateData = {
  body?: PatchedUserWritable
  path: {
    /**
     * A unique integer value identifying this User.
     */
    id: number
  }
  query?: never
  url: '/users/{id}/'
}

export type UsersPartialUpdateResponses = {
  200: User
}

export type UsersPartialUpdateResponse =
  UsersPartialUpdateResponses[keyof UsersPartialUpdateResponses]

export type UsersAddInOrganizationCreateData = {
  body: AddUserInOrganization
  path?: never
  query?: never
  url: '/users/add-in-organization/'
}

export type UsersAddInOrganizationCreateResponses = {
  200: OrganizationUsers
}

export type UsersAddInOrganizationCreateResponse =
  UsersAddInOrganizationCreateResponses[keyof UsersAddInOrganizationCreateResponses]

export type UsersArchiveCreateData = {
  body: AdminArchiveUserRequest
  path: {
    user_id: number
  }
  query?: never
  url: '/users/archive/{user_id}/'
}

export type UsersArchiveCreateErrors = {
  /**
   * Missing or invalid organization_id/action.
   */
  400: unknown
  /**
   * Requester is missing users.manage.
   */
  403: unknown
  /**
   * User or organization membership was not found.
   */
  404: unknown
  409: ArchiveConflictResponse
  503: ArchiveServiceUnavailableResponse
}

export type UsersArchiveCreateError =
  UsersArchiveCreateErrors[keyof UsersArchiveCreateErrors]

export type UsersArchiveCreateResponses = {
  200: AdminArchiveUserResponse
}

export type UsersArchiveCreateResponse =
  UsersArchiveCreateResponses[keyof UsersArchiveCreateResponses]

export type UsersArchiveDependenciesRetrieveData = {
  body?: never
  path: {
    user_id: number
  }
  query: {
    organization_id: number
  }
  url: '/users/archive/{user_id}/dependencies/'
}

export type UsersArchiveDependenciesRetrieveErrors = {
  /**
   * Missing or invalid organization_id.
   */
  400: unknown
  /**
   * Requester is missing users.manage.
   */
  403: unknown
  /**
   * User or organization membership was not found.
   */
  404: unknown
  503: ArchiveServiceUnavailableResponse
}

export type UsersArchiveDependenciesRetrieveError =
  UsersArchiveDependenciesRetrieveErrors[keyof UsersArchiveDependenciesRetrieveErrors]

export type UsersArchiveDependenciesRetrieveResponses = {
  200: ArchiveDependencyResponse
}

export type UsersArchiveDependenciesRetrieveResponse =
  UsersArchiveDependenciesRetrieveResponses[keyof UsersArchiveDependenciesRetrieveResponses]

export type UsersArchiveReplaceAndArchiveCreateData = {
  body: ArchiveReplacementRequest
  path: {
    user_id: number
  }
  query?: never
  url: '/users/archive/{user_id}/replace-and-archive/'
}

export type UsersArchiveReplaceAndArchiveCreateErrors = {
  /**
   * Invalid target user or service/entity payload.
   */
  400: unknown
  /**
   * Requester is missing users.manage.
   */
  403: unknown
  /**
   * Source or target user was not found.
   */
  404: unknown
  409: ArchiveConflictResponse
  503: ArchiveServiceUnavailableResponse
}

export type UsersArchiveReplaceAndArchiveCreateError =
  UsersArchiveReplaceAndArchiveCreateErrors[keyof UsersArchiveReplaceAndArchiveCreateErrors]

export type UsersArchiveReplaceAndArchiveCreateResponses = {
  202: UserArchiveJob
}

export type UsersArchiveReplaceAndArchiveCreateResponse =
  UsersArchiveReplaceAndArchiveCreateResponses[keyof UsersArchiveReplaceAndArchiveCreateResponses]

export type UsersArchiveJobsRetrieveData = {
  body?: never
  path: {
    job_id: number
  }
  query?: never
  url: '/users/archive/jobs/{job_id}/'
}

export type UsersArchiveJobsRetrieveErrors = {
  /**
   * Requester is missing users.manage.
   */
  403: unknown
  /**
   * Archive job was not found.
   */
  404: unknown
}

export type UsersArchiveJobsRetrieveResponses = {
  200: UserArchiveJobDetailResponse
}

export type UsersArchiveJobsRetrieveResponse =
  UsersArchiveJobsRetrieveResponses[keyof UsersArchiveJobsRetrieveResponses]

export type UsersEditPartialUpdateData = {
  body?: PatchedUserEdit
  path?: never
  query?: never
  url: '/users/edit/'
}

export type UsersEditPartialUpdateResponses = {
  200: UserShortWithSettings
}

export type UsersEditPartialUpdateResponse =
  UsersEditPartialUpdateResponses[keyof UsersEditPartialUpdateResponses]

export type UsersEditUpdateData = {
  body: UserEdit
  path?: never
  query?: never
  url: '/users/edit/'
}

export type UsersEditUpdateResponses = {
  200: UserShortWithSettings
}

export type UsersEditUpdateResponse =
  UsersEditUpdateResponses[keyof UsersEditUpdateResponses]

export type UsersLoginAdminCreateData = {
  body?: never
  path?: never
  query?: never
  url: '/users/login/admin/'
}

export type UsersLoginAdminCreateResponses = {
  200: CurrentAccountUser
}

export type UsersLoginAdminCreateResponse =
  UsersLoginAdminCreateResponses[keyof UsersLoginAdminCreateResponses]

export type UsersLoginUserCreateData = {
  body: UserDomainLogin
  path?: never
  query?: never
  url: '/users/login/user/'
}

export type UsersLoginUserCreateResponses = {
  200: CurrentOrganizationUser
}

export type UsersLoginUserCreateResponse =
  UsersLoginUserCreateResponses[keyof UsersLoginUserCreateResponses]

export type UsersNotificationServiceSyncCreateData = {
  body: NotificationServiceSync
  path?: never
  query?: never
  url: '/users/notification-service-sync/'
}

export type UsersNotificationServiceSyncCreateResponses = {
  200: NotificationServiceUser
}

export type UsersNotificationServiceSyncCreateResponse =
  UsersNotificationServiceSyncCreateResponses[keyof UsersNotificationServiceSyncCreateResponses]

export type UsersSyncActiveDirectoryCreateData = {
  body?: never
  path: {
    organization_id: number
  }
  query?: never
  url: '/users/sync-active-directory/{organization_id}/'
}

export type UsersSyncActiveDirectoryCreateResponses = {
  200: SyncActiveDirectoryResponse
}

export type UsersSyncActiveDirectoryCreateResponse =
  UsersSyncActiveDirectoryCreateResponses[keyof UsersSyncActiveDirectoryCreateResponses]
